/*
 * Bundle Builder — Phase 1 (tiers + slots) + Phase 2 (Print Selector)
 *
 * One <bundle-builder> custom element owns the state:
 *   state = { tier, frame, paidSlots:[{assigned,product,size}], freeSlots:[{assigned,product}] }
 *
 * - Pricing is DISPLAY ONLY (real discount applied by the discount app):
 *   total = Σ(paid slot price at chosen size + shared frame) × (1 − tier%); free = £0.
 * - The Print Selector repurposes the desktop media column (`.product-information__media`)
 *   into a mini-PLP backed by per-medium collections. "+ Add" fills the first empty slot
 *   (paid first, then free); filling past the tier auto-bumps to the next tier (R1.5.5).
 * - Material is intentionally NOT handled (paper-only for now, per scope).
 */

// Build marker. A STRING literal, so it survives minification — grepping for a variable name
// does not (the minifier renames locals). Bump this whenever you push, then read it in the
// console to confirm which build the browser actually has.
const BB_BUILD = '2026-08-27-b';

// The pack ladder, smallest first. Keys 2-5 keep their historical names (duo=2, quad=3,
// gallery=4, mega=5); 6-10 are plain `packN`. Every entry but the last is a FIXED pack; the
// LAST one (`pack10`, the "10+ PACK") is the open-ended top tier, resolved via _isTopTier()
// rather than a hardcoded key — so adding a pack only means extending this list plus the
// matching Theme settings / tier JSON in blocks/bundle-builder.liquid.
const TIER_ORDER = ['single', 'duo', 'quad', 'gallery', 'mega', 'pack6', 'pack7', 'pack8', 'pack9', 'pack10'];
// …but a merchant can switch individual packs OFF (Theme settings → Bundle Builder Offer →
// "Enable N pack"), and a disabled pack is simply absent from the injected tier JSON. So every
// ladder walk at runtime goes through this.tierOrder — TIER_ORDER filtered down to the packs
// actually on offer — never TIER_ORDER itself, or a bump could land on a pack with no button.
// Floor for the paid-slot array: the biggest pack's paid count, so switching straight to the
// top pack can never leave the array too short for its slots.
const MAX_PAID = 10;
const MAX_FREE = 2;

// Filter pill -> collection handle (server-side filtered, no Boost dependency).
const FILTERS = [
  { label: 'Bestsellers', handle: 'best-sellers' },
  { label: 'Graphic Design & Illustration', handle: 'graphic-design' },
  { label: 'Painting & Drawing', handle: 'painting' },
  { label: 'Photography', handle: 'photography' },
  { label: 'Mixed Media', handle: 'mixed-media' },
];

// ---- Print Selector facets (bundle mode) — powered by the same Boost filter API the
// PLP/storefront uses (the ?pf_t_* collection filters). collection_scope + shop come from
// the injected `.bundle-builder__boost` JSON. Facet values are fetched live from Boost's
// filter tree at init (build_filter_tree); FACET_DEFS is the fallback if that call fails. ----
const BOOST_FILTER_URL = 'https://services.mybcapps.com/bc-sf-filter/filter';
const BOOST_PAGE_SIZE = 48; // initial + "Load more" page size (reduced from 64 to speed up the Boost fetch — feedback)
const SEL_CACHE_KEY = 'drool_bb_sel_v1'; // sessionStorage cache of the selector's first Boost page (perf)
const SEL_CACHE_TTL = 10 * 60 * 1000; // 10 min — short so a sold-out/changed print can't linger
const FACET_DEFS = [
  // Discount % first — this list IS the pill order (_buildFacetTree maps over it in place and
  // _buildFilterRow renders it in order). Values and the tag prefix are whatever Boost's live
  // filter tree reports, because which markdowns exist changes with the sale; there is no sensible
  // static fallback, so the pill simply doesn't render until Boost answers (see the empty-values
  // guard in _buildFilterRow) — and Medium takes the first slot on a scope with no sale.
  { key: 'pf_t_discount', label: 'Discount %', prefix: '', values: [] },
  { key: 'pf_t_medium', label: 'Medium', prefix: 'Medium2026-',
    values: ['Graphic Design & Illustration', 'Photography', 'Painting & Drawing', 'Mixed Media'] },
  { key: 'pf_t_style', label: 'Style', prefix: 'Style2026-',
    values: ['Bold', 'Minimal', 'Typography', 'Abstract', 'Maximal', 'Figurative', 'Vintage & Retro', 'Animal', 'Folk Inspired', 'Bauhaus', 'Painterly', 'Abstract & Expressive', 'Artistic Photography', 'Colour-field'] },
  { key: 'pf_t_colour', label: 'Colour', prefix: 'Colour2026-', swatch: true,
    values: ['Black', 'Blue', 'White', 'Red', 'Green', 'Pink', 'Orange', 'Yellow', 'Beige', 'Grey', 'Brown', 'Purple'] },
  { key: 'pf_t_orientation', label: 'Orientation', prefix: 'Orientation-',
    values: ['Portrait', 'Landscape', 'Square'] },
  { key: 'pf_t_theme', label: 'Theme', prefix: 'Theme2026-',
    values: ['Quotes', 'Nature', 'Animals', 'Botanicals', 'Japanese', 'Landscapes', 'Feminine', 'Cities & Architecture', 'Food & Living', 'Holidays & Travel', 'Cars', 'Waterscapes', 'Music', 'Nude', 'Americana', 'Sports', 'Movies & TV', 'Anime'] },
  // Size is a multi_level_tag facet (pf_mlt_size), values carried on Boost's `manualValues`
  // (not `values`). Full tag = prefix + value, e.g. "Size-M (longest side 30-60cm)".
  { key: 'pf_mlt_size', label: 'Size', prefix: 'Size-',
    values: ['S (longest side up to 30cm)', 'M (longest side 30-60cm)', 'L (longest side from 60cm)', 'XL (longest side from 90cm)', 'XXL (longest side fram 120cm)'] },
];
// CSS swatch colours for the Colour facet (display only).
const COLOUR_SWATCHES = {
  Black: '#1a1a1a', Blue: '#2f5fd0', White: '#ffffff', Red: '#cf2f2f', Green: '#3a8f4f',
  Pink: '#e58bb0', Orange: '#e0822f', Yellow: '#e8c34a', Beige: '#d8c7a8', Grey: '#9a9a9a',
  Brown: '#7a4f33', Purple: '#7a4fb0',
};

// Selector grid layout preference (2 or 4 per row) — persisted per browser.
const LAYOUT_KEY = 'drool_bundle_cols';
function getLayoutPref() {
  const v = parseInt(localStorage.getItem(LAYOUT_KEY) || '', 10);
  return v === 2 || v === 4 ? v : 4; // Figma default = 4-up
}
function setLayoutPref(n) { try { localStorage.setItem(LAYOUT_KEY, String(n)); } catch (e) {} }

// Inline SVG icons (currentColor) for the selector controls.
const ICON_EYE = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z"/><circle cx="12" cy="12" r="3"/></svg>';
const ICON_CHEVRON = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';
const ICON_CHEVRON_LEFT = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>';
const ICON_CLOSE = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const ICON_INFO = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 7.5v.5"/></svg>';
const ICON_CLOCK = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.05 11a9 9 0 1 1 .5 4"/><path d="M3 4v4h4"/><path d="M12 8v4l2.5 1.5"/></svg>';
const ICON_QMARK ='<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9.6 9.2a2.5 2.5 0 1 1 3.4 2.4c-.7.3-1 .8-1 1.6"/><path d="M12 16.5v.2"/></svg>';
// Savings now also absorb any sale markdown, so the tooltip has to name all three sources.
// "any sale reduction" keeps it accurate when nothing is marked down.
const SAVE_TOOLTIP = 'Savings are based on any sale reduction, any pack discount and the value of free medium print(s).';
const ICON_CARET = '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
const ICON_SEARCH = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>';
const ICON_GRID2 = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><rect x="3" y="4" width="8" height="16" rx="1.2"/><rect x="13" y="4" width="8" height="16" rx="1.2"/></svg>';
const ICON_GRID4 = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><rect x="2.5" y="4" width="3.6" height="16" rx="0.8"/><rect x="8" y="4" width="3.6" height="16" rx="0.8"/><rect x="13.4" y="4" width="3.6" height="16" rx="0.8"/><rect x="18.9" y="4" width="3.6" height="16" rx="0.8"/></svg>';

// Map a Boost filter-API product object to our lightweight card shape.
function mapBoostProduct(p) {
  const ii = (p.images_info && p.images_info[0]) || null;
  const img = (ii && ii.src) || (p.images && p.images[0]) || null;
  const ratio = ii && ii.width && ii.height ? ii.width + ' / ' + ii.height : null;
  // Sale badge signal. Boost's PER-CURRENCY fields (price_min_gbp / compare_at_price_min_gbp)
  // are not trustworthy — checked against the live storefront, they reported a product that was
  // visibly on sale as not on sale — so derive the markdown from the base-currency pair. Only
  // the PERCENTAGE is used, never the money: this store's market price lists are a uniform
  // multiplier, so the percentage is identical in every currency even though the amounts aren't.
  // The badge is decorative; every figure the shopper is charged still comes from the variant.
  const salePct = markdownPct(Number(p.price_min) || 0, Number(p.compare_at_price_min) || 0);
  return { id: p.id, handle: p.handle, title: p.title, vendor: p.vendor, image: img, ratio, salePct };
}

// Medium swatch tabs (Single mode). key = collection handle = ?medium URL value.
const SWATCH_MEDIUMS = [
  { key: 'graphic-design', label: 'Graphic Design', tag: 'medium2026-graphic design & illustration' },
  { key: 'painting', label: 'Painting', tag: 'medium2026-painting & drawing' },
  { key: 'photography', label: 'Photography', tag: 'medium2026-photography' },
  { key: 'mixed-media', label: 'Mixed Media', tag: 'medium2026-mixed media' },
];
const SWATCH_MAX = 16; // show up to 16 product swatches (if available) before "View more" (feedback #11)

function mediumFromTags(tags) {
  const lower = (tags || []).map((t) => String(t).toLowerCase());
  const hit = SWATCH_MEDIUMS.find((m) => lower.includes(m.tag));
  return hit ? hit.key : null;
}

// Shopify CDN URLs come out of the product JSON at full print resolution (e.g. 1100x1354).
// Rendered into a 64x80 swatch that is ~300 KB of wasted transfer per image, so ask the CDN
// for the display size instead — the same thing `image_url: width:` does in Liquid, and the
// same width-forcing pattern used for the frame swatches in _frameImgMap().
function cdnThumb(url, w) {
  if (!url) return url;
  let out = String(url).replace(/^\/\//, 'https://').replace(/([?&](?:width|height)=)\d+/gi, '$1' + w);
  if (!/[?&]width=/i.test(out)) out += (out.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  return out;
}

// Recently-viewed prints (per-browser, localStorage) — powers the selector tab + swatch tab.
const RECENT_KEY = 'drool_recent_prints';
const RECENT_MAX = 24;
// Bumped when the stored SHAPE changes. v2 adds `vendor` (the card's "By <artist>" line) and
// pins `image` to the product's FIRST image. Entries written before this carry neither, so
// _recentMeta() backfills them from /products/<handle>.js and upgradeRecent() rewrites them.
const RECENT_V = 2;
function getRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) { return []; }
}
function pushRecent(p) {
  if (!p || !p.handle) return;
  try {
    let list = getRecent().filter((x) => String(x.id) !== String(p.id));
    list.unshift({
      id: p.id, handle: p.handle, title: p.title,
      vendor: p.vendor || null,
      // `firstImage` is product.images.first; featured_image is only a fallback, because a
      // merchant can promote any image to featured and the card must always show the first.
      image: p.firstImage || p.image,
      imageW: p.imageW, imageH: p.imageH, ratio: p.ratio || null, v: RECENT_V,
    });
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch (e) { /* storage full / disabled */ }
}
// Write a backfilled vendor/first-image back into storage so the lookup happens once per
// product, not once per page view. Only marks an entry current when real data came back.
function upgradeRecent(handle, meta) {
  if (!handle || !meta || (!meta.vendor && !meta.image)) return;
  try {
    const list = getRecent();
    let changed = false;
    list.forEach((p) => {
      if (p.handle !== handle) return;
      if (meta.vendor && p.vendor !== meta.vendor) { p.vendor = meta.vendor; changed = true; }
      if (meta.image && p.image !== meta.image) { p.image = meta.image; changed = true; }
      if (Number(p.v) !== RECENT_V) { p.v = RECENT_V; changed = true; }
    });
    if (changed) localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch (e) { /* storage full / disabled */ }
}

// ---- Flash-sale ("was") pricing --------------------------------------------------------
// A markdown is a real price reduction: `price` drops and `compare_at_price` holds the old
// price. Everything else in this file prices off `variant.price`, which is ALREADY the sale
// price — these helpers only recover the pre-sale figure so the markdown can be shown as a
// saving alongside the bundle discount.
//
// `compare_at_price` is null for most of the catalogue and is sometimes stored EQUAL to
// `price`, so anything not strictly above `price` means "no markdown" and the was-price is
// simply the price. Never assume it is present, and never assume a fixed percentage: on this
// store the same sale is -20% on unframed variants and -10% on framed ones (the markdown
// applies to the print, not the frame), so any percentage must be derived per variant.
function wasPriceOf(v) {
  const p = (v && v.price) || 0;
  const c = (v && v.compare_at_price) || 0;
  return c > p ? c : p;
}

// Whole-number % off for badge copy, derived (never hardcoded) so it follows the sale when it
// rotates to different products. Returns 0 when there is no markdown.
function markdownPct(price, was) {
  if (!(was > price) || !was) return 0;
  return Math.round((1 - price / was) * 100);
}

function formatMoney(cents) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    try { return window.Shopify.formatMoney(cents, window.mrk_money_format); } catch (e) { /* fall through */ }
  }
  const fmt = window.mrk_money_format || '${{amount}}';
  const amount = (Math.round(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount);
}

function shortSize(value) {
  const m = String(value || '').match(/\(([^)]+)\)/);
  return m ? m[1] : value;
}

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    console.log('[BB] build ' + BB_BUILD + ' — bundle-builder.js loaded');

    // Sale A/B test: variant arm (html.bb-off-pdp — set by Visually / the theme's
    // wd-sale-ab-prepaint snippet, never by the theme itself). The variant keeps ONLY the
    // product swatch row: no pack selector, no pack mode, no single-card mutations — and no
    // URL (?bundle=/?prints=/?paid=&free=) or sessionStorage (drool_view_mode /
    // drool_bundle_v1) entry path can re-enter pack mode, because none of that state is
    // wired. The bundle entry product is exempt (belt-and-braces — the pre-paint snippet
    // also skips it), so /products/build-your-bundle always runs the full builder.
    if (document.documentElement.classList.contains('bb-off-pdp') && this.dataset.entryMode !== 'true') {
      this._initVariantSwatches();
      return;
    }

    this.entryMode = this.dataset.entryMode === 'true'; // "Build your Bundle" entry product
    this.tiers = this._readJSON('.bundle-builder__data') || {};
    // Zero-discount (sale mode) wording — Theme settings via the head-level copy JSON
    // emitted by wd-sale-ab-prepaint.liquid (document scope: the builder block file is
    // at Shopify's per-file Liquid size cap and can't host it). See _zeroCopy.
    this._copy = this._readJSON('.bundle-builder__copy', document) || {};
    // The LIVE ladder: catalogue order, minus packs the merchant has switched off. `single` is
    // always present in the JSON, so this is never empty.
    this.tierOrder = TIER_ORDER.filter((k) => this.tiers[k]);
    // Free-slot capacity = the largest free-print count any tier grants (merchant-configurable,
    // e.g. quad 3 / gallery 4 / 10+ pack 10). The free-slots array must hold this many so every
    // pack renders all of its free slots (items F1/F2). Never below the MAX_FREE floor.
    this._maxFree = Math.max(MAX_FREE, ...Object.keys(this.tiers).map((k) => Number(this.tiers[k] && this.tiers[k].free) || 0));
    this.product = this._readJSON('.bundle-builder__product') || {};
    this.materials = (this._readJSON('.bundle-builder__materials') || []).map((m) => ({
      title: m.title, image: m.image, badge: m.badge, isCanvas: /canvas/i.test(m.title || ''),
    }));
    this._canvasMat = this.materials.find((m) => m.isCanvas) || null;
    this._paperMat = this.materials.find((m) => !m.isCanvas) || this.materials[0] || null;
    this._canvasCache = {};
    this._paperCache = {};
    this._cartProductIds = new Set(); // product_ids currently in the cart (for the "In cart" slot badge)
    this._overflow = []; // prints trimmed by a downgrade — flow back in when the pack grows again
    this._persistKey = 'drool_bundle_v1';
    // If we landed on a CANVAS product PDP, the bundle should default to Canvas.
    this._landingIsCanvas = !!(this._canvasMat && /-canvas$/i.test(this.product.handle || ''));
    // Record this print in recently-viewed (skip the entry product, which isn't a print).
    if (!this.entryMode) pushRecent(this.product);
    this.variants = Array.isArray(window.main_product_data) ? window.main_product_data : [];
    this.sizes = [...new Set(this.variants.map((v) => v.option1).filter(Boolean))];
    this.frames = [...new Set(this.variants.map((v) => v.option2).filter(Boolean))];

    const selected = this._readJSON('#mrk-main-variant-data', document) || {};
    // Paid prints (incl. the landing print) default to the SMALLEST size + Unframed.
    const defaultSize = this._smallestSizeFor({ variants: this.variants }) || selected.option1 || this.sizes[0] || null;
    this._defaultSize = defaultSize;
    // Framing is now PER-SLOT (not a global choice). New prints default to Unframed.
    this._defaultFrame = this.frames.find((f) => /unframed/i.test(f)) || this.frames[0] || null;

    // R2.2: a ?bundle=<tier> URL param (e.g. from the PLP "Build my Wall" CTA)
    // pre-selects that tier; otherwise fall back to the block's default.
    let initialTier = this.dataset.defaultTier || 'single';
    const urlTier = new URLSearchParams(window.location.search).get('bundle');
    if (urlTier && this.tiers[urlTier]) {
      initialTier = urlTier;
    } else {
      // Carry the shopper's last view across PDPs (items 8/9): if they were on Single, the
      // next PDP opens on Single; if they were on a bundle tier, it opens on that tier.
      let savedView = null;
      try { savedView = sessionStorage.getItem('drool_view_mode'); } catch (e) {}
      if (savedView === 'single' || (savedView && this.tiers[savedView])) initialTier = savedView;
    }
    if (this.entryMode && initialTier === 'single') initialTier = this._entryTier(); // bundles only

    // Pre-built bundle mode (marketing links): ?paid=/&free= (explicit split) or ?prints=
    // (paid-first list) pre-fill the slots by product handle. Parsed ONCE here — every
    // other prefill hook in the file is gated on this._prefill, so when the params are
    // absent nothing changes anywhere. The URL bundle wins over ?bundle=/saved view.
    this._prefill = this._parsePrefillParams();
    if (this._prefill) initialTier = this._prefillFitTier();

    this.state = {
      tier: initialTier,
      frame: selected.option2 || this.frames[0] || null,
      material: this._landingIsCanvas ? this._canvasMat.title : (this._paperMat ? this._paperMat.title : null),
      removeFraming: false, // global "Remove framing service" toggle (item PDP-1)
      paidSlots: [],
      freeSlots: [],
    };
    for (let i = 0; i < MAX_PAID; i++) {
      // Slots ALWAYS start empty — never pre-populate the landing print (item G5).
      this.state.paidSlots.push({ assigned: false, product: this._currentProductRef(), size: defaultSize, frame: this._defaultFrame });
    }
    for (let i = 0; i < this._maxFree; i++) {
      this.state.freeSlots.push({ assigned: false, product: null, frame: this._defaultFrame });
    }

    this.tierButtons = [...this.querySelectorAll('.bundle-builder__tier')];
    if (this.entryMode) this.tierButtons.forEach((b) => { if (b.dataset.tier === 'single') b.style.display = 'none'; });
    this._initPppFootnote();
    this.priceEl = this.querySelector('.bundle-builder__price');
    this.fromEl = this.querySelector('.bundle-builder__price-from');
    this.compareEl = this.querySelector('.bundle-builder__price-compare');
    this.saleEl = this.querySelector('.bundle-builder__price-sale');
    this.badgeEl = this.querySelector('.bundle-builder__price-badge');
    this.bundleEl = this.querySelector('.bundle-builder__bundle');
    this.slotsEl = this.querySelector('.bundle-builder__slots');
    this.addSlotBtn = this.querySelector('.bundle-builder__add-slot');
    this.frameOptionsEl = this.querySelector('.bundle-builder__frame-options');
    this.materialEl = this.querySelector('.bundle-builder__material');
    this.materialOptionsEl = this.querySelector('.bundle-builder__material-options');
    this.atcBtn = this.querySelector('.bundle-builder__atc');

    this.browseBtn = this.querySelector('.bundle-builder__browse');
    this.tierButtons.forEach((btn) => btn.addEventListener('click', () => { this._userTierChange = true; this.selectTier(btn.dataset.tier); }));
    this._wireTierArrows();
    if (this.addSlotBtn) this.addSlotBtn.addEventListener('click', () => this._bumpTier());
    if (this.atcBtn) this.atcBtn.addEventListener('click', () => this._addToCart());
    if (this.browseBtn) this.browseBtn.addEventListener('click', () => this._openSelectorDrawer());

    const boostCfg = this._readJSON('.bundle-builder__boost') || {};
    this._boost = {
      shop: boostCfg.shop || (window.Shopify && window.Shopify.shop) || 'drool-art.myshopify.com',
      scope: boostCfg.collectionScope ? String(boostCfg.collectionScope) : '',
      canvasScope: boostCfg.collectionScopeCanvas ? String(boostCfg.collectionScopeCanvas) : '',
    };
    // Frame overlays (same PNGs the PDP composites) for slot-thumbnail framing and the cart's
    // _frame_id. PRIMARY source: the theme's global #mrk-frames-data template (rendered by
    // layout/theme.liquid on every page) — it paginates past Liquid's 50-metaobject cap, so it
    // holds the FULL frames list. The block JSON alone missed the portrait A-ratio frames
    // (entries past 50), which made the closest-ratio match stamp a 2-3 frame id and composite
    // with a white strip in the builder + cart. JSON stays as the fallback for pages where the
    // template lacks the frame-id attribute (snippet not yet updated).
    this._frames = this._readFramesTemplate() || this._readJSON('.bundle-builder__frames') || [];
    // Framing service (item PDP-1): per-print service fee + copy. removeFraming is the global toggle.
    this._framingSvc = this._readJSON('.bundle-builder__framing-service') || {};
    this.framingEl = this.querySelector('.bundle-builder__framing');
    this._selector = {
      built: false,
      view: 'all',            // 'all' (default) | 'best-sellers' | 'recent'
      facets: {},             // { pf_t_colour: Set(['Colour2026-Blue']), ... }
      facetDefs: FACET_DEFS,  // replaced by Boost's live filter tree once fetched
      page: 1,
      total: 0,
      loadedCount: 0,
      layout: getLayoutPref(),
      query: '',
      openDropdown: null,
      reqId: 0,
    };

    // Swatches (single mode) + View More modal.
    this.swatchesEl = this.querySelector('.bundle-builder__swatches');
    this.swatchTabsEl = this.querySelector('.bundle-builder__swatch-tabs');
    this.swatchRowEl = this.querySelector('.bundle-builder__swatch-row');
    this.modalEl = this.querySelector('.bundle-modal');
    this._expandNode = this.querySelector('.bundle-expand');
    // Re-parent the fixed overlays to <body> so they aren't trapped by the right
    // column's clip-path (which creates a containing block for position:fixed).
    if (this.modalEl) document.body.appendChild(this.modalEl);
    if (this._expandNode) document.body.appendChild(this._expandNode);
    const urlMedium = new URLSearchParams(window.location.search).get('medium');
    this._swatch = {
      built: false,
      // Default to "More from this artist" (the first tab) on a real PDP (feedback #10), unless the
      // URL explicitly requests a medium tab (e.g. arrived via a swatch click). Falls back to FBT when
      // the product has no vendor/artist.
      activeMedium:
        urlMedium && SWATCH_MEDIUMS.some((m) => m.key === urlMedium)
          ? urlMedium
          : (this.dataset.entryMode === 'true' ? (this.dataset.productMedium || SWATCH_MEDIUMS[0].key)
            : (this.dataset.vendor || (this.product && this.product.vendor) ? '__artist' : '__fbt')),
      loaded: {},
    };
    this._modal = { built: false, filter: FILTERS[0].handle };

    // Restore an in-progress bundle (added, not yet checked out) from a previous PDP.
    // Pre-built bundle mode: the URL param WINS over any saved bundle — clear it and
    // build from the handles instead (kicked off below, after the first render).
    if (this._prefill) this._persistClear();
    else this._restoreFromPersist();

    this._renderFrames();
    this.selectTier(this.state.tier, { silent: true });

    // Pre-built bundle mode: resolve the URL handles into the slots (async, fire-and-forget —
    // _prefillFromParams shows its own loading state and re-renders when done).
    if (this._prefill) this._prefillFromParams();

    // If the restored (or landing) material is Canvas, resolve each paper-based slot's
    // canvas sibling so prices/thumbs are correct without a manual toggle.
    if (this._canvasMat && this.state.material === this._canvasMat.title) {
      this._preloadEffectiveForAssigned().then((changed) => {
        if (changed) { this._reconcileSizes(); this._renderSlots(); this.recalculate(); }
      });
    }
    // Canvas PDP landing: free-slot prices must quote the PRINT (paper) sibling — free prints
    // are always art paper (item 6) — so resolve it up front and refresh once it's cached.
    if (this._landingIsCanvas) {
      this._ensurePaperFor(this.product.handle || '').then((p) => {
        if (p && this.state.tier !== 'single') { this._renderSlots(); this.recalculate(); }
      });
    }

    // Learn which products are already in the cart, for the "In cart" slot badge,
    // and keep it live as the cart changes elsewhere on the page (cart drawer, etc.).
    this._loadCart();
    // The theme fires TWO cart events: `cart:update` (events.js — native ATC) and `cart:updated`
    // (component-cart-items.js — cart drawer + PDP/cart product recommendations). Listen to BOTH,
    // or a print added from the PDP add-to-cart or a cart recommendation never refreshes the
    // bundle (mobile review #1: cart wasn't updating on those paths).
    const onCartEvent = () => {
      // Skip our own refetch when we just reconciled from a /cart/update.js response (perf); other
      // listeners (header count, PLP widget) still get the event.
      if (this._selfCartUpdate) { this._selfCartUpdate = false; return; }
      this._loadCart();
    };
    document.addEventListener('cart:update', onCartEvent);
    document.addEventListener('cart:updated', onCartEvent);

    // "Add to pack" button inside the shared quick-view modal (rendered by the
    // quick-view template, revealed only in pack mode). Delegated on document so it
    // works no matter when the modal content is injected. See _openQuickView.
    document.addEventListener('click', (e) => {
      const btn = e.target && e.target.closest && e.target.closest('.bb-qv-add-to-pack');
      if (btn) { e.preventDefault(); e.stopPropagation(); this._addFromQuickView(btn); }
    });

    // Every quick view records the print it opened (recordRecentPrint in assets/quick-add.js
    // — the same list this file writes on PDP load). Refresh the parts of the selector built
    // from that list, so a print just quick-viewed is there when the modal closes instead of
    // waiting for the next page load.
    document.addEventListener('drool:recent-prints-updated', () => {
      const has = getRecent().length > 0;
      if (this._selEls && this._selEls.recent) this._selEls.recent.hidden = !has;
      if (has && !this._hasRecentTab) this._renderSwatchTabs(); // first-ever entry adds the tab
      if (this._selector && this._selector.view === 'recent') this._loadSelector(false);
    });

  }

  // Open the native quick-view buy-box (same one the collection page uses) for a
  // print, in "pack mode": the modal's own Add-to-cart / framing service are hidden
  // (pack owns those) and an "Add to pack" CTA is shown instead (_addFromQuickView).
  // Falls back to the lightweight in-builder expand modal if the shared modal/JS
  // isn't available.
  _openQuickView(p) {
    // The modal's "See full details" is a plain PDP link — stamp the current view so that
    // navigation lands on the same view the shopper opened the quick view from.
    this._stampViewMode();
    const dialog = document.getElementById('quick-add-dialog');
    const url = p && p.handle ? '/products/' + p.handle : (p && p.url) || '';
    if (dialog && url && typeof window.mrkOpenQuickAdd === 'function') {
      // Stash the print so _addFromQuickView can build the slot even before the
      // buy-box variant script is ready.
      dialog.dataset.bbHandle = (p && p.handle) || '';
      dialog.dataset.bbProductId = (p && p.id != null) ? String(p.id) : '';
      dialog.dataset.bbImage = (p && p.image) || '';
      // When the NEXT add lands in a FREE slot (the grid button reads "ADD FREE PRINT"),
      // flag the dialog so the buy-box opens on the locked free size at £0.00: adding
      // without touching anything yields the free locked print, while a choice that can't
      // be free routes to a paid slot via _addProduct's preselect logic.
      const nextTarget = this._nextAddTarget();
      dialog.toggleAttribute('data-bb-free', !!(nextTarget && nextTarget.kind === 'free'));
      window.mrkOpenQuickAdd(url, { pack: true });
      if (dialog.hasAttribute('data-bb-free')) this._qvSetupFreeMode(dialog);
      return;
    }
    this._openExpand(p); // graceful fallback
  }

  // Free-mode quick view: the injected buy-box arrives async, so poll briefly until the
  // size radios exist, then (a) preselect the locked free size and (b) zero its shown
  // price. Re-zeroing across later variant morphs is handled by _qvBindFreeRezero.
  _qvSetupFreeMode(dialog) {
    this._qvBindFreeRezero();
    // Drop any observer left over from a previous open (it watches THAT dialog state).
    if (this._qvPriceObs) { this._qvPriceObs.disconnect(); this._qvPriceObs = null; }
    // A framed free print prices from the separate frame product, and whether it may stay free
    // depends on the print's framing-service tag — both async. Warm them now; each re-applies
    // the pricing when it lands.
    this._ensureChildFrames(dialog.dataset.bbHandle || '')
      .then(() => { if (dialog.hasAttribute('data-bb-free')) this._qvApplyFreePricing(dialog); })
      .catch(() => { /* frame price falls back to 0 */ });
    this._qvFramingSvc(dialog.dataset.bbHandle || '');
    const started = Date.now();
    clearInterval(this._qvFreePoll);
    this._qvFreePoll = setInterval(() => {
      const expired = Date.now() - started > 6000;
      if (!dialog.hasAttribute('data-bb-pack') || !dialog.hasAttribute('data-bb-free')) {
        clearInterval(this._qvFreePoll);
        return;
      }
      // Only the VISIBLE picker arm — checking a radio in the hidden arm morphs the wrong picker.
      const radios = [...dialog.querySelectorAll('.mrk-size-option input')].filter((r) => this._qvArmVisible(r));
      if (!radios.length) { if (expired) clearInterval(this._qvFreePoll); return; } // buy-box not injected yet
      // Wait for the CHILD-FRAME picker to finish rendering before touching the size. Preselecting
      // the locked size fires a `change`, and mrk.js answers that by refetching `?view=frame` and
      // REPLACING <frame-variant-picker> wholesale (mrk.js:523-526) with a page-global selector.
      // Firing it while the picker is still injecting replaces it with nothing, so its Black/White/
      // Oak swatches vanish and only the print's own "Unframed" option is left. It looked random
      // because _qvPreselectFreeSize only dispatches when the locked size isn't already selected —
      // i.e. on some products and not others. Prints with no child-frame picker skip the wait, and
      // the 6s cap still preselects rather than leaving the shopper on the wrong size.
      const fvp = dialog.querySelector('frame-variant-picker');
      const frameReady = !fvp || !!fvp.querySelector('.mrk-frame-switcher input');
      if (!frameReady && !expired) return;
      clearInterval(this._qvFreePoll);
      this._qvPreselectFreeSize(radios);
      this._qvApplyFreePricing(dialog);
    }, 100);
  }

  // Does this print carry the `framing-service` tag (the same signal as _framingSvcPrint)? The
  // selector card has no tags, so fetch the product once per handle and cache it. Returns null
  // while in flight — callers treat "unknown" as not-free and re-apply when it resolves.
  _qvFramingSvc(handle) {
    if (!handle) return false;
    if (!this._qvSvcCache) this._qvSvcCache = {};
    if (handle in this._qvSvcCache) return this._qvSvcCache[handle];
    this._qvSvcCache[handle] = null; // request in flight
    fetch('/products/' + handle + '.js')
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        this._qvSvcCache[handle] = !!(p && (p.tags || []).indexOf('framing-service') !== -1);
        const dialog = document.getElementById('quick-add-dialog');
        if (dialog && dialog.dataset.bbHandle === handle && dialog.hasAttribute('data-bb-free')) {
          this._qvApplyFreePricing(dialog);
        }
      })
      .catch(() => { this._qvSvcCache[handle] = false; });
    return null;
  }

  // Check the size radio matching the locked free size (first _freeKeywords hit — the
  // same priority _freeSizes uses) and fire change so the buy-box morphs to that variant.
  _qvPreselectFreeSize(radios) {
    let locked = null;
    for (const kw of this._freeKeywords()) {
      locked = radios.find((r) => this._normSize(r.getAttribute('data-value') || r.value).indexOf(kw) > -1);
      if (locked) break;
    }
    if (!locked || locked.checked) return;
    locked.checked = true;
    locked.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // The quick view's currently selected size + frame. The buy-box re-stamps #mrk-main-variant-data
  // with the fully-resolved variant on every change, which is the one reliable source — reading the
  // frame off the swatch inputs missed it whenever the product used a different picker (that's why
  // a framed selection was priced as unframed). The inputs stay as a fallback.
  // Is this node inside a picker that is actually rendered? Kept from the size-upsell A/B era,
  // when two picker arms shipped and a plain querySelector could read the HIDDEN arm's checked
  // radio (the size read back wrong, so the free-pricing block was skipped). Only one picker
  // (.wd-native-picker) renders now, but the visibility check still guards CSS-hidden options.
  _qvArmVisible(el) {
    if (!el || !el.closest) return false;
    const arm = el.closest('.wd-native-picker');
    if (arm && !arm.getClientRects().length) return false; // hidden A/B arm
    const field = el.closest('fieldset, .variant-option');
    if (field && !field.getClientRects().length) return false; // CSS-hidden option group
    return true;
  }
  // Strict = null when nothing is visible (caller falls through to another source);
  // otherwise the first visible match, else the first match at all.
  _qvPickVisible(dialog, selector, strict) {
    const nodes = [...dialog.querySelectorAll(selector)];
    const shown = nodes.find((n) => this._qvArmVisible(n));
    return shown || (strict ? null : nodes[0] || null);
  }

  _qvSelection(dialog) {
    // SIZE — the checked radio in the VISIBLE arm is exactly what the shopper sees.
    let size = '';
    const si = this._qvPickVisible(dialog, '.mrk-size-option input:checked, .variant-option input[name^="Size"]:checked');
    if (si) size = si.getAttribute('data-value') || si.value || '';

    // FRAME — four guarded sources, in the order that reflects what mrk.js actually priced:
    //  (a) the print's OWN option2 swatches, but ONLY while they're really rendered. On a
    //      `child-frames` print the theme hides them and they read a permanent "Unframed".
    //  (b) the child-frame COLOUR swatch. Accept it only when it carries a real `frame-color`:
    //      <frame-variant-picker> also contains a HIDDEN Size radio (frame-color="", value is a
    //      SIZE string), and a bare `frame-variant-picker input:checked` matched THAT — the frame
    //      then resolved to a size, _childFramePrice returned 0, and the real total stayed put.
    //  (c) .mrk-frame-color — mrk.js stamps the resolved colour here in the SAME updateVariants()
    //      pass that writes .mrk-main-price, so it can never disagree with the price on screen.
    //  (d) #mrk-main-variant-data.option2 — only when there's no frame picker at all; on a
    //      child-frames print it is permanently "Unframed".
    let frame = '';
    const own = this._qvPickVisible(
      dialog,
      'variant-picker .variant-option--swatches input:checked, variant-picker .mrk-swatches-slider-wrapper input:checked',
      true
    );
    if (own) frame = own.getAttribute('data-value') || own.value || '';

    if (!frame || /unframed|no frame/i.test(frame)) {
      const real = [...dialog.querySelectorAll('.mrk-frame-switcher input:checked')].find((n) => {
        const fc = (n.getAttribute('frame-color') || '').trim();
        return fc && !/unframed|no frame/i.test(fc); // a real colour, never the hidden size radio
      });
      if (real) {
        frame = real.value || real.getAttribute('frame-color') || frame;
      } else {
        const stamped = dialog.querySelector('.mrk-frame-color');
        const v = stamped ? String(stamped.value || '').trim() : '';
        if (v && !/unframed|no frame/i.test(v)) frame = v;
      }
    }

    if (!size || (!frame && !dialog.querySelector('frame-variant-picker'))) {
      try {
        const el = dialog.querySelector('#mrk-main-variant-data');
        const v = el ? JSON.parse(el.textContent || '{}') : null;
        if (v) {
          if (!size) size = v.option1 || '';
          if (!frame) frame = v.option2 || '';
        }
      } catch (e) { /* keep what the pickers gave us */ }
    }
    return { size, frame };
  }

  // Free-print pricing in the quick view. TWO independent rules:
  //   • SIZE LIST — while the pack's next slot is free, every locked-size row reads struck-original
  //     → £0.00 no matter which size is currently selected (pick A2 and the A3 row still shows £0).
  //   • MAIN PRICE — depends on the SELECTION: locked size + unframed → £0.00; locked size + framed
  //     → the separate FRAME product's price only (the print is free, the frame is the paid part,
  //     same as .bundle-builder__price-menu in the slot); any other size → the real price stands.
  _qvApplyFreePricing(dialog) {
    const { size, frame } = this._qvSelection(dialog);
    const framed = !!(frame && !/unframed|no frame/i.test(frame));
    const svc = this._qvFramingSvc(dialog.dataset.bbHandle || '');
    const zero = formatMoney(0);

    // ---- Ownership tracking. mrk.js repaints these nodes on every size/frame change and we
    // overwrite some of them with free-print figures, so each write stamps the EXACT string it
    // wrote into data-wd-bb-set. A later pass can then tell OUR value from one mrk.js has since
    // recomputed. Without this the cached "real" price was write-once and got handed back over a
    // FRESH mrk.js total: picking a frame on a locked free print restored the previous UNFRAMED
    // £32.00 under a £28.00 strike (a strike BELOW the price — only our code can produce that),
    // and because the restore also deleted the cache, the next frame change had nothing to write
    // back, mrk.js's real total survived, and the price appeared to jump up.
    const isOurs = (el) => !!el && el.dataset.wdBbSet !== undefined && el.textContent.trim() === el.dataset.wdBbSet;
    // Refresh the cached real value — but only while mrk.js still owns the node, since that is
    // the only time its text IS the real price for the currently selected size + frame.
    const capture = (el, key) => { if (el && !isOurs(el)) el.dataset[key] = el.textContent; };
    const ourWrite = (el, text) => { if (!el) return; el.textContent = text; el.dataset.wdBbSet = text; };
    // Hand the node back to mrk.js. Only overwrite while we still own the text: once mrk.js has
    // repainted it, ITS value is the correct one and the cache is stale by definition.
    const restore = (el, key) => {
      if (!el) return;
      if (isOurs(el) && el.dataset[key] !== undefined) el.textContent = el.dataset[key];
      delete el.dataset[key];
      delete el.dataset.wdBbSet;
    };

    // A framed selection on a print with NO framing service can never be free — there's no child
    // frame to carry the frame as a separate paid line, so _addProduct routes it to a PAID slot
    // (framedBlocksFree). The locked size therefore stops being locked here too: put every real
    // price back so nothing reads as free. `svc === false` strictly — while the tag lookup is in
    // flight it's null, and unlocking on unknown data would flicker.
    // (!removeFraming) covers the normal case: with the service kept, ANY framed choice lands in a
    // paid slot. (svc === false) covers the toggle being on but the print not offering the service.
    // Deliberately not `svc !== true` — while the tag lookup is in flight svc is null, and
    // unlocking on unknown data would flicker.
    if (framed && (!this.state.removeFraming || svc === false)) {
      dialog.querySelectorAll('.mrk-size-item').forEach((item) => {
        restore(item.querySelector('.mrk-size-price'), 'wdBbReal');
        restore(item.querySelector('.mrk-size-compare-at-price'), 'wdBbOrig');
      });
      restore(dialog.querySelector('product-price .mrk-main-price'), 'wdBbReal');
      restore(dialog.querySelector('product-price .mrk-main-compare-price'), 'wdBbOrig');
      this._qvWatchPrice(dialog); // re-apply if they switch back to Unframed
      return;
    }

    // ---- Size list. Every write is conditional on the text actually differing, so re-running
    // (late frame data, a morph, the observer) is a no-op once the values are right — that's what
    // stops the observer looping.
    dialog.querySelectorAll('.mrk-size-item').forEach((item) => {
      const title = item.querySelector('.mrk-size-item-title');
      const price = item.querySelector('.mrk-size-price');
      if (!title || !price) return;
      if (!this._isFreeSizeAnyRatio(title.textContent)) return; // only locked free sizes
      const cur = price.textContent.trim();
      if (cur !== zero) capture(price, 'wdBbReal'); // refresh the real price before overwriting
      const compare = item.querySelector('.mrk-size-compare-at-price');
      if (compare) {
        capture(compare, 'wdBbOrig');
        // On a marked-down print mrk.js has ALREADY rendered the pre-sale price here (£40.00 beside
        // the £32.00 sale price) — leave it exactly as it is. A free print must be struck through at
        // its true worth, not at the sale price the shopper would otherwise have paid; this is the
        // same contract _slotPriceEl() follows for the slot cards (`was = wasPriceOf(v)`).
        // Only a print with NO compare-at (blank — the ~95% that aren't on sale) gets the real price
        // injected, so a free row still shows something struck through instead of a bare £0.00.
        const real = (price.dataset.wdBbReal || '').trim();
        if (real && !(compare.dataset.wdBbOrig || '').trim() && compare.textContent.trim() !== real) ourWrite(compare, real);
      }
      if (cur !== zero) ourWrite(price, zero);
    });
    this._qvWatchPrice(dialog); // keep the rows + price corrected across mrk.js's recalcs

    // ---- Main ("total") price.
    const main = dialog.querySelector('product-price .mrk-main-price');
    const cmp = dialog.querySelector('product-price .mrk-main-compare-price');
    if (!main) return;
    // A framed selection lands in a PAID slot, so this hands the buy-box back to mrk.js and its
    // real print + frame total stands (e.g. £76.00 struck -> £60.80) instead of a cached figure.
    const restoreMain = () => { restore(main, 'wdBbReal'); restore(cmp, 'wdBbOrig'); };
    // The print itself is free at a locked size. A frame is ALWAYS a paid extra (it ships as a
    // separate child-frame line), so a framed selection stays free as long as the print offers the
    // framing service — the professional-framing toggle only governs the service fee, not this.
    const keepsFree = this._isFreeSizeAnyRatio(size)
      && (!framed || (this.state.removeFraming && svc === true));
    if (!keepsFree) { restoreMain(); return; }

    let target = zero;
    if (framed) {
      // Price THIS print's own child frame (custom.child_frames). If that data isn't in yet, or no
      // frame variant exists for this size/colour, leave the real price alone — never flash £0.00
      // for a framed print. _childFramesFor re-applies this once the lookup lands.
      const framePrice = this._childFramePrice(dialog.dataset.bbHandle || '', size, frame);
      if (!framePrice) { restoreMain(); return; }
      target = formatMoney(framePrice);
    }
    const cur = main.textContent.trim();
    if (cur !== target) capture(main, 'wdBbReal'); // the real full price mrk.js last wrote
    if (cmp) {
      capture(cmp, 'wdBbOrig');
      // Same rule as the size rows above: keep mrk.js's pre-sale figure when the print is marked
      // down, and only fill a blank compare-at with the real price.
      const real = (main.dataset.wdBbReal || '').trim();
      if (real && !(cmp.dataset.wdBbOrig || '').trim() && cmp.textContent.trim() !== real) ourWrite(cmp, real);
      cmp.style.display = '';
    }
    if (cur !== target) ourWrite(main, target);
  }

  // mrk.js recomputes and rewrites the buy-box price after every variant change — it overwrote our
  // free-print figure milliseconds after we set it (the £83.95 print+frame total). Watch the dialog
  // and re-apply. Observing the dialog (not the price node) survives the node being replaced by a
  // morph; the rAF debounce plus the write-only-if-different rule above make it settle in one pass.
  _qvWatchPrice(dialog) {
    if (this._qvPriceObs || typeof MutationObserver === 'undefined') return;
    let queued = false;
    this._qvPriceObs = new MutationObserver(() => {
      if (!dialog.hasAttribute('data-bb-pack') || !dialog.hasAttribute('data-bb-free')) {
        this._qvPriceObs.disconnect();
        this._qvPriceObs = null;
        return;
      }
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; this._qvApplyFreePricing(dialog); });
    });
    this._qvPriceObs.observe(dialog, { childList: true, subtree: true, characterData: true });
  }

  // Re-apply the free-print pricing after every buy-box morph (each variant change
  // re-renders the size list + price with real values). Bound once; no-ops unless the
  // pack quick view is open in free mode.
  _qvBindFreeRezero() {
    if (this._qvFreeRezeroBound) return;
    this._qvFreeRezeroBound = true;
    const rezero = () => {
      const dialog = document.getElementById('quick-add-dialog');
      if (!dialog || !dialog.hasAttribute('data-bb-pack') || !dialog.hasAttribute('data-bb-free')) return;
      // The morphed size list lands after the event — apply on the next frames.
      requestAnimationFrame(() => requestAnimationFrame(() => this._qvApplyFreePricing(dialog)));
    };
    document.addEventListener('variant:update', rezero);
    document.addEventListener('mrkVariantChanged', rezero);
  }

  // Add the print currently shown in the quick-view modal to the pack, honouring the
  // shopper's chosen size + frame (read from the buy-box's #mrk-main-variant-data),
  // then close the modal.
  async _addFromQuickView(btn) {
    const dialog = document.getElementById('quick-add-dialog');
    if (!dialog) return;
    const handle = dialog.dataset.bbHandle || (btn && btn.dataset.handle) || '';
    if (!handle) return;

    // The checked size radio's data-variant-id reflects the FULLY selected variant
    // (size + frame): the picker morphs on every change, re-stamping that id. It's the
    // single most reliable source, so pass it to _addProduct to resolve size + frame.
    // data-value / the variant-data script are fallbacks only.
    let variantId = null;
    let size = null;
    let frame = null;
    let frameColor = null;
    const sizeInput = dialog.querySelector('.mrk-size-option input:checked, .variant-option input[name^="Size"]:checked');
    if (sizeInput) {
      variantId = sizeInput.getAttribute('data-variant-id') || null;
      size = sizeInput.getAttribute('data-value') || sizeInput.value || null;
    }
    // Frame model 1: the print's own frame swatch selected directly in <variant-picker>.
    const mainFrameChecked = dialog.querySelector(
      'variant-picker .variant-option--swatches input:checked, variant-picker .mrk-swatches-slider-wrapper input:checked'
    );
    if (mainFrameChecked) frame = mainFrameChecked.getAttribute('data-value') || mainFrameChecked.value || null;

    // Frame model 2: a separate <frame-variant-picker> (linked child frame). Both pickers stamp
    // the SAME `frame-color` on their swatches, so map the frame-picker's selected swatch to the
    // print's OWN frame swatch in <variant-picker> and take THAT swatch's value — the print's real
    // option2 — with no colour guessing. frameColor is kept as a fallback for _addProduct when the
    // print has no matching frame swatch (true child-frame products).
    if (dialog.querySelector('frame-variant-picker')) {
      const fvpChecked = dialog.querySelector('.mrk-frame-switcher input:checked');
      const fc = fvpChecked ? (fvpChecked.getAttribute('frame-color') || '').trim().toLowerCase() : '';
      if (fc) {
        frameColor = fc;
        const mainMatch = [
          ...dialog.querySelectorAll('variant-picker .mrk-swatches-slider-wrapper input, variant-picker .variant-option--swatches input'),
        ].find((o) => (o.getAttribute('frame-color') || '').trim().toLowerCase() === fc);
        if (mainMatch) frame = mainMatch.getAttribute('data-value') || mainMatch.value || frame;
      }
    }

    // Fallback: the variant-data script, if the inputs weren't found.
    if (!variantId || !size || !frame) {
      let sel = {};
      try { sel = JSON.parse((dialog.querySelector('#mrk-main-variant-data') || {}).textContent || '{}'); } catch (e) { }
      if (!variantId) variantId = (sel && sel.id) || null;
      if (!size) size = (sel && sel.option1) || null;
      if (!frame) frame = (sel && sel.option2) || null;
    }

    const card = {
      handle,
      id: dialog.dataset.bbProductId || '',
      image: dialog.dataset.bbImage || '',
    };
    // _addProduct swaps the button to "Adding…" and only restores it (to "+ ADD") on failure — so
    // the quick-view "Add to pack" button would stay stuck on "Adding…" if the modal didn't close.
    // Capture its own label and always restore it + close the modal in a finally (mobile review #4).
    const qvLabel = btn ? btn.textContent : '';
    try {
      await this._addProduct(card, btn, { variantId, size, frame, frameColor });
    } finally {
      if (btn) { btn.textContent = qvLabel; btn.disabled = false; btn.dataset.bbAdding = ''; }
      // Close the quick-view. Prefer the element's own method; fall back to the native <dialog> and
      // the close button so a missing/renamed API can't leave the modal open showing "Adding…".
      if (typeof dialog.closeDialog === 'function') dialog.closeDialog();
      else {
        const nativeDialog = dialog.querySelector('dialog');
        if (nativeDialog && typeof nativeDialog.close === 'function') nativeDialog.close();
        const closeBtn = dialog.querySelector('[ref="closeButton"], .quick-add-modal__close');
        if (closeBtn) closeBtn.click();
      }
    }
  }

  // Fetch the cart and note which product_ids it holds, then refresh slot badges.
  // Map a raw cart object (from /cart.js OR a /cart/update.js response) into our state + reconcile
  // the bundle against it. Split out so removals can reuse the update-response and skip a 2nd fetch.
  _applyCartData(cart) {
    const prevCartIds = this._cartProductIds || new Set(); // what was in the cart before this refresh
    this._cartProductIds = new Set((cart.items || []).map((i) => String(i.product_id)));
    this._cartItems = (cart.items || []).map((i) => ({
      product_id: i.product_id, price: i.original_price || i.price, props: i.properties || {},
      type: (i.product_type || '').toLowerCase(), title: i.product_title || i.title || '',
      handle: i.handle, image: i.image || i.featured_image || null,
      optionsValues: i.options_with_values || null, variantTitle: i.variant_title || '',
      key: i.key, variant_id: i.variant_id, // for removing the exact cart line
      parentKey: (i.parent_relationship && i.parent_relationship.parent && i.parent_relationship.parent.key) || null,
    }));
    // #2: a print removed from the CART (e.g. via the cart drawer) should also leave the bundle.
    // Clear any assigned state slot whose product WAS in the cart but no longer is — otherwise it
    // lingers as an editable "phantom" slot once it's no longer in-cart.
    const nowIds = this._cartProductIds;
    let reconciled = false;
    // The placeholder a cleared PAID slot falls back to. _currentProductRef() dereferences
    // this.product, which isn't guaranteed on every entry path — and this runs inside the cart
    // REMOVAL path, so a throw here used to abort the loop with the removed print still in the
    // pack, and (via _loadCart's catch) wipe every cart id, dropping the "In cart" badges too.
    let blankRef = null;
    try { blankRef = this._currentProductRef(); } catch (e) { blankRef = null; }
    const reconcile = (store, free) => {
      (store || []).forEach((s) => {
        if (s && s.assigned && s.product && prevCartIds.has(String(s.product.id)) && !nowIds.has(String(s.product.id))) {
          s.assigned = false;
          s.product = free ? null : blankRef;
          s.size = free ? s.size : this._defaultSize;
          s.frame = this._defaultFrame;
          reconciled = true;
        }
      });
    };
    reconcile(this.state.paidSlots, false);
    reconcile(this.state.freeSlots, true);
    if (reconciled) this._persistSave();
    this._autoBumpForCart();   // grow the pack to fit prints added via the cart (feedback)
  }

  // Prints added straight to the cart (e.g. from cart recommendations) don't run through _addProduct,
  // so the pack tier never grew to hold them — leaving extra medium prints stuck as paid because the
  // smaller tier only unlocks e.g. 2 free slots (feedback). Bump the tier UP (never down) to the
  // smallest pack whose capacity fits the total prints, so the right number of free slots open.
  _autoBumpForCart() {
    if (this.state.tier === 'single') return; // don't pull a shopper out of single view
    const cartPrints = (this._cartItems || []).filter((l) => (l.type || '').indexOf('print') > -1 && !/framing service/i.test(l.title || '')).length;
    const inProgress = this.state.paidSlots.filter((s) => s && s.assigned && !this._inCart(s)).length
      + this.state.freeSlots.filter((s) => s && s.assigned && !this._inCart(s)).length;
    const total = cartPrints + inProgress;
    let bumped = false;
    let guard = 0;
    while (guard++ < this.tierOrder.length && !this._isTopTier() && total > (this._tier().paid + this._tier().free)) {
      const idx = this.tierOrder.indexOf(this.state.tier);
      if (idx < 0 || idx >= this.tierOrder.length - 1) break;
      this.state.tier = this.tierOrder[idx + 1];
      bumped = true;
    }
    if (bumped) {
      try { sessionStorage.setItem('drool_view_mode', this.state.tier); } catch (e) {}
      if (this.tierButtons) this.tierButtons.forEach((btn) => btn.setAttribute('aria-checked', btn.dataset.tier === this.state.tier ? 'true' : 'false'));
    }
  }

  // The cart's print lines, unclassified. Excludes the physical frame + framing-service children.
  _cartPrintLines() {
    return (this._cartItems || []).filter((l) => (l.type || '').indexOf('print') > -1 && !/framing service/i.test(l.title || ''));
  }

  // Cart-line twin of _canTakeFreeSlot, judged on what a cart line actually carries. The framing
  // rule needs no special case here: a FREE framed print ships as an unframed print line plus a
  // separate child-frame line, so the print half is still giveable, whereas a framed print with the
  // service kept is a framed VARIANT — that single line IS the frame, and it belongs in a paid slot.
  _cartLineFreeEligible(l, size, frame) {
    if (/canvas/i.test(l.type || '') || /-canvas$/i.test(l.handle || '')) return false;
    if (!this._isFreeSizeAnyRatio(size)) return false;
    return !frame || /unframed/i.test(frame);
  }

  // Every print in the pack from BOTH sources in one list — the unit the checkout discount actually
  // works on. A state slot that is already in the cart is represented by its cart line, never twice.
  // Order matters for ties: committed cart lines first (so an existing free print keeps its status
  // rather than flickering when an equally-priced print is added), then free slots before paid ones.
  _packPrints() {
    const out = [];
    this._cartPrintLines().forEach((l) => {
      const [size, frame] = this._parseCartVariant(l);
      out.push({ ref: l, price: l.price || 0, eligible: this._cartLineFreeEligible(l, size, frame) });
    });
    [...this.state.freeSlots, ...this.state.paidSlots].forEach((s) => {
      if (!s || !s.assigned || this._inCart(s)) return;
      out.push({ ref: s, price: this.slotPrice(s), eligible: this._canTakeFreeSlot(s) });
    });
    return out;
  }

  // How many prints the pack gives away, derived ONLY from the tier and the total print count so it
  // can never go circular with the free/paid classification that reads it. Mirrors the long-standing
  // shape of _freeEarnedCount (paid capacity fills first, nothing free below two paid prints).
  _freeQuota(total) {
    const tier = this._tier();
    if (!tier || !(Number(tier.free) > 0)) return 0;
    const paid = Math.min(total, Number(tier.paid) || 0);
    if (paid < 2) return 0;
    return Math.min(total - paid, Number(tier.free) || 0);
  }

  // THE single source of truth for which prints are free — slots and cart lines alike, ranked
  // together, because checkout ranks them together. `_bundle_free` is deliberately ignored: it is
  // stamped once at add-to-cart and then frozen, while the discount re-reads the whole cart on every
  // change, so the two diverge the moment anything else lands in the cart (a second pack, a
  // Quick-Add, a cart recommendation, a removal). Returns a Set of the refs that are free.
  _rankPackFree() {
    const prints = this._packPrints();
    const quota = this._freeQuota(prints.length);
    const free = new Set();
    if (quota <= 0) return free;
    prints
      .filter((p) => p.eligible)
      .sort((a, b) => a.price - b.price)
      .slice(0, quota)
      .forEach((p) => free.add(p.ref));
    return free;
  }

  async _loadCart() {
    // Reading the cart and APPLYING it are separate, and NEITHER failure is destructive.
    //
    // A failed read tells us nothing about the cart, so it must never assert an empty one. Clearing
    // _cartProductIds here blanked the "In cart" badge AND the locked state on every print at once,
    // because both are gated on it. Safari made that routine: the theme fires BOTH `cart:update` and
    // `cart:updated`, so every cart change queues two overlapping /cart.js reads while the drawer is
    // re-rendering its sections, and Safari drops in-flight fetches under that load far more readily
    // than Chrome. Keeping the last known cart is the safer wrong answer — the next successful read
    // corrects it, whereas asserting "empty" unlocks in-cart prints the shopper can then double-add.
    const seq = (this._cartLoadSeq = (this._cartLoadSeq || 0) + 1);
    const read = async () => {
      try { return await (await fetch('/cart.js')).json(); } catch (e) { return null; }
    };
    let data = await read();
    // One quick retry. A dropped read is exactly what leaves the pack showing a cart from BEFORE
    // the shopper's last add — retrying turns Safari's dropped fetch into a correct pack instead.
    if (!data) { await new Promise((r) => setTimeout(r, 200)); data = await read(); }
    if (!data) console.warn('[bundle-builder] could not read the cart; keeping the last known state');
    // Apply any snapshot NEWER than the one already applied, keyed on its own sequence number.
    // Deliberately NOT "discard this response because a newer read has started": that threw away
    // good data, and when the newer read then failed the pack was left rendering a pre-add cart —
    // the prints just added showed unbadged and unlocked while sitting in the cart. Ordering is
    // still safe, because an older snapshot can never overwrite a newer one that already landed.
    //
    // `items: []` is a genuinely EMPTY cart and must still clear the pack. A payload carrying no
    // `items` ARRAY at all is a bad read (an error body, an HTML challenge page) — _applyCartData
    // reads that as `(cart.items || [])` and would silently wipe everything, so it stops here.
    if (data && Array.isArray(data.items) && seq > (this._cartAppliedSeq || 0)) {
      this._cartAppliedSeq = seq;
      try { this._applyCartData(data); }
      catch (e) { console.warn('[bundle-builder] cart apply failed; keeping the cart data already read', e); }
    }
    if (this.state.tier !== 'single') {
      // A cart change (e.g. deleting paid prints from the cart drawer) can leave more free prints
      // than the remaining paid earn — rebalance so a free print becomes paid (feedback: never more
      // free than paid).
      // Re-arranging the pack must never be able to stop the RE-RENDER: if it throws, the slots
      // keep whatever was drawn before the cart loaded — no "In cart" badges, stale prices — and
      // the failure is invisible. Render from the cart data regardless, and say so in the console.
      try {
        this._enforceFreeCap();
        this._rebalanceFreeSlots(); // a cart change can make a paid print the cheapest eligible one
      } catch (e) {
        console.warn('[bundle-builder] pack rebalance failed; rendering the cart state anyway', e);
      }
      this._renderSlots(); this.recalculate(); // CTA depends on cart (scenario d)
      if (this._selector && this._selector.mode !== 'browse') this._markCards(); // keep selector "Added" states in sync as the cart changes (feedback)
      this._updateSelectorCounter(); // fold cart-added prints into the selector's progress widget (thumbs/count/label) on every cart change
    }
  }

  // Prints already in the cart that belong to THIS bundle tier (for the pack total).
  // ALL prints in the cart count toward the pack — however they were added (single PDP, PLP
  // Quick-Add, or the bundle builder). Excludes the physical frame + framing-service child lines.
  _cartBundle() {
    const lines = this._cartPrintLines();
    // Free/paid comes from the price rank, NOT the frozen `_bundle_free` property — see
    // _rankPackFree. This is what keeps the columns, the per-print £0.00 and the pack total in
    // step with checkout after add-to-cart, not just before it.
    const freeSet = this._rankPackFree();
    const toObj = (l) => {
      const [size, frame] = this._parseCartVariant(l);
      const price = l.price || 0;
      // cart.js carries no compare_at_price, so recover the pre-sale figure from the product
      // when we already hold it (the landing print, or one still sitting in a slot) — that
      // covers prints added through this builder, so SAVE doesn't lurch when a print moves
      // from a slot into the cart. Otherwise fall back to the price: understating a markdown
      // is safe, overstating one is not.
      const known = this._findKnownProduct(l.product_id);
      const kv = known && this.variantFor(known, size, frame);
      return { id: l.product_id, key: l.key, variantId: l.variant_id, handle: l.handle, title: l.title, image: l.image, price, was: kv ? Math.max(wasPriceOf(kv), price) : price, size, frame, free: freeSet.has(l) };
    };
    const objs = lines.map(toObj);
    const paidLines = objs.filter((o) => !o.free);
    const freeLines = objs.filter((o) => o.free);
    return {
      paidLines, freeLines,
      paidCount: paidLines.length, paidValue: paidLines.reduce((s, o) => s + o.price, 0),
      freeCount: freeLines.length, freeValue: freeLines.reduce((s, o) => s + o.price, 0),
      paidWasValue: paidLines.reduce((s, o) => s + o.was, 0),
      freeWasValue: freeLines.reduce((s, o) => s + o.was, 0),
    };
  }

  // Parse a cart line's size / frame from its option values (or "Size / Frame" variant title).
  _parseCartVariant(l) {
    let size = null, frame = this._defaultFrame;
    if (l.optionsValues && l.optionsValues.length) {
      l.optionsValues.forEach((ov) => {
        const n = (ov.name || '').toLowerCase();
        if (n.indexOf('size') > -1) size = ov.value;
        else if (n.indexOf('fram') > -1) frame = ov.value;
      });
      if (!size && l.optionsValues[0]) size = l.optionsValues[0].value;
    } else if (l.variantTitle) {
      const parts = l.variantTitle.split(' / ');
      size = parts[0] || null;
      if (parts[1]) frame = parts[1];
    }
    return [size || this._defaultSize, frame];
  }

  // A synthetic (display-only) LOCKED slot built from a cart print line, so it renders as an
  // "In cart" slot with the right thumb/size/frame/price without being in the editable state.
  _cartLineSlot(o) {
    const known = this._findKnownProduct(o.id);
    // Cart-only lines (print added on ANOTHER PDP / quick-add): the cart line's own image is
    // the FIRST (framed) product image, so the framed thumb composited the wrong art. Resolve
    // the real product once per handle (second image + ratio + type, the same positional
    // convention as everywhere else) and re-render when it lands.
    const fetched = !known ? this._cartArt(o.handle) : null;
    const product = known ? {
      id: o.id, handle: known.handle || o.handle, title: o.title || known.title, vendor: known.vendor || null,
      type: known.type || '',
      image: known.image || o.image, artImage: known.artImage || o.image, ratio: known.ratio || null,
      framingService: !!known.framingService, _resolvedArt: true,
      variants: (known.variants && known.variants.length) ? known.variants : [{ id: 0, option1: o.size, option2: o.frame, price: o.price }],
    } : {
      id: o.id, handle: o.handle, title: o.title, vendor: (fetched && fetched.vendor) || null,
      type: (fetched && fetched.type) || '',
      image: o.image, artImage: (fetched && fetched.artImage) || o.image, ratio: (fetched && fetched.ratio) || null,
      variants: [{ id: 0, option1: o.size, option2: o.frame, price: o.price }],
    };
    return { assigned: true, _fromCart: true, size: o.size, frame: o.frame, _cartKey: o.key, _variantId: o.variantId, product };
  }

  // Cached art lookup for cart-line products this page doesn't know. Fetches /products/*.js
  // once per handle; while in flight (or on failure) returns null so the caller falls back to
  // the line image, then re-renders the slots when the real art arrives. Display-only — the
  // line's price/variants are untouched.
  _cartArt(handle) {
    if (!handle) return null;
    if (!this._cartArtCache) this._cartArtCache = {};
    if (handle in this._cartArtCache) return this._cartArtCache[handle];
    this._cartArtCache[handle] = null; // request in flight
    fetch('/products/' + handle + '.js')
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) return;
        const imgs = p.images || [];
        const artMedia = (p.media || [])[1] || (p.media || [])[0];
        let ratio = 0;
        if (artMedia) {
          const w = artMedia.width || (artMedia.preview_image && artMedia.preview_image.width);
          const h = artMedia.height || (artMedia.preview_image && artMedia.preview_image.height);
          if (w && h) ratio = w / h;
        }
        this._cartArtCache[handle] = {
          artImage: imgs[1] || imgs.find((u) => /mock-up/i.test(u)) || imgs[0] || null,
          ratio: ratio || 0.707,
          type: p.type || '',
          vendor: p.vendor || null,
        };
        if (this.state.tier !== 'single') this._renderSlots();
      })
      .catch(() => { /* keep the line-image fallback */ });
    return null;
  }

  // Look up the full product (raw artImage + ratio) for a cart line id, from the current PDP
  // product or the bundle-state slots — so in-cart thumbs render the correct (un)framed art.
  _findKnownProduct(id) {
    const sid = String(id);
    if (this.product && String(this.product.id) === sid) return this._currentProductRef();
    const find = (arr) => { for (const s of (arr || [])) { if (s && s.product && String(s.product.id) === sid) return s.product; } return null; };
    return find(this.state.paidSlots) || find(this.state.freeSlots) || null;
  }

  // Cheapest single-print price (smallest size, unframed) — used to estimate empty slots
  // in the projected "From" pack total. Prices are uniform across prints on this store.
  _cheapestPrice() {
    const v = this._cheapestVariant();
    return v ? v.price : 0;
  }

  // The cheapest VARIANT, not just its price — the was-price projection for empty slots has to
  // come from the same variant as the price projection, or the two drift apart and SAVE jumps
  // around as slots are filled.
  _cheapestVariant() {
    let list = (this.variants || []).filter((v) => v && v.price > 0);
    if (!list.length) {
      // Entry product / no landing variants: fall back to a filled slot's cheapest variant.
      const s = this.state.paidSlots.find((x) => x && x.assigned && x.product);
      const eff = s && (this._effective(s) || s.product);
      list = ((eff && eff.variants) || []).filter((v) => v && v.price > 0);
    }
    if (!list.length) return null;
    return list.reduce((best, v) => (v.price < best.price ? v : best), list[0]);
  }

  // Pre-sale twin of _cheapestPrice(); equal to it when nothing is on sale.
  _cheapestWasPrice() {
    const v = this._cheapestVariant();
    return v ? wasPriceOf(v) : 0;
  }

  // Representative value of a FREE (medium) print — the LOCKED-size, unframed price of the
  // PRINT (paper) product. Free prints are always art paper (item 6), so on a canvas PDP the
  // paper sibling's variants (preloaded in connectedCallback) are used, never canvas prices.
  // Used for the struck price on an empty free slot (item 3) and the pack totals.
  _freePrintValue() {
    const v = this._freePrintVariant();
    return (v && v.price) || this._cheapestPrice();
  }

  // The free print's VARIANT (locked medium size, unframed, paper product), so price and
  // was-price are always read off the same row.
  _freePrintVariant() {
    let variants = this.variants;
    if (this._landingIsCanvas) {
      const base = (this.product.handle || '').replace(/-canvas$/i, '');
      const paper = this._paperCache[base];
      if (paper && paper.variants && paper.variants.length) variants = paper.variants;
    }
    const ref = { variants };
    const size = this._freeSizeFor(ref) || this.mediumSizeFor(ref);
    return (size && this.variantFor(ref, size, this._defaultFrame)) || null;
  }

  // Pre-sale value of the free print. Falls back exactly the way _freePrintValue() does, so an
  // unresolvable free print can never pick up a phantom markdown.
  _freePrintWasValue() {
    const v = this._freePrintVariant();
    if (v && v.price) return wasPriceOf(v);
    return this._cheapestWasPrice();
  }

  // The next pack tier the shopper hasn't reached yet (by paid-print count), used for the
  // "unlock X% off" upsell copy on the CTA + the build-bigger component. null once they're
  // already at the biggest pack (items G12/G13).
  _nextTierUp() {
    // Distinct paid prints = assigned slots NOT yet in the cart + prints already added via the
    // bundle. (A slot that's in the cart is also counted by _cartBundle, so exclude it here to
    // avoid double-counting, which would skip a tier — item G13.)
    const slotsNotInCart = this.state.paidSlots.filter((s) => s && s.assigned && !this._inCart(s)).length;
    const filled = slotsNotInCart + this._cartBundle().paidCount;
    for (const key of this._packTierKeys()) {
      const t = this.tiers[key];
      if (t && filled < t.paid) return t;
    }
    return null;
  }

  // Subtitle for an empty PAID slot — the next discount / free prints you'd unlock. Uses the
  // de-duplicated next-tier calc so it reflects real progress (a print counted in both a slot
  // and the cart no longer skips a tier — desktop feedback 1).
  _addArtworkSubtitle() {
    const t = this._nextTierUp();
    if (!t) return 'Add artwork'; // already at the biggest discount
    const d = Number(t.discount) || 0;
    // Zero-discount offer (sale mode): reference only the free prints — never "unlock 0% off".
    if (d === 0) {
      if (t.free > 0) return this._zeroCopy('zeroSlotSubtitle', 'Add artwork to unlock your {count} free prints').replace('{count}', t.free);
      return 'Add artwork';
    }
    let msg = 'Add artwork to unlock ' + d + '% off';
    if (t.free > 0) msg += ' + ' + t.free + ' free print' + (t.free > 1 ? 's' : '');
    return msg;
  }

  // Merchant-editable zero-discount wording (Theme settings → the block's copy JSON),
  // with a hard fallback so a blanked setting can't render an empty label.
  _zeroCopy(key, fallback) {
    const v = this._copy && this._copy[key];
    return typeof v === 'string' && v.trim() ? v : fallback;
  }

  // Canonical PAID count = in-progress paid slots (not already in the cart) + paid cart lines.
  // Counting this way avoids double-counting a print that sits in BOTH a state slot and the cart,
  // while still including prints added via single-PDP / Quick-ATC that live only in the cart.
  _paidFilledCount() {
    return this.state.paidSlots.filter((s) => s && s.assigned && !this._inCart(s)).length + this._cartBundle().paidCount;
  }
  _freeFilledCount() {
    return this.state.freeSlots.filter((s) => s && s.assigned && !this._inCart(s)).length + this._cartBundle().freeCount;
  }
  // How many FREE prints are earned right now: none below the entry threshold of 2 paid, then one
  // free per additional paid, capped at the tier's free count. This is the hard ceiling on free
  // prints — you can never have more free than earned (invariant enforced by _enforceFreeCap).
  _freeEarnedCount() {
    const tier = this._tier();
    const pf = this._paidFilledCount();
    return pf < 2 ? 0 : Math.min(pf, tier.free);
  }

  // The highest tier discount the filled PAID prints have actually earned — regardless of which pack
  // tab is selected. e.g. 2 paid on the 3-pack view has unlocked the 2-pack's 20% (not yet the 22%),
  // so the CTA quotes 20% (feedback). 0% below the entry threshold.
  _unlockedDiscountPct() {
    const paid = this._paidFilledCount();
    let pct = 0;
    Object.keys(this.tiers).forEach((k) => {
      const t = this.tiers[k];
      const need = Number(t.paid) || 0;
      const d = Number(t.discount) || 0;
      if (need <= paid && d > pct) pct = d;
    });
    return pct;
  }

  // Is a free-print slot still available to fill? True while fewer free prints are filled than
  // are currently earned by the filled paid prints (feedback: never more free than paid earns).
  _freeSlotOpen() {
    const tier = this._tier();
    if (!tier || tier.free <= 0) return false;
    return this._freeFilledCount() < this._freeEarnedCount();
  }

  // Enforce the invariant "filled free prints ≤ free earned by paid prints". If a change (removing a
  // paid print from the cart, resizing, etc.) leaves MORE free prints than are earned, pull the
  // extra IN-PROGRESS free prints up into paid slots (keeping their size/frame) until the pack is
  // valid again. In-cart free lines can't be silently moved, so they're left as-is. Idempotent: a
  // no-op when the pack already satisfies the invariant.
  _enforceFreeCap() {
    const tier = this._tier();
    if (!tier || tier.free <= 0) return;
    let guard = 0;
    while (guard++ < 12) {
      if (this._freeFilledCount() <= this._freeEarnedCount()) break;
      // Which in-progress (not-in-cart) free print to promote to paid. Prefer one that is NOT the
      // product's default (smallest) free size — e.g. the bigger 4:5 size — so the smaller size keeps
      // the free slot when they compete (feedback). Otherwise take the last.
      const cands = [];
      for (let i = this.state.freeSlots.length - 1; i >= 0; i--) {
        const s = this.state.freeSlots[i];
        if (s && s.assigned && !this._inCart(s)) cands.push(i);
      }
      if (!cands.length) break; // only in-cart free prints remain — can't move committed cart lines
      let srcIdx = cands.find((i) => { const s = this.state.freeSlots[i]; return s.size !== this._freeSizeFor(this._effective(s) || s.product); });
      if (srcIdx === undefined) srcIdx = cands[0];
      // Find (or create) an empty paid slot to receive it.
      let dstIdx = this.state.paidSlots.findIndex((s) => !s || !s.assigned);
      if (dstIdx < 0) {
        if (this._isTopTier() || this.state.paidSlots.length < tier.paid) {
          dstIdx = this.state.paidSlots.length;
          this.state.paidSlots.push({ assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame });
        } else break;
      }
      const dst = this.state.paidSlots[dstIdx] || (this.state.paidSlots[dstIdx] = { assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame });
      const src = this.state.freeSlots[srcIdx];
      dst.assigned = true; dst.product = src.product; dst.size = src.size; dst.frame = src.frame; // keep size + frame
      src.assigned = false; src.product = null; src.frame = this._defaultFrame;
    }
  }

  // Can this slot's print occupy a FREE slot? The same three rules the pack has always applied,
  // pulled into one predicate so the ranking below and _convertPaidToFreeIfEligible can't drift:
  //   1. the product qualifies at all — never a canvas, and it must offer a locked free size;
  //   2. the size currently chosen IS one of those locked free sizes (recs_free_size_keyword);
  //   3. framing gate — a framed print is free only while the framing service is REMOVED and the
  //      print offers that service (it then ships as a free unframed print + a separate paid frame).
  // Judged on the EFFECTIVE product, so a paid slot currently showing its canvas sibling is
  // excluded: canvas is never given away, which is also why free slots resolve to paper in
  // _effective(). Prices therefore never cross the paper/canvas boundary in the rank below.
  _canTakeFreeSlot(slot) {
    if (!slot || !slot.assigned || !slot.product) return false;
    if (!this._productFreeEligible(this._effective(slot) || slot.product)) return false;
    if (!this._isFreeSizeAnyRatio(slot.size || '')) return false;
    const framed = slot.frame && !/unframed/i.test(slot.frame);
    if (framed && (!this.state.removeFraming || !this._framingSvcPrint(slot))) return false;
    return true;
  }

  // Put the CHEAPEST eligible prints in the free slots, because that is what the checkout discount
  // does: it zeroes the cheapest eligible ("medium"-size) lines in the cart, NOT the ones the
  // shopper happened to drop into the free column. Without this the pack advertises one saving and
  // the cart delivers another — two £40 prints sitting in the free slots while two marked-down
  // £19.20 prints sit in the paid slots promised £80 off and actually gave £38.40. The same gap
  // opens on ratios whose medium size is under the usual £40 (e.g. an 11x14" at £25 in a paid slot).
  //
  // Pure PERMUTATION: it only ever swaps the CONTENTS of two already-filled slots, so the paid/free
  // counts are untouched and _enforceFreeCap / _freeEarnedCount / tier bumping keep behaving exactly
  // as before. In-cart slots are committed cart lines, so they're left alone.
  // The cart line that represents an in-cart slot, so the rank (which is keyed by cart line for
  // anything already committed) can be applied back to that slot.
  _lineForSlot(slot) {
    if (!slot || !slot.assigned || !slot.product) return null;
    const eff = this._effective(slot);
    const ids = [String(slot.product.id), eff && String(eff.id)].filter(Boolean);
    const lines = this._cartPrintLines();
    const exact = lines.find((l) => {
      if (ids.indexOf(String(l.product_id)) < 0) return false;
      const [ls, lf] = this._parseCartVariant(l);
      return ls === slot.size && lf === slot.frame;
    });
    return exact || lines.find((l) => ids.indexOf(String(l.product_id)) > -1) || null;
  }

  // Does the rank put THIS slot's print in the free column? A slot still being edited is ranked as
  // itself; one already in the cart is ranked as its cart line (_packPrints represents it that way,
  // so the print is never counted twice).
  _slotRanksFree(slot, freeSet) {
    if (!slot || !slot.assigned) return false;
    if (!this._inCart(slot)) return freeSet.has(slot);
    const l = this._lineForSlot(slot);
    return !!l && freeSet.has(l);
  }

  // Reconcile BOTH slot arrays with the price rank, so array position always means what the rank
  // says. Previously only cart lines followed the rank while slots followed their array position,
  // and the two could disagree: with the free positions held by in-cart prints, a cheaper
  // in-progress print could be ranked free yet keep rendering and pricing as PAID — it consumed a
  // free entitlement without ever showing as free, so the pack quoted one thing and charged another.
  //
  // Works on a snapshot of the assigned prints, then writes both arrays back in place. Snapshotting
  // first matters: the rank Set is keyed by slot OBJECT, so mutating slots while querying it would
  // re-read membership against contents that had already moved.
  _rebalanceFreeSlots() {
    const tier = this._tier();
    if (!tier || !(Number(tier.free) > 0)) return false;
    const freeSet = this._rankPackFree();

    // Free array first so that, on a price tie, a print already sitting in a free slot keeps it —
    // no reshuffling unless something is genuinely cheaper.
    const items = [];
    const take = (arr) => (arr || []).forEach((s) => {
      if (s && s.assigned && s.product) items.push({ product: s.product, size: s.size, frame: s.frame, free: this._slotRanksFree(s, freeSet) });
    });
    take(this.state.freeSlots);
    take(this.state.paidSlots);

    const apply = (arr, list, minLen) => {
      let moved = false;
      const len = Math.max(minLen, list.length, arr.length);
      while (arr.length < len) arr.push({ assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame });
      for (let i = 0; i < arr.length; i++) {
        // A slot array can legitimately carry gaps — a restored bundle, or an array grown by index
        // assignment. The snapshot above skips them; this loop must too, or it throws and takes the
        // whole caller down with it. Inside _loadCart that meant _renderSlots() never ran, so the
        // pack stayed drawn from its pre-cart state and the "In cart" badges never appeared.
        let s = arr[i];
        if (!s) { s = arr[i] = { assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame }; moved = true; }
        const it = list[i] || null;
        const product = it ? it.product : null;
        const size = it ? it.size : this._defaultSize;
        // A framed print keeps its frame (it ships as a separate paid child line); anything
        // unframed normalises to the default frame, as _convertPaidToFreeIfEligible has always done.
        const frame = it && it.frame && !/unframed/i.test(it.frame) ? it.frame : this._defaultFrame;
        if (s.assigned !== !!it || s.product !== product || s.size !== size || s.frame !== frame) moved = true;
        s.assigned = !!it; s.product = product; s.size = size; s.frame = frame;
      }
      return moved;
    };

    const toFree = items.filter((i) => i.free);
    const toPaid = items.filter((i) => !i.free);
    const a = apply(this.state.freeSlots, toFree, this._maxFree || tier.free || 0);
    const b = apply(this.state.paidSlots, toPaid, MAX_PAID);
    return a || b;
  }

  // Reverse of _moveFreeToPaidIfIneligible: when a PAID (not-in-cart) print is changed to the
  // locked free size and a free slot is open, silently move it back into the free slot, keeping
  // its size (feedback #17). A framed print only qualifies while the framing service is REMOVED
  // (the cart then ships the free unframed print + the separate paid frame) — and it keeps its
  // frame; with the service kept, a framed print stays paid (never silently drop a chosen frame).
  _convertPaidToFreeIfEligible(index) {
    const slot = this.state.paidSlots[index];
    if (!slot || !slot.assigned || this._inCart(slot)) return false;
    // A free print ships as the PAPER product, so eligibility is judged on the paper side —
    // same resolution _effective applies to free slots. A canvas-only print (no paper
    // sibling) or a product with no locked free size can never move into a free slot.
    const h = (slot.product && slot.product.handle) || '';
    const printSide = /-canvas$/i.test(h) ? this._paperCache[h.replace(/-canvas$/i, '')] : slot.product;
    if (!this._productFreeEligible(printSide)) return false;
    if (!this._isFreeSize(this._effective(slot) || slot.product, slot.size || '')) return false;
    const framed = slot.frame && !/unframed/i.test(slot.frame);
    // A framed print may take a free slot as long as it offers the framing service: the print is
    // the free item and its frame ships as a separate PAID child line. A framed print without one
    // has no such split, so it stays paid.
    if (framed && (!this.state.removeFraming || !this._framingSvcPrint(slot))) return false;
    if (!this._freeSlotOpen()) return false;
    const fi = this.state.freeSlots.findIndex((s) => s && !s.assigned);
    if (fi < 0) return false;
    const fslot = this.state.freeSlots[fi];
    fslot.assigned = true; fslot.product = slot.product; fslot.size = slot.size; fslot.frame = framed ? slot.frame : this._defaultFrame;
    slot.assigned = false; slot.product = this._currentProductRef(); slot.size = this._defaultSize; slot.frame = this._defaultFrame;
    this._compactSlots();
    return true;
  }

  // Is this slot's print already in the cart? Checks both the stored product and its
  // effective (paper/canvas) sibling id, since either may be the one that was added.
  _inCart(slot) {
    if (!slot || !slot.assigned || !slot.product) return false;
    const ids = [slot.product.id, this._effective(slot) && this._effective(slot).id];
    return ids.some((id) => id != null && this._cartProductIds.has(String(id)));
  }

  _readJSON(selector, root = this) {
    const el = root.querySelector ? root.querySelector(selector) : document.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Parse the theme's global #mrk-frames-data <template> (mrk-frames-data.liquid) into the same
  // shape as the block's frames JSON: { frameId, color, alt, type, ratio, lr, tb, image }. The
  // template must carry the frame-id attribute (the metaobject system id) — without it the list
  // can't drive the cart's _frame_id, so return null and fall back to the block JSON.
  _readFramesTemplate() {
    const tpl = document.getElementById('mrk-frames-data');
    if (!tpl || !tpl.content) return null;
    const imgs = [...tpl.content.querySelectorAll('img.mrk-frame-image-item')];
    if (!imgs.length || !imgs.some((img) => img.getAttribute('frame-id'))) return null;
    return imgs.map((img) => ({
      frameId: img.getAttribute('frame-id'),
      color: (img.getAttribute('color') || '').toLowerCase(),
      alt: (img.getAttribute('alt-name') || '').toLowerCase(),
      type: img.getAttribute('type') || '',
      ratio: parseFloat(img.getAttribute('ratio')) || 0,
      lr: parseFloat(img.getAttribute('left_right')) || 0,
      tb: parseFloat(img.getAttribute('top_bottom')) || 0,
      image: img.getAttribute('src') || '',
    }));
  }

  _currentProductRef() {
    return { id: this.product.id, handle: this.product.handle, title: this.product.title, vendor: this.product.vendor, type: this.product.type, image: this.product.image, artImage: this.product.artImage, ratio: this.product.ratio, framingService: !!this.product.framingService, variants: this.variants };
  }

  _tier() { return this.tiers[this.state.tier] || { paid: 1, free: 0, discount: 0 }; }

  // Is the given tier (default: the selected one) the OPEN-ENDED top tier? That tier renders
  // extra paid slots beyond its base count, isn't trimmed, and disables "add slot"/bump.
  _isTopTier(key) { return (key || this.state.tier) === this.tierOrder[this.tierOrder.length - 1]; }

  // The pack an entry-mode product ("Build your Bundle") opens on: the smallest pack still
  // enabled, since Single is not offered there.
  _entryTier() { return this.tierOrder.find((k) => k !== 'single') || 'single'; }

  // How this pack is written in copy ("2 pack", "10+ pack"). Derived from the tier's own paid
  // count so a new pack needs no map here; only the open-ended top tier gets the "+".
  _packNum() { return String(this._tier().paid) + (this._isTopTier() ? '+' : ''); }

  // Every bundle tier in order, excluding `single` — the pack ladder used for upsell copy.
  _packTierKeys() { return this.tierOrder.filter((k) => k !== 'single'); }

  // Number of PAID slots to render/price. Fixed at the tier's paid count for the smaller
  // packs; only the open-ended top tier ("5+ PACK") grows to hold extra prints (item 4).
  _paidCount() {
    const tier = this._tier();
    if (!this._isTopTier()) return tier.paid;
    // Open-ended top tier: render every paid slot in the array (incl. an extra empty one
    // appended by "Build Bigger Pack"), so it can grow beyond the base count.
    return Math.max(tier.paid, this.state.paidSlots.length);
  }

  // Move prints beyond the current pack's capacity into an overflow stash (fixed-size
  // packs only; the "4+" top tier is open-ended). Non-destructive: they flow back in via
  // _expandFromOverflow when the pack grows again.
  _trimToTier() {
    const tier = this._tier();
    if (!this._isTopTier()) {
      let kept = 0;
      this.state.paidSlots.forEach((s) => {
        if (s && s.assigned) {
          kept++;
          if (kept > tier.paid) { this._overflow.push({ product: s.product, size: s.size, frame: s.frame }); s.assigned = false; s.product = null; }
        }
      });
      this._compactSlots();
    }
    for (let i = tier.free; i < this.state.freeSlots.length; i++) {
      const s = this.state.freeSlots[i];
      if (s && s.assigned) { this._overflow.push({ product: s.product, size: s.size, frame: s.frame }); s.assigned = false; s.product = null; s.frame = this._defaultFrame; }
    }
  }

  // Refill empty slots (paid then free) from the overflow stash when the pack grows.
  _expandFromOverflow() {
    while (this._overflow.length) {
      const target = this._firstEmptyPaid() || this._firstEmptyFree();
      if (!target) break; // no spare capacity — leave the rest stashed
      // A free slot may only receive a free-eligible print (paper + has a locked free size);
      // pick the first qualifying stashed item, or leave the rest stashed if none qualify.
      let takeIdx = 0;
      if (target.kind === 'free') {
        takeIdx = this._overflow.findIndex((it) => this._productFreeEligible(it.product));
        if (takeIdx < 0) break;
      }
      const item = this._overflow.splice(takeIdx, 1)[0];
      if (target.kind === 'paid') {
        const slot = this.state.paidSlots[target.index];
        slot.assigned = true; slot.product = item.product;
        slot.size = item.size || this._smallestSizeFor(item.product);
        slot.frame = item.frame || this._defaultFrame;
      } else {
        const slot = this.state.freeSlots[target.index];
        slot.assigned = true; slot.product = item.product;
        slot.size = this._freeSizeFor(item.product);
        slot.frame = this._defaultFrame;
      }
    }
  }

  // The pack discount only applies once the pack's paid count is reached.
  _discountUnlocked() {
    const tier = this._tier();
    const pct = Number(tier.discount) || 0;
    const paidAssigned = this.state.paidSlots.filter((s) => s && s.assigned).length;
    return pct > 0 && paidAssigned >= tier.paid;
  }

  variantFor(product, size, frame) {
    const vs = (product && product.variants) || [];
    return vs.find((v) => v.option1 === size && v.option2 === frame) || vs.find((v) => v.option1 === size) || vs[0] || null;
  }

  // Does a real variant exist for this exact size + frame combination? Some sizes offer no
  // frames at all (e.g. A0 ships unframed only), and variantFor's size-only fallback would
  // silently price such a combo as unframed — callers use this to block/reset it instead.
  _comboExists(product, size, frame) {
    return (((product && product.variants) || [])).some((v) => v.option1 === size && v.option2 === frame);
  }

  // If the slot's chosen frame has no variant at the slot's size (e.g. the size just changed
  // to A0, which offers no frames), drop it back to Unframed — mirroring the PDP, where the
  // native picker resolves to the real variant and the frame swatches render crossed out.
  // Only when the frame is genuinely one of the product's option2 values: child-frame
  // products carry a bare colour word ("Black") that never matches option2 and must not reset.
  _dropFrameIfComboMissing(slot) {
    if (!slot || !slot.assigned) return;
    const eff = this._effective(slot) || slot.product;
    const frame = slot.frame;
    if (!frame || /unframed/i.test(frame)) return;
    const vs = (eff && eff.variants) || [];
    if (!vs.some((v) => v.option2 === frame)) return;
    if (!this._comboExists(eff, slot.size, frame)) slot.frame = this._defaultFrame;
  }

  mediumSizeFor(product) {
    const sizes = [...new Set(((product && product.variants) || []).map((v) => v.option1).filter(Boolean))];
    return sizes.find((s) => /a3/i.test(s)) || sizes[Math.floor(sizes.length / 2)] || sizes[0] || null;
  }

  // The smallest offered size — used as the default for PAID prints. Handles mixed
  // A-series + cm option sets by estimating each size's area (cm²) and taking the min.
  _smallestSizeFor(product) {
    const sizes = [...new Set(((product && product.variants) || []).map((v) => v.option1).filter(Boolean))];
    if (!sizes.length) return null;
    const A_AREA = { 0: 9999, 1: 4996, 2: 2495, 3: 1247, 4: 624, 5: 311, 6: 155 }; // cm²
    const area = (s) => {
      // DROOL sizes read like `8.3x11.7" (A4)` — the inch prefix must NOT be measured;
      // evaluate the real spec inside the parentheses (an A-code or a cm dimension).
      const paren = /\(([^)]+)\)/.exec(s);
      const t = paren ? paren[1] : s;
      const am = /a\s*(\d)\b/i.exec(t);
      if (am && A_AREA[am[1]] != null) return A_AREA[am[1]];
      const dm = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i.exec(t);
      if (dm) return parseFloat(dm[1]) * parseFloat(dm[2]);
      return Number.MAX_SAFE_INTEGER;
    };
    return [...sizes].sort((a, b) => area(a) - area(b))[0];
  }

  // Free ("medium") print sizes come from the SAME merchant setting the cart recommendations
  // use (settings.recs_free_size_keyword, injected as data-free-sizes): comma-separated
  // keywords in priority order, e.g. "A3, 40x40cm, 30x45cm, 30x40cm, 28x35.5cm". A size
  // qualifies as free iff its name CONTAINS a keyword after the same normalisation
  // custom-recs-product.liquid applies (lowercase; strip whitespace/quotes/primes/parens;
  // × → x). Keyword-anchored matching — not ratio regexes — so an inch spec can't collide
  // with a cm keyword: 30x40" (75x100cm) must NOT match the 30x40cm free size.
  _normSize(s) {
    return String(s || '').toLowerCase().replace(/[\s"”“′″()]/g, '').replace(/×/g, 'x');
  }
  _freeKeywords() {
    if (this._freeKw) return this._freeKw;
    // The merchant setting is the sole source when it has a value; the built-in list is a
    // FALLBACK for an empty setting only — never appended, or retired sizes would stay free
    // forever. Same list + rule as custom-recs-product.liquid.
    const FALLBACK = '12x12" (30x30cm), 11x14" (28x35.5cm), 12x15" (30x38cm), 12x16" (30x40cm), 11.7x16.5" (A3), 12x18" (30x45cm)';
    const src = (this.dataset.freeSizes || '').trim() || FALLBACK;
    const seen = new Set();
    this._freeKw = src.split(',').map((k) => this._normSize(k)).filter((k) => {
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    return this._freeKw;
  }

  // A size matches a keyword when either normalised string contains the other, so the setting
  // accepts EITHER format for the same size: "A3" and "11.7x16.5\" (A3)" both match the A3
  // variant, as do "30x40cm" and "12x16\" (30x40cm)". Still anchored enough that an inch spec
  // can't collide with a cm keyword — 30x40" (75x100cm) matches neither direction.
  _sizeMatchesKeyword(size, kw) {
    const n = this._normSize(size);
    if (!n || !kw) return false;
    return n.indexOf(kw) > -1 || kw.indexOf(n) > -1;
  }
  // The product's actual size options that qualify as free prints, in keyword priority order.
  _freeSizes(product) {
    const sizes = [...new Set(((product && product.variants) || []).map((v) => v.option1).filter(Boolean))];
    const matches = [];
    this._freeKeywords().forEach((kw) => {
      const s = sizes.find((sz) => this._sizeMatchesKeyword(sz, kw));
      if (s && !matches.includes(s)) matches.push(s);
    });
    return matches;
  }
  // Is `size` an eligible free-print size? (`product` kept for call-site compatibility.)
  _isFreeSize(product, size) {
    return this._isFreeSizeAnyRatio(size);
  }
  // Same keyword check for cart lines whose product we don't have to hand (e.g. a print
  // added from the cart recommendations) — the name predates the keyword matching.
  _isFreeSizeAnyRatio(size) {
    if (!size) return false;
    return this._freeKeywords().some((kw) => this._sizeMatchesKeyword(size, kw));
  }
  _freeSizeFor(product) {
    const sizes = [...new Set(((product && product.variants) || []).map((v) => v.option1).filter(Boolean))];
    if (!sizes.length) return null;
    return this._freeSizes(product)[0] || this.mediumSizeFor(product) || sizes[0]; // locked/default
  }
  // Can this PRODUCT ever occupy a FREE slot? Free prints are always art-paper PRINTS: a
  // canvas product (type or -canvas handle) never qualifies, and neither does a product with
  // no size matching the locked free-size keywords — without this gate, _freeSizeFor's
  // medium/smallest fallback would seat a paid-only size in a free slot.
  _productFreeEligible(product) {
    if (!product) return false;
    if (/canvas/i.test(product.type || '') || /-canvas$/i.test(product.handle || '')) return false;
    return this._freeSizes(product).length > 0;
  }

  // The product actually used for pricing / variants / cart — paper by default,
  // or the canvas sibling when Canvas material is selected (R: full canvas support).
  _effective(slot) {
    if (!slot || !slot.product) return null;
    // A committed cart line (synthetic slot from _cartLineSlot) is locked — its product IS the
    // truth of what's in the cart, so the material toggle must never swap it (e.g. a free PRINT
    // line displaying its canvas sibling's price after add-to-cart on Canvas).
    if (slot._fromCart) return slot.product;
    const h = slot.product.handle || '';
    const base = h.replace(/-canvas$/i, '');
    const slotIsCanvas = /-canvas$/i.test(h);
    // Free prints are ALWAYS art paper — the global canvas material never applies to them (item 6).
    const isFree = this.state.freeSlots && this.state.freeSlots.indexOf(slot) !== -1;
    const wantCanvas = !isFree && this._canvasMat && this.state.material === this._canvasMat.title;
    if (wantCanvas) {
      if (slotIsCanvas) return slot.product;            // slot already holds the canvas product
      return this._canvasCache[base] || slot.product;   // resolve canvas sibling (fall back to paper)
    }
    // Want paper: if the slot holds a canvas product (e.g. landed on a canvas PDP),
    // resolve its paper sibling; otherwise the stored product is already paper.
    if (!slotIsCanvas) return slot.product;
    return this._paperCache[base] || slot.product;
  }

  slotPrice(slot) {
    const v = this.variantFor(this._effective(slot), slot.size, slot.frame);
    return v ? v.price : 0;
  }

  // Pre-sale value of the same variant slotPrice() picks. Equals slotPrice() when the print
  // isn't on sale, so callers can sum it unconditionally.
  slotWasPrice(slot) {
    const v = this.variantFor(this._effective(slot), slot.size, slot.frame);
    return v ? wasPriceOf(v) : 0;
  }

  // Markdown % on the variant this slot has selected — DERIVED, never hardcoded. It is read per
  // variant rather than per product because the same sale is a different percentage on different
  // variants: on this store the markdown applies to the print and not the frame, so an unframed
  // print can be -20% while its framed sibling is -10%. Returns 0 when the slot isn't on sale.
  _slotSalePct(slot) {
    if (!slot || !slot.product) return 0;
    const v = this.variantFor(this._effective(slot) || slot.product, slot.size, slot.frame);
    return v ? markdownPct(v.price, wasPriceOf(v)) : 0;
  }

  // ---- Tier selection / bundle mode ----
  // Stamp the CURRENTLY displayed view into the carried view-mode key right before leaving the
  // page (swatch/browse navigation, quick view). The destination then mirrors what the shopper
  // actually saw — even when a bfcache-restored page holds storage that went stale meanwhile.
  _stampViewMode() {
    try { sessionStorage.setItem('drool_view_mode', this.state.tier); } catch (e) {}
  }

  selectTier(tier, { silent = false } = {}) {
    if (!this.tiers[tier]) tier = 'single';
    if (this.entryMode && tier === 'single') tier = this._entryTier(); // no Single on entry product
    this.state.tier = tier;
    // Remember the view (single vs bundle tier) so the next PDP opens the same way (items 8/9).
    // Only for REAL selections, never the silent init call: an init write poisons the carried
    // view without any user choice (e.g. the entry product forces duo on load, and every PDP
    // opened afterwards would start as a 2-pack).
    if (!silent) { try { sessionStorage.setItem('drool_view_mode', tier); } catch (e) {} }
    this._trimToTier();         // a fixed-size pack can't hold more prints than its capacity
    this._expandFromOverflow(); // …and a bigger pack pulls trimmed prints back in
    this.tierButtons.forEach((btn) => btn.setAttribute('aria-checked', btn.dataset.tier === tier ? 'true' : 'false'));
    this._revealTier(tier); // the tier row scrolls now (5+ pack) — keep the active one on screen

    const isBundle = tier !== 'single';
    document.body.classList.toggle('bb-bundle-active', isBundle);
    // Single view mirrors the bundle 55/45 split + grey right panel (single-view feedback 1/2).
    document.body.classList.toggle('bb-single-active', !isBundle);
    if (this.bundleEl) this.bundleEl.hidden = !isBundle;
    if (this.swatchesEl) this.swatchesEl.hidden = isBundle;
    this._toggleNativeMaterial(isBundle); // the bundle has its own Material selector
    this._toggleNativeUpsell(isBundle);   // hide the "Complete the look" upsell in pack mode
    this._toggleNativeArtist(isBundle);   // hide "About artist" — a pack isn't one artist (PDP feedback 1)
    this._swapTitle(isBundle); // PDP title → "Build your bundle" in pack mode

    if (isBundle) {
      this._renderSlots();
      this._updateAddSlotBtn();
      this._ensureSelector();
      this._renderMaterials();
      this._updateSelectorCounter();
      this._markCards();
    } else {
      this._ensureSwatches();
      this._ensureSingleCard(); // group Material + Size/Frame into one white card (single-view feedback 3)
      this._bindSingleCardRebuild(); // …and put it back after every variant change (bound once)
      // The single-view buy box is display:none while a pack is active, so on reveal its layout is
      // stale until a reflow (the shopper scrolling): the sticky ATC + native price render full width
      // and the frame-swatch slider collapses/overflows its card. Fix on reveal:
      //  (1) force a synchronous layout reflow — the sticky ATC + price are pure CSS layout with no JS
      //      listener, so only a real reflow (reading offsetHeight) corrects their width, not a resize event;
      //  (2) dispatch resize so the frame-swatch slider (which DOES re-measure on resize) fixes itself.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        void document.documentElement.offsetHeight; // (1) flush layout
        window.dispatchEvent(new Event('resize'));  // (2) nudge the swatch slider's resize listener
      }));
    }
    this.recalculate();
    if (!silent) this._persistSave();
    // A user-initiated tier change (e.g. single → 2-pack) swaps in a taller pack UI; anchor the
    // shopper to the top of the pack-size ("quantity") selector on BOTH mobile and desktop so they
    // don't land at a weird scroll position (item 25 / G1). Only when the shopper actually clicked a
    // tier — not on programmatic switches (_bumpTier while adding a print).
    if (!silent && isBundle && this._userTierChange) this._scrollToPackTop();
    this._userTierChange = false;
    if (!silent) this.dispatchEvent(new CustomEvent('bundle:tierchange', { bubbles: true, detail: { tier, ...this._tier() } }));
  }

  // Scroll so the "1 · Choose Your Pack Size" heading is fully in view below the sticky header.
  // Runs on both mobile and desktop (item 25 / G1). Uses the heading's own top minus the measured header
  // height, run after two frames so the tier-swap re-render has settled the layout first.
  _scrollToPackTop() {
    const target = this.querySelector('.bundle-builder__section-heading') || this;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const header = document.querySelector('.header-wrapper, .section-header, header[role="banner"], .shopify-section-header, .header, header');
      const offset = (header ? header.getBoundingClientRect().height : 0) + 16;
      const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }));
  }

  _bumpTier() {
    const idx = this.tierOrder.indexOf(this.state.tier);
    if (idx > -1 && idx < this.tierOrder.length - 1) { this.selectTier(this.tierOrder[idx + 1]); return true; }
    return false;
  }

  _updateAddSlotBtn() { if (this.addSlotBtn) this.addSlotBtn.disabled = this._isTopTier(); }

  // Swap the PDP product title to "Build your bundle" when a pack is selected
  // (restores the original in Single mode). Skipped on the entry product.
  // Hide the "About artist" accordion in pack mode — a bundle isn't a single artist (PDP feedback 1).
  _toggleNativeArtist(isBundle) {
    if (this._artistAccordion === undefined) {
      const info = this.closest('.product-information') || document.querySelector('.product-information');
      let el = null;
      if (info) {
        const summary = [...info.querySelectorAll('summary, .details__header, .accordion__title')]
          .find((s) => /about (the )?artist/i.test((s.textContent || '').trim()));
        // Hide ONLY the single "About artist" <details> — NOT its .accordion wrapper, which also
        // holds sibling rows like "Delivery & returns" (feedback 1).
        el = summary ? (summary.closest('details') || summary.parentElement) : null;
      }
      this._artistAccordion = el;
    }
    if (this._artistAccordion) this._artistAccordion.style.display = isBundle ? 'none' : '';
  }

  // Single view: wrap the native Material/Size (.wd-native-picker) + Frame (frame-variant-picker)
  // into one white card (Figma 376-5859) and move the FBT swatches to sit just below it, OUTSIDE
  // the card (single-view feedback 3/4). Elements are MOVED, not rebuilt, so their cart wiring is
  // preserved. The card is hidden by CSS in bundle mode.
  //
  // RE-ENTRANT on purpose. Every ingredient here belongs to the THEME, not to us: a variant change
  // re-renders the native pickers from server HTML that knows nothing about this card, so the
  // numbered headings and the size-chart "?" are wiped back to plain "Material / Size / Frame",
  // and a morph can lift a picker back out of the card entirely. This used to latch on
  // _singleCardBuilt and return, so nothing was ever repaired and the single view stayed reset
  // until a reload. _bindSingleCardRebuild re-runs it after each variant change; every step below
  // is written to repair in place rather than duplicate.
  _ensureSingleCard() {
    // Wrap the native option pickers into the card: .wd-native-picker (Material/Size) and,
    // where the template has one, <frame-variant-picker>. This works on the DEFAULT product
    // template too, which has NO frame-variant-picker (feedback #23 — the non-A-ratio single
    // view was missing the card).
    const pickers = [...document.querySelectorAll('.wd-native-picker, frame-variant-picker')];
    if (!pickers.length) return;
    let card = document.querySelector('.bundle-builder__single-card');
    const fresh = !card;
    if (fresh) {
      card = document.createElement('div');
      card.className = 'bundle-builder__single-card';
      pickers[0].parentElement.insertBefore(card, pickers[0]);
    }
    // Move in only the arms that are NOT already inside the card, each one placed before the next
    // arm that is — which keeps document order without touching the ones already in place.
    // Deliberately not a blanket re-append: <frame-variant-picker> is a custom element, so moving
    // it fires disconnected/connectedCallback and would re-init a picker that was perfectly fine.
    pickers.forEach((p, i) => {
      if (p.parentElement === card) return;
      const nextInCard = pickers.slice(i + 1).find((n) => n.parentElement === card);
      card.insertBefore(p, nextInCard || null);
    });
    // FBT swatches sit directly ABOVE the card (outside it), kept untouched otherwise. Only on the
    // first build: afterwards they are already positioned, and re-inserting would move them below
    // a card that has since been re-parented.
    if (fresh && this.swatchesEl) card.parentElement.insertBefore(this.swatchesEl, card);
    this._numberCardHeadings(card); // numbered "② Choose Your Material" style titles (feedback 2)
    // "Size chart" gets a "?" trigger next to it that opens the same size-chart modal (feedback 3).
    const scBtn = [...card.querySelectorAll('.popup-link__button')].find((b) => /size chart/i.test(b.textContent || ''));
    // The dataset flag rides on the button, so a re-rendered button correctly reads as un-tipped.
    // The sibling check is the backstop for a render that keeps the button but drops our marker —
    // without it that case would add a second "?" on every variant change.
    const tipped = scBtn && (scBtn.dataset.bbTip
      || (scBtn.nextElementSibling && scBtn.nextElementSibling.classList.contains('bundle-builder__sizechart-tip')));
    if (scBtn && !tipped) {
      scBtn.dataset.bbTip = '1';
      const q = document.createElement('button');
      q.type = 'button';
      q.className = 'bundle-builder__sizechart-tip';
      q.setAttribute('aria-label', 'Open size chart');
      q.innerHTML = ICON_QMARK;
      q.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); scBtn.click(); });
      scBtn.insertAdjacentElement('afterend', q);
    }
  }

  // Keep the single-view card alive across variant changes. The card is assembled out of
  // theme-owned DOM (see _ensureSingleCard), and both the theme's morph and mrk.js's ?view=frame
  // refetch rewrite that DOM from server HTML with no knowledge of the card — which reset the
  // single view to the default picker layout until the next reload.
  _bindSingleCardRebuild() {
    if (this._singleCardRebuildBound) return;
    this._singleCardRebuildBound = true;
    const rebuild = () => {
      // Pack mode has its own Material/Size selectors and hides the card, so there is nothing to
      // repair — and rebuilding there would fight _toggleNativeMaterial.
      if (this.state.tier !== 'single') return;
      // The re-rendered picker lands after the event, so wait two frames — same reason and same
      // pattern as _qvBindFreeRezero.
      requestAnimationFrame(() => requestAnimationFrame(() => this._ensureSingleCard()));
    };
    document.addEventListener('variant:update', rebuild);
    document.addEventListener('mrkVariantChanged', rebuild);
  }

  // Turn the native Material/Size/Frame <legend> labels into numbered section headings that match
  // the bundle view (circled number + "Choose Your …"). Modifies ONLY the label text + prepends a
  // number span, leaving the native controls (and any selected-value spans) intact (feedback 2).
  _numberCardHeadings(card) {
    const setHeading = (legend, num, title) => {
      if (!legend || legend.dataset.bbNumbered) return;
      legend.dataset.bbNumbered = '1';
      legend.classList.add('bundle-builder__card-heading');
      const textNode = [...legend.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
      if (textNode) textNode.textContent = title;
      else legend.insertBefore(document.createTextNode(title), legend.firstChild);
      const numEl = document.createElement('span');
      numEl.className = 'bundle-builder__section-num';
      numEl.textContent = String(num);
      legend.insertBefore(numEl, legend.firstChild);
    };
    // Number EVERY matching heading, not just the first. The card holds .wd-native-picker plus a
    // <frame-variant-picker>, so several headings need numbering (feedback #23: single view still
    // showed plain "Material/Size/Frame" when only the first was numbered). Include the picker-header
    // divs so it works on BOTH product templates (default template uses .mrk-variant-picker-header).
    const all = [...card.querySelectorAll('legend, .mrk-variant-picker-header, .single-option-radio__label')];
    // Number only the INNERMOST heading per label — if a picker-header div wraps a <legend>,
    // numbering both would double the text ("3Choose Your Size 3Choose Your Size"). Keeping just the
    // leaf also covers the DEFAULT template, whose header may be the div itself with no legend.
    const heads = all.filter((h) => !all.some((o) => o !== h && h.contains(o)));
    // Numbers are assigned sequentially to the categories that actually exist (pack size is 1) —
    // a product with no Material selector must read 2 Size / 3 Frame, not 3/4.
    let num = 2;
    [
      { re: /^\s*material/i, title: 'Choose Your Material' },
      { re: /^\s*size/i, title: 'Choose Your Size' },
      { re: /^\s*frame/i, title: 'Choose Your Frame' },
    ].forEach((cat) => {
      const matches = heads.filter((l) => cat.re.test(l.textContent));
      if (!matches.length) return;
      matches.forEach((l) => setHeading(l, num, cat.title));
      num++;
    });
  }

  _swapTitle(isBundle) {
    const info = this.closest('.product-information') || document.querySelector('.product-information');
    // Hide the product artist byline ("By <artist>") in pack mode (all products, incl. entry).
    if (this._bylineEl === undefined) {
      this._bylineEl = info ? ([...info.querySelectorAll('.group-block-content > *, p, div, a, span')].find((e) =>
        e.offsetParent !== null && /^\s*by\b/i.test(e.textContent || '') && (e.textContent || '').trim().length < 40
        && !e.closest('.bundle-builder') && !e.closest('.bundle-print-selector')) || null) : null;
    }
    if (this._bylineEl) this._bylineEl.style.display = isBundle ? 'none' : '';
    // Title swap is skipped on the entry product (already "Build Your Bundle").
    if (this.entryMode) return;
    if (this._titleEl === undefined) {
      this._titleEl = (info && (info.querySelector('h1') || info.querySelector('.product-title')))
        || document.querySelector('.product-information h1, h1.product-title, .product-title') || null;
    }
    if (!this._titleEl) return;
    if (this._origTitle == null) this._origTitle = this._titleEl.textContent;
    this._titleEl.textContent = isBundle ? 'Build your bundle' : this._origTitle;
  }

  // ---- Slots ----
  _renderFrames() {
    if (!this.frameOptionsEl) return;
    this.frameOptionsEl.innerHTML = '';
    this.frames.forEach((frame) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-builder__frame-option';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', frame === this.state.frame ? 'true' : 'false');
      btn.textContent = frame;
      btn.addEventListener('click', () => { this.state.frame = frame; this._renderFrames(); this._renderSlots(); this.recalculate(); });
      this.frameOptionsEl.appendChild(btn);
    });
  }

  _renderSlots() {
    if (!this.slotsEl) return;
    this._closeAllTips(); // drop any open (portaled) tooltip before the DOM it belongs to is rebuilt
    const tier = this._tier();
    const pc = this._paidCount();
    // Guarantee the array holds every slot we're about to render (a smaller tier may have
    // shrunk it) — otherwise _buildPaidSlot hits an undefined slot and throws, which left
    // the "Adding…" button stuck and the extra slots missing until a refresh.
    while (this.state.paidSlots.length < pc) this.state.paidSlots.push({ assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame });
    this.slotsEl.innerHTML = '';

    // Unified render (feedback): ALL cart prints show as locked "In cart" slots (however added —
    // single PDP, Quick-ATC, or the bundle), then in-progress (not-in-cart) slots, then empties —
    // capped at tier capacity.
    const cb = this._cartBundle();
    const renderColumn = (stateSlots, lockedLines, otherLines, cap, build) => {
      // Render slots in their ORIGINAL order so adding a print to the cart doesn't reshuffle the
      // pack (feedback 4). Each in-cart bundle print is matched to its cart line and drawn IN PLACE
      // (as a locked "In cart" slot). Cart prints with no bundle slot (single-PDP / Quick-ATC adds)
      // have no position, so they render first; then the shopper's slots in order; then empties.
      const remainingLines = lockedLines.slice();
      // Match against BOTH the stored product id and its effective (paper/canvas) sibling id —
      // the cart line carries the EFFECTIVE variant's product (e.g. the canvas sibling), so
      // matching slot.product.id alone left the line unclaimed and the print rendered TWICE:
      // the in-cart state slot + an appended cart-line slot (canvas doubling bug).
      const findIdx = (list, slot) => {
        const eff = this._effective(slot);
        const ids = [slot.product && String(slot.product.id), eff && String(eff.id)].filter(Boolean);
        if (!ids.length) return -1;
        let idx = list.findIndex((o) => ids.indexOf(String(o.id)) > -1 && o.size === slot.size && o.frame === slot.frame);
        if (idx < 0) idx = list.findIndex((o) => ids.indexOf(String(o.id)) > -1);
        return idx;
      };
      const takeMatch = (slot) => {
        const idx = findIdx(remainingLines, slot);
        return idx < 0 ? null : remainingLines.splice(idx, 1)[0];
      };
      const ordered = [];
      const empties = [];
      stateSlots.forEach((s, i) => {
        if (s && s.assigned && this._inCart(s)) {
          const line = takeMatch(s);
          // No match in THIS column: if the print's cart line now ranks into the other column it
          // renders there, so drawing the state slot here as well would show it twice. Only fall
          // back to the state slot when the cart holds no line for it at all (e.g. a line the print
          // filter didn't classify) — otherwise the print would vanish from the pack entirely.
          if (line) ordered.push(build(this._cartLineSlot(line), -1)); // in place, locked
          else if (findIdx(otherLines, s) < 0) ordered.push(build(s, i));
        } else if (s && s.assigned) {
          ordered.push(build(s, i));
        } else {
          empties.push(i);
        }
      });
      remainingLines.forEach((o) => this.slotsEl.appendChild(build(this._cartLineSlot(o), -1))); // external cart adds first
      ordered.forEach((el) => this.slotsEl.appendChild(el));
      let remaining = Math.max(0, cap - (remainingLines.length + ordered.length));
      for (const i of empties) { if (remaining <= 0) break; this.slotsEl.appendChild(build(stateSlots[i], i)); remaining--; }
    };
    renderColumn(this.state.paidSlots, cb.paidLines, cb.freeLines, tier.paid, (s, i) => this._buildPaidSlot(s, i));
    renderColumn(this.state.freeSlots, cb.freeLines, cb.paidLines, tier.free, (s, i) => this._buildFreeSlot(s, i));

    // When the pack is full, offer to build a bigger one (Figma 257:9570). For fixed packs
    // this bumps to the next tier; the open-ended "4+" tier just appends another paid slot.
    const canBump = this.tierOrder.indexOf(this.state.tier) < this.tierOrder.length - 1;
    if (!this._firstEmptyPaid() && !this._firstEmptyFree()) {
      const bigger = document.createElement('button');
      bigger.type = 'button';
      bigger.className = 'bundle-builder__build-bigger';
      // While a bigger pack is still available, sell the NEXT tier's unlock (item G12):
      // "Unlock Y% // Add 1 more print to unlock Y% off and 1 extra free print". Only at the
      // very top (4 paid + 4 free, no bump left) do we revert to the generic "Build Bigger Pack".
      const nextUp = this._nextTierUp();
      if (canBump && nextUp) {
        const y = Number(nextUp.discount) || 0;
        if (y === 0) {
          // Zero-discount offer (sale mode): sell the extra free print, never "0% off".
          // Merchant-editable text → textContent, never string-built HTML (R1).
          const zt = document.createElement('span');
          zt.className = 'bundle-builder__build-bigger-title';
          zt.textContent = this._zeroCopy('zeroBiggerTitle', 'Unlock 1 extra free print');
          const zs = document.createElement('span');
          zs.className = 'bundle-builder__build-bigger-sub';
          zs.textContent = this._zeroCopy('zeroBiggerSub', 'Add 1 more print to unlock 1 extra free print');
          bigger.append(zt, zs);
        } else {
          bigger.innerHTML =
            '<span class="bundle-builder__build-bigger-title">Unlock ' + y + '%</span>' +
            '<span class="bundle-builder__build-bigger-sub">Add 1 more print to unlock ' + y + '% off and 1 extra free print</span>';
        }
      } else {
        bigger.innerHTML =
          '<span class="bundle-builder__build-bigger-title">+ Build Bigger Pack</span>' +
          '<span class="bundle-builder__build-bigger-sub">Select more artwork</span>';
      }
      bigger.addEventListener('click', () => {
        if (canBump) { this._bumpTier(); return; }
        this.state.paidSlots.push({ assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame });
        this._afterSlotChange();
      });
      this.slotsEl.appendChild(bigger);
    }
  }

  _buildPaidSlot(slot, index) {
    if (!slot) slot = this.state.paidSlots[index] = { assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame };
    const row = document.createElement('div');
    row.className = 'bundle-builder__slot' + (slot.assigned ? '' : ' bundle-builder__slot--empty') + (slot.assigned && this._inCart(slot) ? ' bundle-builder__slot--incart' : '');
    row.setAttribute('role', 'listitem');

    // A print already in the cart is LOCKED in the bundle: the size/framing selectors are disabled
    // with a placeholder tooltip. The remove × IS shown — clicking it removes the print from the
    // bundle AND the cart via _removeCartGroup (feedback: delete in-cart prints from the bundle).
    const locked = slot.assigned && this._inCart(slot);
    // Media (thumb) with the remove × overlaid on the top-left (Figma).
    const media = document.createElement('div');
    media.className = 'bundle-builder__slot-media';
    media.appendChild(slot.assigned ? this._framedThumb(slot) : this._thumb(null));
    if (slot.assigned) {
      const x = document.createElement('button');
      x.type = 'button';
      x.className = 'bundle-builder__slot-remove';
      x.setAttribute('aria-label', 'Remove ' + (slot.product.title || 'print'));
      x.innerHTML = ICON_CLOSE;
      x.addEventListener('click', () => (slot._fromCart ? this._removeCartGroup(slot) : this._removeSlot(index)));
      media.appendChild(x);
    }
    row.appendChild(media);

    const main = document.createElement('div');
    main.className = 'bundle-builder__slot-main';

    if (!slot.assigned) {
      main.classList.add('bundle-builder__slot-main--empty');
      // Desktop (original design, node 91:996): "Empty Slot" + "Add artwork".
      const head = document.createElement('div');
      head.className = 'bundle-builder__slot-head';
      const titles = document.createElement('div');
      titles.className = 'bundle-builder__slot-titles';
      titles.appendChild(this._title('Empty Slot', true));
      titles.appendChild(this._artist(this._addArtworkSubtitle()));
      head.appendChild(titles);
      main.appendChild(head);
      // Mobile-only: a single centred "Add Artwork" link (CSS shows one per breakpoint).
      const add = document.createElement('span');
      add.className = 'bundle-builder__slot-add';
      add.textContent = 'Add Artwork';
      main.appendChild(add);
      row.appendChild(main);
      this._makeEmptyTappable(row, slot);
      return row;
    }

    // Row 1: title (left) + per-print price (right).
    const head = document.createElement('div');
    head.className = 'bundle-builder__slot-head';
    const titles = document.createElement('div');
    titles.className = 'bundle-builder__slot-titles';
    titles.appendChild(this._title(slot.product.title, false));
    head.appendChild(titles);
    head.appendChild(this._slotPriceEl(slot));
    main.appendChild(head);
    // Row 2: "By artist" (left) + right side = the "Discount applies to X pack" caveat
    // (item 5) then, when in the cart, the black "In cart" badge to its right (item 2).
    const artistLine = document.createElement('div');
    artistLine.className = 'bundle-builder__slot-artistline';
    artistLine.appendChild(this._artist(slot.product.vendor ? 'By ' + slot.product.vendor : ''));
    const rightMeta = document.createElement('div');
    rightMeta.className = 'bundle-builder__slot-artistline-meta';
    const note = this._discountNote(slot);
    if (note) rightMeta.appendChild(note);
    artistLine.appendChild(rightMeta);
    // Badges ride the slot's top-right border, exactly like the free slot's "Free Medium Print"
    // badge — one shared container so that when a print is both on sale and in the cart the two
    // sit side by side ("In cart" rightmost) instead of overlapping.
    const badges = document.createElement('div');
    badges.className = 'bundle-builder__slot-badges';
    const salePct = this._slotSalePct(slot);
    if (salePct > 0) {
      const sb = document.createElement('span');
      sb.className = 'bundle-builder__free-badge bundle-builder__free-badge--sale';
      sb.textContent = 'Extra ' + salePct + '% off';
      badges.appendChild(sb);
    }
    if (this._inCart(slot)) {
      const ic = document.createElement('span');
      ic.className = 'bundle-builder__free-badge bundle-builder__free-badge--incart';
      ic.textContent = 'In cart';
      badges.appendChild(ic);
    }
    if (badges.children.length) artistLine.appendChild(badges);
    main.appendChild(artistLine);

    // Size + Framing dropdowns (per-slot) — disabled + placeholder tooltip when locked (in cart).
    main.appendChild(this._buildOptionsRow(slot, index, 'paid', locked));
    row.appendChild(main);
    return row;
  }

  // On mobile, tapping an empty slot ("Add Artwork") opens the print-selector drawer.
  _makeEmptyTappable(row, slot) {
    if (slot.assigned) return;
    row.addEventListener('click', () => {
      if (window.matchMedia('(max-width: 749px)').matches) this._openSelectorDrawer();
    });
  }

  _buildFreeSlot(slot, index) {
    const row = document.createElement('div');
    row.className = 'bundle-builder__slot bundle-builder__slot--free' + (slot.assigned ? '' : ' bundle-builder__slot--empty') + (slot.assigned && this._inCart(slot) ? ' bundle-builder__slot--incart' : '');
    row.setAttribute('role', 'listitem');

    const media = document.createElement('div');
    media.className = 'bundle-builder__slot-media';
    if (slot.assigned) {
      media.appendChild(this._framedThumb(slot));
      const x = document.createElement('button');
      x.type = 'button';
      x.className = 'bundle-builder__slot-remove';
      x.setAttribute('aria-label', 'Remove ' + (slot.product.title || 'free print'));
      x.innerHTML = ICON_CLOSE;
      x.addEventListener('click', () => (slot._fromCart ? this._removeCartGroup(slot) : this._removeFreeSlot(index)));
      media.appendChild(x);
    } else {
      // Empty free slot shows the same "+" thumbnail as empty paid slots (item 17a).
      media.appendChild(this._thumb(null));
    }
    row.appendChild(media);

    const main = document.createElement('div');
    main.className = 'bundle-builder__slot-main';

    if (!slot.assigned) {
      // EMPTY free slot (Figma 257:10064): "Free Print Slot" + "Add artwork", yellow border.
      const head = document.createElement('div');
      head.className = 'bundle-builder__slot-head';
      const titles = document.createElement('div');
      titles.className = 'bundle-builder__slot-titles';
      titles.appendChild(this._title('Free Print Slot', false));
      head.appendChild(titles);
      // Show the free print's value struck through → £0, even while empty (item 3).
      const priceEl = document.createElement('div');
      priceEl.className = 'bundle-builder__slot-price';
      // Pre-sale value of the free print, so an EMPTY free slot reads the same way a filled one
      // does and matches the pack total above it (which values free prints pre-sale).
      const cmp = document.createElement('span');
      cmp.className = 'bundle-builder__slot-price-compare';
      cmp.textContent = formatMoney(this._freePrintWasValue());
      const fin = document.createElement('span');
      fin.className = 'bundle-builder__slot-price-final';
      fin.textContent = formatMoney(0);
      priceEl.appendChild(cmp); priceEl.appendChild(fin);
      head.appendChild(priceEl);
      main.appendChild(head);
      // "Add artwork" (left) + "Free Medium Print" badge (right) share ONE row (feedback 3).
      const badgeLine = document.createElement('div');
      badgeLine.className = 'bundle-builder__slot-artistline';
      badgeLine.appendChild(this._artist('Add artwork'));
      const badges = document.createElement('div');
      badges.className = 'bundle-builder__slot-badges';
      const fb = document.createElement('span');
      fb.className = 'bundle-builder__free-badge';
      fb.textContent = 'Free Medium Print';
      badges.appendChild(fb);
      badgeLine.appendChild(badges);
      main.appendChild(badgeLine);
      // Red nudge when the shopper has SURPLUS paid prints they could have taken as free (feedback 5):
      // shown only past the entry (≥3 paid — the 2-paid tier entry is excluded) AND while ≥2 earned
      // free slots are still empty. Matches: 3 paid + 0/1 free, 4 paid + 0/1/2 free; NOT 2 paid + 0 free.
      if (this._paidFilledCount() >= 3 && (this._freeEarnedCount() - this._freeFilledCount()) >= 2) {
        const nudge = document.createElement('div');
        nudge.className = 'bundle-builder__free-nudge';
        nudge.appendChild(document.createTextNode('Add a medium-sized unframed print to unlock your free print '));
        const tip = this._fieldTip({ text: 'Medium sizes include A3, 12x12" (30x30cm), 12x18" (30x45cm), 12x16" (30x40cm), 11x14" (28x35.5cm) and 12x15" (30x38cm).' });
        if (tip) { tip.classList.add('bundle-builder__free-nudge-tip'); nudge.appendChild(tip); } // red "?" at the end
        main.appendChild(nudge);
      }
      row.appendChild(main);
      this._makeEmptyTappable(row, slot);
      return row;
    }

    // Row 1: title (left) + price (right).
    const head = document.createElement('div');
    head.className = 'bundle-builder__slot-head';
    const t = document.createElement('div');
    t.className = 'bundle-builder__slot-titles';
    t.appendChild(this._title(slot.product.title, false));
    head.appendChild(t);
    head.appendChild(this._slotPriceEl(slot, true)); // struck → £0.00
    main.appendChild(head);
    // Row 2: "By artist" (left) + badges (right): "Free Medium Print" (yellow) and, when in
    // the cart, an "In cart" badge with the same shape but black bg / white text (item 2).
    const artistLine = document.createElement('div');
    artistLine.className = 'bundle-builder__slot-artistline';
    artistLine.appendChild(this._artist(slot.product.vendor ? 'By ' + slot.product.vendor : ''));
    const badges = document.createElement('div');
    badges.className = 'bundle-builder__slot-badges';
    const fb = document.createElement('span');
    fb.className = 'bundle-builder__free-badge';
    fb.textContent = 'Free Medium Print';
    badges.appendChild(fb);
    if (this._inCart(slot)) {
      const ic = document.createElement('span');
      ic.className = 'bundle-builder__free-badge bundle-builder__free-badge--incart';
      ic.textContent = 'In cart';
      badges.appendChild(ic);
    }
    artistLine.appendChild(badges);
    main.appendChild(artistLine);
    // Size + Framing selectors, shown but DISABLED (free size is fixed by ratio).
    main.appendChild(this._buildOptionsRow(slot, index, 'free', this._inCart(slot)));
    row.appendChild(main);
    return row;
  }

  _inCartBadge() {
    const b = document.createElement('span');
    b.className = 'bundle-builder__slot-incart';
    b.textContent = 'In cart';
    return b;
  }

  _thumb(image, alt) {
    if (image) {
      const img = document.createElement('img');
      img.className = 'bundle-builder__slot-thumb';
      img.src = image; img.alt = alt || ''; img.loading = 'lazy';
      return img;
    }
    const div = document.createElement('div');
    div.className = 'bundle-builder__slot-thumb bundle-builder__slot-thumb--empty';
    div.innerHTML = '<span aria-hidden="true" class="bundle-builder__slot-thumb-plus">+ADD</span>'; // mobile placeholder (item 20); hidden on desktop
    return div;
  }

  // Pick the frame (from the frames metaobject) matching a frame option's colour + print ratio —
  // replicating product-media.liquid's selection EXACTLY: the frame's type must equal the
  // product type (when known) and its ratio must sit within ±0.02 of the print's. A closest-match
  // without these guards could return a wrong-ratio frame id, which the cart composites with a
  // white strip under the art (cart feedback). null = unframed / no matching frame.
  _frameOverlay(frameOption, ratio, type) {
    if (!frameOption || /unframed/i.test(frameOption) || !this._frames.length) return null;
    const word = String(frameOption).trim().split(/\s+/).pop().toLowerCase(); // black / white / natural
    let cands = this._frames.filter((f) => f.color === word || f.alt === word);
    if (type) cands = cands.filter((f) => f.type === type);
    cands = cands.filter((f) => Math.abs((f.ratio || 0) - ratio) <= 0.02); // PDP tolerance
    if (!cands.length) return null;
    cands.sort((a, b) => Math.abs((a.ratio || 0) - ratio) - Math.abs((b.ratio || 0) - ratio));
    return cands[0];
  }

  // The frame metaobject id for a slot's chosen frame — set as the `_frame_id` cart line
  // property so the live cart composites the frame (cart-products.liquid), same as the PDP.
  // Type comes from the EFFECTIVE (paper/canvas) product, since that's the variant added.
  _frameIdFor(slot) {
    const eff = this._effective(slot) || slot.product;
    const ratio = (slot.product && slot.product.ratio) || 0.707;
    const ov = this._frameOverlay(slot.frame, ratio, eff && eff.type);
    return ov && ov.frameId != null ? String(ov.frameId) : null;
  }

  // Slot thumbnail that reflects the chosen frame — replicates the live PDP exactly: raw art
  // padded by the frame inset widths (--tb/--lr) + the frame PNG absolutely overlaid
  // (object-fit: contain), with the box HEIGHT auto so nothing is distorted. Unframed = raw art.
  _framedThumb(slot) {
    const p = slot.product || {};
    const art = p.artImage || p.image || null;
    const overlay = this._frameOverlay(slot.frame, p.ratio || 0.707, p.type);
    const box = document.createElement('div');
    box.className = 'bundle-builder__slot-thumb' + (overlay ? ' is-framed' : ' bb-frame--none');
    if (!overlay) {
      const img = document.createElement('img');
      img.className = 'bundle-builder__slot-art';
      if (art) img.src = art;
      img.alt = p.title || ''; img.loading = 'lazy';
      box.appendChild(img);
      return box;
    }
    box.style.setProperty('--tb', (overlay.tb || 0) / 2 + '%');
    box.style.setProperty('--lr', (overlay.lr || 0) / 2 + '%');
    const owrap = document.createElement('div');
    owrap.className = 'bb-frame-overlay-wrap';
    const fr = document.createElement('img');
    fr.className = 'bb-frame-overlay';
    fr.src = overlay.image; fr.alt = ''; fr.loading = 'lazy';
    owrap.appendChild(fr);
    const media = document.createElement('div');
    media.className = 'bb-frame-media';
    const artImg = document.createElement('img');
    artImg.className = 'bb-frame-art';
    if (art) artImg.src = art;
    artImg.alt = p.title || ''; artImg.loading = 'lazy';
    media.appendChild(artImg);
    box.appendChild(owrap);
    box.appendChild(media);
    return box;
  }
  _title(text, empty) {
    const s = document.createElement('span');
    s.className = 'bundle-builder__slot-title' + (empty ? ' bundle-builder__slot-title--empty' : '');
    s.textContent = text;
    return s;
  }
  _artist(text) {
    const s = document.createElement('span');
    s.className = 'bundle-builder__slot-artist';
    s.textContent = text;
    return s;
  }

  // Open the site's existing Size Chart / Frame ("About Frames") dialog. Those live inside
  // the native variant picker which is display:none in bundle mode, so we temporarily
  // re-parent the dialog-component to <body> (visible), then restore it on close.
  _openChart(kind) {
    const re = kind === 'frame' ? /about frames|frame/i : /size chart/i;
    const comps = [...document.querySelectorAll('dialog-component.popup-link')];
    const comp = comps.find((c) => re.test((c.querySelector('.popup-link__button') || {}).textContent || ''));
    const dlg = comp && comp.querySelector('dialog');
    if (!dlg) return;
    // Both chart popups hydrate LAZILY, and the trigger is a real interaction with their own
    // .popup-link__button: size-chart.liquid clones its <template class="wd-size-guide-source">
    // into place, frame-popup.liquid fetches .frames-tabs-content[data-frames-url]. Opening the
    // <dialog> straight from here never ran either, so the popup appeared with nothing in it.
    //
    // Fire the same delegated intent their blocks already listen for, rather than duplicating the
    // cloning/fetching here — one source of truth. `pointerover` and not `click`: both blocks bind
    // ['pointerover','focusin','touchstart','click'], but only click would ALSO trip the button's
    // own open handler and show the dialog a second time.
    //
    // Must run BEFORE the id-rewrite below: the size guide's slides only enter the DOM at this
    // point, and un-prefixed ids are exactly what that rewrite exists to fix.
    const openBtn = comp.querySelector('.popup-link__button');
    if (openBtn) openBtn.dispatchEvent(new Event('pointerover', { bubbles: true }));

    // The chart's "A0/A1/…" tabs are in-page anchor links (href="#slide-N"), but the DOM has
    // TWO charts with DUPLICATE slide IDs — so the links jumped to the other (hidden) chart and
    // the visible one never updated. Give THIS chart's ids + hrefs a unique prefix (once) so its
    // links target its own slides.
    if (!comp.dataset.bbChartFixed) {
      const uid = 'bbchart-' + kind + '-';
      comp.querySelectorAll('[id]').forEach((el) => { el.id = uid + el.id; });
      comp.querySelectorAll('a[href^="#"]').forEach((a) => { a.setAttribute('href', '#' + uid + a.getAttribute('href').slice(1)); });
      comp.querySelectorAll('[aria-controls]').forEach((el) => { el.setAttribute('aria-controls', uid + el.getAttribute('aria-controls')); });
      comp.dataset.bbChartFixed = '1';
    }
    const home = comp.parentElement;
    const next = comp.nextSibling;
    document.body.appendChild(comp);
    // Guarded on dlg.open in case the hydration intent above ever reaches a handler that opens it.
    try { if (!dlg.open) dlg.showModal(); } catch (e) { /* already open / unsupported */ }
    // Close on backdrop click (native <dialog> only closes via its X otherwise). Detect it by
    // whether the click landed OUTSIDE the dialog's box — robust even when inner content fills
    // the dialog (target === dlg then never matches). (item 2)
    const onBackdrop = (e) => {
      const r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
    };
    dlg.addEventListener('click', onBackdrop);
    const restore = () => {
      dlg.removeEventListener('close', restore);
      dlg.removeEventListener('click', onBackdrop);
      if (next && next.parentElement === home) home.insertBefore(comp, next); else home.appendChild(comp);
    };
    dlg.addEventListener('close', restore);
  }

  // A "?" icon shown next to Size / Framing. tip.chart → opens that chart; tip.text →
  // a hover/tap tooltip bubble with the given copy.
  _fieldTip(tip) {
    if (!tip) return null;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bundle-builder__tip';
    btn.innerHTML = ICON_QMARK;
    if (tip.chart) {
      btn.setAttribute('aria-label', tip.chart === 'frame' ? 'About frames' : 'Size chart');
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this._openChart(tip.chart); });
    } else if (tip.text) {
      btn.setAttribute('aria-label', 'More info');
      const bubble = document.createElement('span');
      bubble.className = 'bundle-builder__tip-bubble';
      bubble.setAttribute('role', 'tooltip');
      bubble.textContent = tip.text;
      btn.appendChild(bubble);
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this._toggleTip(btn); });
      // Desktop (hover-capable pointers): open the copy on hover too, not just click (feedback).
      // Chart openers (frame info / size chart) are excluded — they only ever open on click.
      if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
        btn.addEventListener('mouseenter', () => { if (!btn.classList.contains('is-open')) this._toggleTip(btn); });
        btn.addEventListener('mouseleave', () => this._closeAllTips());
      }
    }
    return btn;
  }

  // ---- Tooltip manager: one open at a time, tap-again closes, tap-outside closes, bubble
  // rendered as a viewport-clamped fixed element so it's never cut off or hidden (feedback 2-5).
  _toggleTip(btn) {
    const wasOpen = btn.classList.contains('is-open');
    this._closeAllTips();
    if (wasOpen) return;              // tapping the open tip closes it (feedback 2)
    btn.classList.add('is-open');     // (closeAll already cleared any other — feedback 3)
    const bubble = btn.querySelector('.bundle-builder__tip-bubble');
    if (bubble) this._positionTip(btn, bubble);
    this._ensureTipDismiss();
  }

  _closeAllTips() {
    this.querySelectorAll('.bundle-builder__tip.is-open').forEach((b) => b.classList.remove('is-open'));
    const bub = this._activeTipBubble;
    if (bub) {
      bub.style.cssText = '';
      bub.classList.remove('is-below', 'is-fixed');
      if (bub.__tipOwner) bub.__tipOwner.appendChild(bub); // return the portaled bubble to its "?"
      this._activeTipBubble = null;
    }
  }

  // Place the bubble above (or below, if no room) the "?" icon, clamped to the viewport, arrow
  // aimed at the icon. The bubble is PORTALED to <body> first: the in-field "?" button carries a
  // transform, which would otherwise make position:fixed resolve against the button, not the
  // viewport (bubble flew off-screen). document.body has no transform, so fixed = true viewport.
  _positionTip(btn, bubble) {
    const M = 8; // viewport margin
    bubble.__tipOwner = btn;
    document.body.appendChild(bubble);
    bubble.classList.add('is-fixed');
    bubble.style.position = 'fixed';
    bubble.style.left = '0px';
    bubble.style.top = '0px';
    bubble.style.right = 'auto';
    bubble.style.bottom = 'auto';
    bubble.style.transform = 'none';
    bubble.style.margin = '0';
    bubble.style.display = 'block'; // base CSS is display:none (keeps the closed bubble from overflowing the page on mobile)
    bubble.style.opacity = '1';
    bubble.style.visibility = 'visible';
    // measure after the reset so offsetWidth/Height are the true bubble size
    const icon = btn.getBoundingClientRect();
    const bw = bubble.offsetWidth;
    const bh = bubble.offsetHeight;
    const iconCenter = icon.left + icon.width / 2;
    const left = Math.max(M, Math.min(iconCenter - bw / 2, window.innerWidth - bw - M));
    let top = icon.top - bh - 10;
    let below = false;
    if (top < M) { top = icon.bottom + 10; below = true; } // not enough room above → flip below
    bubble.style.left = left + 'px';
    bubble.style.top = top + 'px';
    bubble.classList.toggle('is-below', below);
    bubble.style.setProperty('--tip-arrow-x', (iconCenter - left) + 'px'); // arrow meets the icon (feedback 5)
    this._activeTipBubble = bubble;
  }

  // Footnote for the asterisk on the per-print prices in the tier buttons. It belongs under the
  // details accordion (whose last row is "Delivery & returns"), but that placement lives in the
  // template JSON where this block can't render, so the static copy is appended once here — chrome,
  // not price logic; nothing is recomputed. Carries the Visually-hidden class so the A/B control
  // arm drops it with the same rule as the in-button lines.
  _initPppFootnote() {
    // Zero-discount offer (sale mode): the Liquid suppresses the per-print tile lines, so
    // the asterisk this footnote explains doesn't exist — skip the footnote too.
    if (!this.querySelector('.bundle-builder__tier-ppp')) return;
    const details = this.closest('.product-details');
    const accordion = details && details.querySelector('.accordion');
    if (!accordion || details.querySelector('.bundle-builder__ppp-footnote')) return;
    const note = document.createElement('p');
    note.className = 'bundle-builder__ppp-footnote bundle-builder__price-per-print';
    note.textContent = '* Price per print calculated using the lowest total bundle price divided by the number of paid and free prints.';
    accordion.insertAdjacentElement('afterend', note);
  }

  _ensureTipDismiss() {
    if (this._tipDismissBound) return;
    this._tipDismissBound = true;
    document.addEventListener('click', (e) => { if (!e.target.closest('.bundle-builder__tip')) this._closeAllTips(); });
    window.addEventListener('scroll', () => this._closeAllTips(), true);
    window.addEventListener('resize', () => this._closeAllTips());
  }

  // Per-print price: original struck + discounted. Free slots (isFree) show original struck → £0.00.
  _slotPriceEl(slot, isFree) {
    const wrap = document.createElement('div');
    wrap.className = 'bundle-builder__slot-price';
    const eff = this._effective(slot) || slot.product;
    const v = this.variantFor(eff, slot.size, slot.frame);
    const orig = v ? v.price : 0;
    // Pre-sale price. Equal to `orig` unless this exact variant is marked down, so the extra
    // element below simply never renders for the ~95% of the catalogue that isn't on sale.
    const was = v ? wasPriceOf(v) : 0;
    if (isFree) {
      // Struck value = the price with NO discounts of any kind, so a marked-down free print is
      // shown at its true worth (£40.00, not the £32.00 sale price) — the customer pays £0 either
      // way, and the pack total values free prints pre-sale too.
      if (was > 0) {
        const c = document.createElement('span');
        c.className = 'bundle-builder__slot-price-compare';
        c.textContent = formatMoney(was);
        wrap.appendChild(c);
      }
      const s = document.createElement('span');
      s.className = 'bundle-builder__slot-price-final';
      // A framed free print: the print is free but its separate physical frame is paid — show the
      // frame's price instead of £0.00.
      const framed = slot.frame && !/unframed/i.test(slot.frame);
      s.textContent = formatMoney(framed && this.state.removeFraming && this._framingSvcPrint(slot)
        ? this._childFramePrice(this._frameHandleFor(slot), slot.size, slot.frame) : 0);
      wrap.appendChild(s);
      return wrap;
    }
    // Paid slot: ALWAYS show the discounted per-print price (item 4b). If the pack isn't
    // complete yet, add a small grey-italic caveat under the price.
    const pct = Number(this._tier().discount) || 0;
    const disc = Math.round(orig * (1 - pct / 100));
    const line = document.createElement('div');
    line.className = 'bundle-builder__slot-price-line';
    // TWO numbers only: the struck price is the print with NO discounts at all (neither the pack
    // discount nor any markdown), and the final price carries BOTH. The intermediate sale-only
    // price is deliberately not shown — the "EXTRA X% OFF" badge and the discount note on the
    // artist line explain the split instead. For a print that isn't marked down `was === orig`,
    // so this renders exactly as it always has.
    if (was > disc && was > 0) {
      const c = document.createElement('span');
      c.className = 'bundle-builder__slot-price-compare';
      c.textContent = formatMoney(was);
      line.appendChild(c);
    }
    const s = document.createElement('span');
    s.className = 'bundle-builder__slot-price-final';
    s.textContent = formatMoney(disc || orig);
    line.appendChild(s);
    wrap.appendChild(line);
    // The "Discount applies to X pack" caveat now lives on the artist line (see
    // _discountNote / _buildPaidSlot, item 5), not stacked under the price.
    return wrap;
  }

  // Framing service (item PDP-1). Eligible = assigned prints that are framed, NOT yet
  // in the cart, and carry the `framing-service` tag. In-cart prints already made their
  // framing choice. Framed FREE prints count too (possible only while the service is
  // removed): they keep the toggle visible so unchecking can move them back to paid, and
  // count toward the "(-£X)" the service would cost if re-added.
  // Does this print carry the professional framing service (`framing-service` tag)? This is the
  // signal that makes the "Remove framing service" toggle appear for a print, and the store's
  // marker for prints that ship as an unframed print + a separate child frame. Only these may sit
  // FRAMED in a free slot while the service is removed — a framed print WITHOUT it has no such
  // split, so it stays paid. Shared by the toggle and the free/paid moves so they can't diverge.
  _framingSvcPrint(slot) {
    return !!(slot && slot.product && slot.product.framingService);
  }

  // Assigned, not-in-cart slots that carry a frame — each needs its own child-frame lookup.
  _framedSlots() {
    const framed = (s) => s && s.assigned && s.product && s.frame && !/unframed/i.test(s.frame) && !this._inCart(s);
    return this.state.paidSlots.filter(framed).concat(this.state.freeSlots.filter(framed));
  }

  _framingEligibleSlots() {
    const out = [];
    // Whole array, not the first `tier.paid` — this drives the framing fee in BOTH the quoted
    // total and the CTA, while _addToCart adds a framing-service line per framed slot at any
    // index. Windowing here quoted a fee for fewer prints than the cart was actually charged for.
    for (let i = 0; i < this.state.paidSlots.length; i++) {
      const s = this.state.paidSlots[i];
      if (!s || !s.assigned || !s.product) continue;
      if (this._inCart(s)) continue;
      const framed = s.frame && s.frame !== 'Unframed';
      if (framed && this._framingSvcPrint(s)) out.push(s);
    }
    // Framed FREE prints count too — they only exist while the service is REMOVED, so including
    // them keeps this block visible (unchecking it must be able to move them back to paid). The
    // fee itself is 0 while removed, so they never add a charge.
    for (let i = 0; i < this.state.freeSlots.length; i++) {
      const s = this.state.freeSlots[i];
      if (!s || !s.assigned || !s.product) continue;
      if (this._inCart(s)) continue;
      const framed = s.frame && s.frame !== 'Unframed';
      if (framed && this._framingSvcPrint(s)) out.push(s);
    }
    return out;
  }

  // Render the "Professional Framing" block (heading + copy + "Remove framing service (-£X)"
  // toggle) between the slots and the material selector. Only shows when there's a removable
  // framing service to act on (≥1 eligible framed print). Value = fee × eligible count.
  _renderFraming() {
    // Any framed print needs ITS OWN child-frame product (custom.child_frames) — for the free
    // slot's frame price and for the unframed-print + separate-frame split at add-to-cart.
    // _childFramesFor caches per print handle and repaints when each lookup lands.
    this._framedSlots().forEach((s) => this._childFramesFor(this._frameHandleFor(s)));
    const el = this.framingEl;
    if (!el) return;
    const svc = this._framingSvc || {};
    const fee = Number(svc.price) || 0;
    const eligible = this._framingEligibleSlots();
    if (!fee || !eligible.length) { el.hidden = true; el.innerHTML = ''; this.state.removeFraming = false; return; }
    const total = fee * eligible.length;
    const tip = svc.tooltip || 'Framed prints are professionally assembled and arrive ready to hang. Remove this if you’d rather frame them yourself.';
    el.hidden = false;
    el.innerHTML =
      '<p class="bundle-builder__framing-heading">' + (svc.heading || 'Professional Framing') + '</p>' +
      '<p class="bundle-builder__framing-desc">' + (svc.description || '') + '</p>' +
      '<label class="bundle-builder__framing-toggle">' +
        '<input type="checkbox" class="bundle-builder__framing-check"' + (this.state.removeFraming ? ' checked' : '') + '>' +
        '<span class="bundle-builder__framing-label">' + (svc.label || 'Remove framing service') + ' (-' + formatMoney(total) + ')</span>' +
        '<span class="bundle-builder__framing-info" tabindex="0" role="button" aria-label="About professional framing">' + ICON_INFO +
          '<span class="bundle-builder__framing-tip" role="tooltip">' + tip + '</span></span>' +
      '</label>';
    const cb = el.querySelector('.bundle-builder__framing-check');
    if (cb) cb.addEventListener('change', () => {
      this.state.removeFraming = cb.checked;
      // Framed prints at the locked free size follow the service state: with the service REMOVED
      // the cart ships the unframed print + a separate paid frame, so a print that a frame pushed
      // out of a free slot moves BACK to free (keeping size + frame); re-adding the service moves
      // framed free prints back to paid. Loop until a full pass moves nothing.
      let changed = false;
      let guard = 0;
      let moved = true;
      while (moved && guard++ < 10) {
        moved = false;
        if (cb.checked) {
          for (let i = 0; i < this.state.paidSlots.length; i++) {
            if (this._convertPaidToFreeIfEligible(i)) { moved = true; changed = true; break; }
          }
        } else {
          for (let i = 0; i < this.state.freeSlots.length; i++) {
            if (this._moveFreeToPaidIfIneligible(i)) { moved = true; changed = true; break; }
          }
        }
      }
      if (changed) { this._afterSlotChange(); return; }
      // Toggling the service changes which framed prints are free-eligible, so re-rank even when
      // the loop above moved nothing between columns.
      if (this._rebalanceFreeSlots()) { this._afterSlotChange(); return; }
      this._persistSave();
      this.recalculate();
    });
    const info = el.querySelector('.bundle-builder__framing-info');
    if (info) info.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); info.classList.toggle('is-open'); });
  }

  // The grey "Discount applies to [X] pack" caveat, shown on the artist line while the
  // pack discount isn't yet unlocked (item 5). Returns null when it shouldn't show.
  _discountNote(slot) {
    const pct = Number(this._tier().discount) || 0;
    if (pct <= 0 || this._discountUnlocked()) return null;
    const note = document.createElement('span');
    note.className = 'bundle-builder__slot-price-note';
    const packNum = this._packNum();
    // Because the slot's single discounted price now folds the pack discount and the markdown
    // together, this line is what breaks the two apart. Non-sale prints keep the original copy.
    const salePct = this._slotSalePct(slot);
    // Shorter copy leaves room for the artist name on mobile (mobile feedback 3).
    note.textContent = salePct > 0
      ? packNum + ' pack discount + Extra ' + salePct + '%'
      : 'Applicable to ' + packNum + ' pack';
    return note;
  }

  // Size + Framing dropdowns for a slot. disabled=true renders them greyed/inactive (free slots).
  // A free print is medium (A3) + unframed. If the shopper changes a free slot to any other size or
  // adds a frame, it's no longer a free print — move it into a paid slot, keeping its chosen size/frame
  // (feedback #17). The freed-up free slot returns to empty.
  _moveFreeToPaidIfIneligible(index) {
    const slot = this.state.freeSlots[index];
    if (!slot || !slot.assigned || this._inCart(slot)) return false;
    const isFreeSize = this._isFreeSize(this._effective(slot) || slot.product, slot.size || '');
    const unframed = !slot.frame || /unframed/i.test(slot.frame);
    // Still a valid free print: locked size + unframed — or framed ONLY while the framing service
    // is removed AND the print offers that service (it then ships unframed with a separate PAID
    // frame, so the PRINT itself stays free). With the service kept, adding a frame moves the
    // print UP into a paid slot, which is what surfaces the .bundle-builder__framing toggle.
    if (isFreeSize && (unframed || (this.state.removeFraming && this._framingSvcPrint(slot)))) return false;
    let target = this._firstEmptyPaid();
    if (!target && this._bumpTier()) target = this._firstEmptyPaid();
    if (!target) {
      const idx = this.state.paidSlots.length;
      this.state.paidSlots.push({ assigned: false, product: null, size: null, frame: this._defaultFrame });
      target = { kind: 'paid', index: idx };
    }
    const p = this.state.paidSlots[target.index];
    p.assigned = true; p.product = slot.product; p.size = slot.size; p.frame = slot.frame;
    slot.assigned = false; slot.product = null; slot.size = this._defaultSize; slot.frame = this._defaultFrame;
    this._compactSlots();
    return true;
  }

  _buildOptionsRow(slot, index, kind, disabled) {
    const wrap = document.createElement('div');
    wrap.className = 'bundle-builder__slot-options';
    const eff = this._effective(slot) || slot.product;
    const variants = (eff && eff.variants) || [];
    const sizes = [...new Set(variants.map((v) => v.option1).filter(Boolean))];
    const frames = [...new Set(variants.map((v) => v.option2).filter(Boolean))];
    const store = kind === 'free' ? this.state.freeSlots : this.state.paidSlots;
    // Paid slots: "?" opens the existing size / frame charts. Free (locked) slots: a
    // placeholder-copy tooltip (item 8b).
    // Disabled slots (free print, or a paid print locked because it's in the cart) show a "?"
    // INSIDE the dropdown with placeholder copy. Editable paid slots open the size/frame charts.
    const inField = !!disabled;
    // Disabled-dropdown tooltip copy depends on WHY it's disabled (items G7/G8/G11):
    //   free slot  → size/frame are fixed for the free medium print;
    //   locked paid → the print is already in the cart and can't be edited.
    let sizeTip, frameTip;
    if (kind === 'free') {
      sizeTip = { text: 'Free prints must be medium-sized. Medium sizes include A3, 12x12" (30x30cm), 12x18" (30x45cm), 3:4: 12x16" (30x40cm), 11x14" (28x35.5cm) and 12x15" (30x38cm).' };
      frameTip = { text: 'Free prints are unframed. Adding a frame moves this print to a paid slot.' };
    } else if (disabled) {
      const locked = 'Once products have been added to cart they cannot be edited. To make any changes, please remove from your cart';
      sizeTip = { text: locked };
      frameTip = { text: locked };
    } else {
      sizeTip = { chart: 'size' };
      frameTip = { chart: 'frame' };
    }
    // Editable paid slots get the custom price dropdown (per-option price + frame swatch,
    // Figma 368-2418, item GB). Free + locked (in-cart) slots keep the native <select> — they
    // aren't editable and instead surface a placeholder-copy tooltip inside the field.
    if (!disabled) {
      // Editable paid AND free slots use the custom dropdown so the framing options show real frame
      // images (feedback). Free slots price each option too — £0.00 while it keeps the print free,
      // else the paid price — and, on a change away from the locked size/unframed, move to a paid
      // slot — handled inside _priceSelectField.
      wrap.appendChild(this._priceSelectField('Size', 'size', sizes, slot, index, store, sizeTip));
      if (frames.length) wrap.appendChild(this._priceSelectField('Framing', 'frame', frames, slot, index, store, frameTip));
    } else {
      wrap.appendChild(this._selectField('Size', sizes, slot.size, (val) => { store[index].size = val; if (kind === 'free') this._moveFreeToPaidIfIneligible(index); this._afterSlotChange(); }, disabled, sizeTip, inField));
      if (frames.length) wrap.appendChild(this._selectField('Framing', frames, slot.frame, (val) => { store[index].frame = val; if (kind === 'free') this._moveFreeToPaidIfIneligible(index); this._afterSlotChange(); }, disabled, frameTip, inField));
    }
    return wrap;
  }

  // Editable paid-slot Size / Framing selector rendered as a custom listbox so each option can
  // show its own variant price (with a struck original + discounted markdown once the pack
  // discount is unlocked) and, for frames, a colour swatch — following Figma 368-2418 (item GB).
  _priceSelectField(label, kind, options, slot, index, store, tip) {
    const field = document.createElement('div');
    field.className = 'bundle-builder__field bundle-builder__field--price';

    const lab = document.createElement('span');
    lab.className = 'bundle-builder__field-label';
    lab.textContent = label;
    if (tip) { const t = this._fieldTip(tip); if (t) { lab.appendChild(document.createTextNode(' ')); lab.appendChild(t); } }
    field.appendChild(lab);

    const control = document.createElement('div');
    control.className = 'bundle-builder__price-select';

    const current = () => (kind === 'size' ? slot.size : slot.frame);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bundle-builder__select bundle-builder__select--price';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    const btnText = document.createElement('span');
    btnText.className = 'bundle-builder__price-select-val';
    btnText.textContent = kind === 'frame' ? this._frameBtnLabel(current()) : current();
    btn.appendChild(btnText);
    control.appendChild(btn);

    const menu = document.createElement('div');
    menu.className = 'bundle-builder__price-menu';
    menu.setAttribute('role', 'listbox');

    const eff = this._effective(slot) || slot.product;
    const pct = Number(this._tier().discount) || 0;
    const isFree = store === this.state.freeSlots; // free slots price options by free-eligibility (£0 while free)
    // Per-AXIS option pricing, mirroring the single view (variant-main-picker-frame.liquid: the
    // size list prices off the UNFRAMED variant, the frame swatch off the frame's own price).
    // Pricing BOTH menus off the full size+frame combo made each one repeat the line total, so a
    // framed slot read £56 under Size *and* under Framing instead of £28 print + £28 frame.
    // splitOK guards products whose option2 carries no unframed value at all (nothing to subtract
    // the frame's share from) — those keep the old combo pricing.
    const unframedOpt = this._unframedOption(eff);
    const splitOK = ((eff && eff.variants) || []).some((v) => v.option2 === unframedOpt);
    const at = (size, frame) => this.variantFor(eff, size, frame);
    const disc = (v) => Math.round(v * (1 - pct / 100));
    // { sale, was } for one option, reduced to its own axis. `was` stays the pre-sale figure with
    // NO discounts (same two-number contract as the slot header). The frame's share is taken as a
    // DIFFERENCE, and its discounted figure as the difference of the two DISCOUNTED prices, so
    // Size + Framing always add back up to the slot header exactly (no 1p rounding drift) and the
    // store's split markdown (-20% print / -10% framed, see wasPriceOf) stays on the print.
    const optParts = (val) => {
      const size = kind === 'size' ? val : slot.size;
      const frame = kind === 'frame' ? val : slot.frame;
      const combo = at(size, frame);
      const comboPrice = combo ? combo.price : 0;
      const comboWas = combo ? wasPriceOf(combo) : 0;
      if (!splitOK) return { sale: disc(comboPrice), was: comboWas };
      const base = at(size, unframedOpt);
      const basePrice = base ? base.price : 0;
      const baseWas = base ? wasPriceOf(base) : 0;
      if (kind === 'size') return { sale: disc(basePrice), was: baseWas };
      return { sale: Math.max(0, disc(comboPrice) - disc(basePrice)), was: Math.max(0, comboWas - baseWas) };
    };

    options.forEach((val) => {
      const opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'bundle-builder__price-opt' + (val === current() ? ' is-selected' : '');
      opt.setAttribute('role', 'option');
      if (kind === 'frame') {
        // Use the real frame images from the single-view frame picker rather than solid colour
        // chips (feedback 5); fall back to a colour chip only if no image is found.
        const src = this._frameImageFor(val);
        if (src) {
          const im = document.createElement('img');
          im.className = 'bundle-builder__frame-swatch bundle-builder__frame-swatch--img';
          im.src = src; im.alt = ''; im.loading = 'lazy';
          opt.appendChild(im);
        } else {
          const sw = document.createElement('span');
          sw.className = 'bundle-builder__frame-swatch';
          const c = this._frameSwatchColour(val);
          if (c) sw.style.background = c; else sw.classList.add('is-none');
          opt.appendChild(sw);
        }
      }
      const name = document.createElement('span');
      name.className = 'bundle-builder__price-opt-name';
      name.textContent = kind === 'frame' ? this._frameOptLabel(val) : val;
      opt.appendChild(name);
      // Free slots show per-option prices too (feedback): an option that keeps the print
      // free (the ratio's locked size + unframed) is £0.00; any other option shows what the
      // print would cost as a PAID print, since picking it moves the slot to paid. In the
      // FRAMING menu only, a framed option that keeps the print free (service removed) shows
      // the separate physical frame's own price — the print is free, its frame is paid (flat,
      // no discount). The SIZE menu's locked size stays £0.00 whatever frame is selected.
      const optSize = kind === 'size' ? val : slot.size;
      const optFrame = kind === 'frame' ? val : slot.frame;
      const optFramed = optFrame && !/unframed/i.test(optFrame);
      const keepsFree = isFree && this._isFreeSize(eff, optSize)
        && (!optFramed || (this.state.removeFraming && this._framingSvcPrint(slot)));
      // A framed option with no real variant at the slot's current size (e.g. A0 offers no
      // frames) renders crossed-out + disabled, mirroring the PDP's strikethrough — instead of
      // letting variantFor's size-only fallback price it as unframed. Options priced via the
      // separate child-frame product (a framed FREE print) don't use the main combo, so exempt.
      const viaChildFrame = keepsFree && optFramed && kind === 'frame';
      const unavailable = kind === 'frame' && optFramed && !viaChildFrame && !this._comboExists(eff, optSize, val);
      if (unavailable) {
        opt.classList.add('wd-bb-unavailable');
        opt.setAttribute('aria-disabled', 'true');
      } else {
        const priceEl = document.createElement('span');
        priceEl.className = 'bundle-builder__price-opt-price';
        if (viaChildFrame) {
          priceEl.textContent = formatMoney(this._childFramePrice(this._frameHandleFor(slot), optSize, optFrame));
        } else if (kind === 'frame' && splitOK && !optFramed) {
          // "Unframed" carries no frame cost, so its row is left blank — the single view's frame
          // picker prices only the framed swatches too. The print's own price is in the Size menu.
          priceEl.textContent = '';
        } else {
          // TWO numbers, matching the slot header: struck = the option with NO bundle discount and
          // NO markdown; final = both applied. For an option that isn't marked down `was === price`,
          // so this renders exactly as it did before any sale work.
          const { sale, was } = keepsFree ? { sale: 0, was: 0 } : optParts(val);
          priceEl.innerHTML = (was > sale)
            ? '<s>' + formatMoney(was) + '</s> ' + formatMoney(sale)
            : formatMoney(sale);
        }
        opt.appendChild(priceEl);
      }
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        if (unavailable) return; // no variant for this size+frame — not selectable
        store[index][kind === 'size' ? 'size' : 'frame'] = val;
        // A size change can invalidate the chosen frame (e.g. A0 has no framed variants) —
        // reset it to Unframed so the slot never shows a frame that would add unframed.
        if (kind === 'size') this._dropFrameIfComboMissing(store[index]);
        this._closePriceMenus();
        if (isFree) this._moveFreeToPaidIfIneligible(index); // A3/unframed only; else moves to paid
        else this._convertPaidToFreeIfEligible(index);
        this._afterSlotChange();
      });
      menu.appendChild(opt);
    });

    control.appendChild(menu);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = control.classList.contains('is-open');
      this._closePriceMenus();
      if (!isOpen) { control.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); }
    });

    field.appendChild(control);
    this._ensurePriceMenuDismiss();
    return field;
  }

  // Frame option label per Figma: "Unframed" as-is; framed options as "+ <Colour> Frame"
  // (the raw variant values read "Framed black" etc., so normalise to "Black Frame").
  _frameOptLabel(val) {
    const colour = this._frameColour(val);
    return colour ? '+ ' + colour + ' Frame' : val;
  }
  // Closed-button label: "Unframed" or "<Colour> Frame".
  _frameBtnLabel(val) {
    const colour = this._frameColour(val);
    return colour ? colour + ' Frame' : val;
  }
  // Map a frame COLOUR (from a separate <frame-variant-picker>, e.g. "black", "oak",
  // "natural") to one of THIS product's frame option2 values ("Framed Black"), so a
  // frame chosen in the child-frame picker lands on the right pack slot frame.
  // Pull a clean, title-cased colour word out of a (possibly verbose) frame label —
  // "black-wood-frame" → "Black", "Natural Oak" → "Natural". Null for unframed/empty.
  _frameColourWord(colour) {
    if (!colour) return null;
    const raw = String(colour).replace(/-/g, ' ').trim().toLowerCase();
    if (!raw || /unframed|no frame/.test(raw)) return null;
    const COLOURS = ['black', 'white', 'oak', 'natural', 'walnut', 'gold', 'silver', 'green', 'orange', 'brown', 'pine', 'wood'];
    const word = COLOURS.find((c) => raw.includes(c)) || raw.split(' ')[0];
    return word ? word.charAt(0).toUpperCase() + word.slice(1) : null;
  }

  // Map a frame COLOUR (from a separate frame picker) to one of THIS product's frame
  // option2 values ("Framed Black"). Returns null when the product has no such option2
  // (true child-frame products, where the frame is a linked product, not an option).
  _matchFrameByColour(product, colour) {
    const word = (this._frameColourWord(colour) || '').toLowerCase();
    if (!word) return null;
    const opts = [...new Set(((product && product.variants) || []).map((v) => v.option2).filter(Boolean))];
    // 1. option2 whose extracted colour matches the word.
    let hit = opts.find((o) => (this._frameColour(o) || '').toLowerCase() === word);
    // 2. option2 that contains the colour word.
    if (!hit) hit = opts.find((o) => o.toLowerCase().includes(word));
    // 3. natural ↔ oak / wood synonym.
    if (!hit && /oak|natural|wood|pine/.test(word)) hit = opts.find((o) => /oak|natural|wood|pine/i.test(o));
    return hit || null;
  }

  // Extract the title-cased colour from a frame option value ("Framed black" → "Black").
  // Returns null for unframed / no-frame options.
  _frameColour(val) {
    const v = val || '';
    if (/unframed|no frame/i.test(v)) return null;
    const c = v.replace(/framed|frame/gi, '').replace(/[^a-z ]/gi, ' ').trim();
    if (!c) return null;
    return c.charAt(0).toUpperCase() + c.slice(1).toLowerCase();
  }
  // Build a { colour: imageURL } map from the single-view frame picker's swatch images, so the
  // bundle dropdown reuses the real frame photos (feedback 5). Prefers the corner close-up shot.
  _frameImageMap() {
    if (this._frameImgMap) return this._frameImgMap;
    const map = {};
    const fvp = document.querySelector('frame-variant-picker');
    if (fvp) {
      fvp.querySelectorAll('img').forEach((img) => {
        const src = img.currentSrc || img.getAttribute('src') || '';
        const m = src.match(/(black|white|oak|natural|gold|silver|walnut|green|orange)/i);
        if (!m) return;
        const c = m[1].toLowerCase();
        // Use the SAME clean frame-corner closeups the single-view picker shows (feedback), NOT the
        // product-in-frame mockup ("thing-frame") or other catalogue shots. Corner closeups:
        // wood/paper frames → "…corner-closeup-<colour>", metal → "metal-frame-corner-<colour>",
        // GlowFrame → "GlowFrame-<colour>-small".
        const isCorner = /corner-closeup|metal-frame-corner|glowframe-[a-z]+-small/i.test(src);
        // The picker's lazy <img> often exposes a 1px placeholder (…&width=1), which rendered as a
        // blank swatch (feedback). Force a real render width on the Shopify CDN URL so the frame
        // photo actually shows regardless of the picker's current lazy state.
        let clean = src.replace(/^\/\//, 'https://').replace(/([?&](?:width|height)=)\d+/gi, '$1120');
        if (!/[?&]width=/i.test(clean)) clean += (clean.indexOf('?') > -1 ? '&' : '?') + 'width=120';
        if (!map[c] || (isCorner && !map[c].corner)) map[c] = { src: clean, corner: isCorner };
      });
    }
    this._frameImgMap = map;
    return map;
  }
  _frameImageFor(frameName) {
    const v = (frameName || '').toLowerCase();
    if (/unframed|no frame/.test(v)) return null;
    const map = this._frameImageMap();
    for (const c of ['black', 'white', 'oak', 'natural', 'walnut', 'gold', 'silver', 'green', 'orange']) {
      if (v.includes(c) && map[c]) return map[c].src;
    }
    if (/natural|wood|pine/.test(v) && map.oak) return map.oak.src; // natural ↔ oak fallback
    return null;
  }
  // A solid colour chip approximating each frame (adapted from the Figma's frame-corner photo).
  _frameSwatchColour(val) {
    const v = (val || '').toLowerCase();
    if (/unframed|no frame/.test(v)) return null;
    if (/black/.test(v)) return '#1a1a1a';
    if (/white/.test(v)) return '#f2f2f2';
    if (/oak|natural|wood|pine/.test(v)) return '#c8a366';
    if (/walnut|brown|dark/.test(v)) return '#5b3a29';
    return '#cccccc';
  }
  _closePriceMenus() {
    this.querySelectorAll('.bundle-builder__price-select.is-open').forEach((c) => {
      c.classList.remove('is-open');
      const b = c.querySelector('.bundle-builder__select--price');
      if (b) b.setAttribute('aria-expanded', 'false');
    });
  }
  _ensurePriceMenuDismiss() {
    if (this._priceDismissBound) return;
    this._priceDismissBound = true;
    document.addEventListener('click', (e) => { if (!e.target.closest('.bundle-builder__price-select')) this._closePriceMenus(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this._closePriceMenus(); });
  }

  _selectField(label, options, current, onChange, disabled, tip, tipInField) {
    const field = document.createElement('label');
    field.className = 'bundle-builder__field' + (disabled ? ' is-disabled' : '');
    const lab = document.createElement('span');
    lab.className = 'bundle-builder__field-label';
    lab.textContent = label;
    // Paid slots: "?" next to the label. Free (disabled) slots: "?" inside the dropdown (item 10).
    if (tip && !tipInField) {
      const tipEl = this._fieldTip(tip);
      if (tipEl) { lab.appendChild(document.createTextNode(' ')); lab.appendChild(tipEl); }
    }
    field.appendChild(lab);
    const sel = document.createElement('select');
    sel.className = 'bundle-builder__select';
    if (disabled) sel.disabled = true;
    options.forEach((o) => {
      const opt = document.createElement('option');
      opt.value = o; opt.textContent = o;
      if (o === current) opt.selected = true;
      sel.appendChild(opt);
    });
    if (!disabled) sel.addEventListener('change', () => onChange(sel.value));
    if (tip && tipInField) {
      const ctrl = document.createElement('span');
      ctrl.className = 'bundle-builder__field-control';
      ctrl.appendChild(sel);
      const tipEl = this._fieldTip(tip);
      if (tipEl) { tipEl.classList.add('bundle-builder__tip--in-field'); ctrl.appendChild(tipEl); }
      field.appendChild(ctrl);
    } else {
      field.appendChild(sel);
    }
    return field;
  }

  // Remove a paid print. If a free print is chosen, it "moves up" into the vacated paid
  // slot (becomes paid) and the free slot re-opens; otherwise the slot just empties.
  _removeSlot(index) {
    const slot = this.state.paidSlots[index];
    if (!slot) return;
    const tier = this._tier();
    // Empty the removed paid slot.
    slot.assigned = false;
    slot.product = this._currentProductRef();
    slot.size = this._defaultSize;
    slot.frame = this._defaultFrame;
    // A free print is only "earned" once enough PAID prints are filled: none below the entry (duo)
    // threshold of 2 paid, then one free per additional paid, capped at the tier's free count. If
    // removing this paid print leaves MORE free prints than are now earned, pull a free print up into
    // the vacated paid slot — KEEPING its size (a promoted free print must NOT resize A3→A4 — feedback).
    // Duo: any free needs both paid, so removing a paid always pulls the free up. Quad/4+: a free can
    // sit alongside fewer paid, so it's pulled up only when the paid count drops below what free needs.
    const paidFilled = () => this.state.paidSlots.filter((s) => s && s.assigned).length;
    const freeAssigned = () => this.state.freeSlots.filter((s) => s && s.assigned);
    const freeEarned = (pf) => (pf < 2 ? 0 : Math.min(pf, tier.free));
    let guard = 0;
    while (guard++ < 12) {
      const free = freeAssigned();
      if (free.length <= freeEarned(paidFilled())) break;
      const emptyIdx = this.state.paidSlots.findIndex((s) => !s || !s.assigned);
      if (emptyIdx < 0) break;
      const dst = this.state.paidSlots[emptyIdx] || (this.state.paidSlots[emptyIdx] = { assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame });
      const src = free[free.length - 1]; // pull the LAST free print up into the paid slot
      dst.assigned = true;
      dst.product = src.product;
      dst.size = src.size;   // keep the size — do NOT resize (feedback)
      dst.frame = src.frame; // keep the frame too
      src.assigned = false;
      src.product = null;
      src.frame = this._defaultFrame;
    }
    this._compactSlots();
    this._afterSlotChange();
  }

  // Remove a print that's already IN THE CART (the slot X on a locked "In cart" slot). Removes the
  // cart line (+ its frame/framing-service child lines), refreshes the cart UI without a hard reload,
  // and clears the matching bundle-state slot so it can't re-appear as a "phantom" editable slot.
  async _removeCartGroup(slot) {
    try {
      const key = slot._cartKey;
      const variantId = String(slot._variantId || '');
      const productId = String((slot.product && slot.product.id) || '');
      const findTarget = (items, keyOf) => items.find((i) => key && keyOf(i) === key)
        || items.find((i) => variantId && String(i.variant_id) === variantId)
        || items.find((i) => productId && String(i.product_id) === productId);
      // Use the cached cart lines (they carry key + parentKey) so we can build the removal without a
      // fresh /cart.js fetch — one less round-trip (feedback 4). Fall back to a fetch if the cache misses.
      let target = findTarget(this._cartItems || [], (i) => i.key);
      const updates = {};
      if (target) {
        updates[target.key] = 0;
        (this._cartItems || []).forEach((i) => { if (i.parentKey && i.parentKey === target.key) updates[i.key] = 0; });
      } else {
        const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
        const items = cart.items || [];
        target = findTarget(items, (i) => i.key);
        if (!target) return;
        updates[target.key] = 0;
        items.forEach((i) => {
          const pk = i.parent_relationship && i.parent_relationship.parent && i.parent_relationship.parent.key;
          if (pk && pk === target.key) updates[i.key] = 0; // frame / framing-service child line
        });
      }
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId).filter(Boolean);
      const res = await fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, sections: sectionIds }),
      });
      const data = await res.json().catch(() => null);
      if (data && typeof window.manualCartRefresh === 'function') window.manualCartRefresh(data);
      this._selfCartUpdate = true; // we reconcile from `data` below — don't let our listener refetch
      document.dispatchEvent(new CustomEvent('cart:update'));
      // Also drop the matching bundle-STATE slot (same product + size + frame) so a still-assigned
      // slot can't re-render as an editable "phantom" print once it's no longer in the cart.
      const pid = String((slot.product && slot.product.id) || '');
      const clearMatch = (store, free) => {
        // Prefer an exact product+size+frame match; fall back to a product-id-only match so an
        // in-cart print is ALWAYS cleared from the bundle even when the cart line's parsed size/frame
        // differ from the state slot's (otherwise the print reappeared as an editable "phantom" —
        // feedback: deleted from cart but not the bundle).
        let i = store.findIndex((s) => s && s.assigned && s.product && String(s.product.id) === pid
          && (slot.size == null || s.size === slot.size) && (slot.frame == null || s.frame === slot.frame));
        if (i < 0) i = store.findIndex((s) => s && s.assigned && s.product && String(s.product.id) === pid);
        if (i < 0) return false;
        store[i].assigned = false;
        store[i].product = free ? null : this._currentProductRef();
        store[i].size = free ? store[i].size : this._defaultSize;
        store[i].frame = this._defaultFrame;
        return true;
      };
      if (pid && !clearMatch(this.state.paidSlots, false)) clearMatch(this.state.freeSlots, true);
      this._compactSlots();
      // Reconcile from the /cart/update.js response instead of a fresh /cart.js fetch — one less
      // round-trip, so removing an in-cart print feels quicker (feedback 4).
      if (data && data.items) this._applyCartData(data);
      else await this._loadCart();
      // Removing an in-cart PAID print can leave more free prints than the remaining paid earn —
      // _afterSlotChange runs _enforceFreeCap (promote extra free → paid) and re-renders (feedback 1).
      this._afterSlotChange();
    } catch (e) { /* leave the cart untouched on failure */ }
  }

  _compactSlots() {
    const assigned = this.state.paidSlots.filter((s) => s.assigned);
    // Never shrink below MAX_PAID, so switching UP to a bigger tier can't leave the array
    // too short for its paid slots (which caused a render crash + stuck "Adding…").
    const keep = Math.max(MAX_PAID, assigned.length);
    const result = assigned.slice();
    while (result.length < keep) result.push({ assigned: false, product: null, size: this._defaultSize, frame: this._defaultFrame });
    this.state.paidSlots = result;
  }

  // Remove a free print (slot returns to the empty "FREE · Empty slot" state).
  _removeFreeSlot(index) {
    const slot = this.state.freeSlots[index];
    if (!slot) return;
    slot.assigned = false;
    slot.product = null;
    slot.frame = this._defaultFrame;
    this._afterSlotChange();
  }

  _afterSlotChange() {
    this._enforceFreeCap(); // never leave more free prints than the filled paid prints have earned
    this._rebalanceFreeSlots(); // …then hand the free slots to the CHEAPEST eligible prints, as checkout will
    this._renderSlots();
    this._updateSelectorCounter();
    this._markCards();
    this.recalculate();
    this._persistSave();
  }

  // ---- Pricing ----
  recalculate() {
    const tier = this._tier();
    const pct = Number(tier.discount) || 0;
    if (this.state.tier === 'single') { this.priceEl.hidden = true; if (this.atcBtn) this.atcBtn.disabled = true; return; }

    // EVERY assigned slot, at any index — deliberately NOT windowed by tier capacity. _renderSlots
    // draws every assigned slot (only the EMPTY ones are capped), and _paidFilledCount /
    // _freeFilledCount already count unbounded, so windowing here made recalculate the odd one out:
    // prints the shopper could see were invisible to the CTA. That happens whenever an in-cart print
    // holds a low slot index while a newer print sits beyond capacity — e.g. two cart prints that the
    // price rank now draws in the FREE column still occupy paid slots 0-1, pushing two freshly added
    // prints to 2-3. The CTA then saw only the in-cart pair, concluded there was nothing new to add,
    // and sat disabled on "Add your free prints" with two addable prints on screen.
    const paidSlots = this.state.paidSlots.filter((s) => s && s.assigned);
    const freeSlots = this.state.freeSlots.filter((s) => s && s.assigned);

    // A print that has been added to the cart still lives in its state slot, and _cartBundle()
    // counts it again — summing both DOUBLED the pack total the moment you hit add-to-cart. The
    // top price block must therefore value only the slots NOT yet in the cart and take everything
    // else from the cart lines (the same rule _paidFilledCount/_freeFilledCount already use).
    // paidSlots/freeSlots themselves stay complete — the CTA block below needs the in-cart ones.
    const paidNotInCart = paidSlots.filter((s) => !this._inCart(s));
    const freeNotInCart = freeSlots.filter((s) => !this._inCart(s));
    const paidOriginal = paidNotInCart.reduce((sum, s) => sum + this.slotPrice(s), 0);
    const freeValue = freeNotInCart.reduce((sum, s) => sum + this.slotPrice(s), 0);
    // Pre-sale twins of the sums above, over the SAME de-duped slot lists. Identical to them
    // unless something is on sale, so a pack with no marked-down prints produces exactly the
    // numbers it did before.
    const paidWas = paidNotInCart.reduce((sum, s) => sum + this.slotWasPrice(s), 0);
    const freeWas = freeNotInCart.reduce((sum, s) => sum + this.slotWasPrice(s), 0);
    // The pack discount "unlocks" once the pack's paid count is reached — counting the prints being
    // added (slots not yet in cart) PLUS those already in the cart, so the CTA quotes the DISCOUNTED
    // price when the pack is completed by an already-in-cart print (feedback). Explicit !inCart filter
    // so it counts correctly regardless of whether in-cart prints are kept as assigned slots.
    const unlockedPct = this._unlockedDiscountPct(); // the discount the filled paid prints have actually earned

    // ---- Top price block (item 1): ALWAYS the projected COMPLETE pack —
    // actual price of filled prints (slots + this bundle's cart prints) + the cheapest
    // print for each empty slot, with the pack discount always applied. "From" is shown
    // while any slot is empty (estimate); dropped once the pack is complete (exact total). ----
    const cb = this._cartBundle();
    // Empty slots are estimated at the PRE-SALE price. A shopper who hasn't chosen a print yet
    // shouldn't be quoted the landing product's markdown — the print they eventually pick may not
    // be on sale at all. This keeps the default (empty) pack price identical on every PDP, on sale
    // or not; the sale only starts moving the numbers once a marked-down print is actually added,
    // exactly like adding a cheaper non-A-ratio print does. Used on BOTH sides of the sum below,
    // so it cancels out of SAVE for empty slots.
    const cheapestWas = this._cheapestWasPrice();
    const filledPaidValue = paidOriginal + cb.paidValue;
    const filledPaidCount = paidNotInCart.length + cb.paidCount;
    const emptyPaid = Math.max(0, tier.paid - filledPaidCount);
    const filledFreeValue = freeValue + cb.freeValue;
    const filledFreeCount = freeNotInCart.length + cb.freeCount;
    const emptyFree = Math.max(0, tier.free - filledFreeCount);

    const estPaid = filledPaidValue + cheapestWas * emptyPaid;       // paid portion (what's charged, pre-discount)
    // A free print is a MEDIUM print — estimate empty free slots at the medium-size value
    // (not the cheapest/A4), so the struck price + SAVE reflect its true worth (item 4).
    const freeStruck = filledFreeValue + this._freePrintValue() * emptyFree; // free prints' value at SALE prices (diagnostics only)
    // The struck total, at PRE-SALE prices, so a markdown makes the advertised saving BIGGER.
    // Pricing it at sale prices (as it used to) meant a sale quietly SHRANK the SAVE badge even
    // though the customer paid less — that was the core defect. Note the only difference from
    // estPaid above is the FILLED prints: empty slots use `cheapestWas` on both sides, so an
    // untouched pack shows the same numbers on every PDP.
    const estPaidWas = paidWas + cb.paidWasValue + cheapestWas * emptyPaid;
    const freeStruckWas = freeWas + cb.freeWasValue + this._freePrintWasValue() * emptyFree;
    // Framing service (item 2): a flat, non-discounted per-print fee embedded in the totals.
    // Added equally to struck + sale so SAVE (discount + free value) is unchanged.
    const framingFee = this.state.removeFraming ? 0 : (Number(this._framingSvc && this._framingSvc.price) || 0);
    const framingTotal = framingFee * this._framingEligibleSlots().length;
    const original = estPaidWas + freeStruckWas + framingTotal;       // struck: pre-sale pack value incl free prints + framing
    // Frames on framed FREE prints (service removed) are paid separately — flat, like the fee.
    // Only `sale` gets them: `original` already holds the framed print's full value via freeStruckWas,
    // so SAVE (original − sale) self-corrects to the free unframed-print value.
    const freeFrames = this._freeFrameAddon();
    const sale = Math.round(estPaid * (1 - pct / 100)) + framingTotal + freeFrames; // discount applied to prints only, + framing + free-print frames
    const save = original - sale;
    const hasEmpty = emptyPaid > 0 || emptyFree > 0;

    // ---- TEMPORARY DIAGNOSTICS (remove once the pack total is confirmed) ----
    // Every input to the header price, so a wrong total can be traced to its source.
    try {
      const money = (c) => (c / 100).toFixed(2);
      const dump = (s, i) => ({
        i, id: s.product && s.product.id, title: s.product && s.product.title,
        size: s.size, frame: s.frame, price: money(this.slotPrice(s)), inCart: this._inCart(s),
      });
      console.log('[BB ' + BB_BUILD + '] recalculate —', this.state.tier,
        '| tier.paid=' + tier.paid, 'tier.free=' + tier.free, 'discount=' + pct + '%');
      console.log('[BB] paid slots (state):', paidSlots.map(dump));
      console.log('[BB] free slots (state):', freeSlots.map(dump));
      console.log('[BB] NOT in cart -> paid:', paidNotInCart.length, 'free:', freeNotInCart.length,
        '| paidOriginal=' + money(paidOriginal), 'freeValue=' + money(freeValue),
        '| paidWas=' + money(paidWas), 'freeWas=' + money(freeWas));
      console.log('[BB] _cartProductIds:', [...this._cartProductIds]);
      const rankedFree = this._rankPackFree();
      console.log('[BB] _cartItems:', (this._cartItems || []).map((l) => ({
        product_id: l.product_id, type: l.type, price: money(l.price),
        // bundleFree = the FROZEN add-to-cart property (no longer used for display);
        // rankedFree = what the price rank says, i.e. what checkout will actually zero.
        bundleFree: l.props && l.props._bundle_free, rankedFree: rankedFree.has(l),
      })));
      console.log('[BB] _cartBundle():', {
        paidCount: cb.paidCount, paidValue: money(cb.paidValue),
        freeCount: cb.freeCount, freeValue: money(cb.freeValue),
        paidWasValue: money(cb.paidWasValue), freeWasValue: money(cb.freeWasValue),
      });
      console.log('[BB] MATH:', {
        filledPaidValue: money(filledPaidValue), filledPaidCount, emptyPaid,
        cheapest: money(this._cheapestPrice()), cheapestWas: money(cheapestWas),
        estPaid: money(estPaid), estPaidWas: money(estPaidWas),
        filledFreeValue: money(filledFreeValue), filledFreeCount, emptyFree,
        freePrintValue: money(this._freePrintValue()), freeStruck: money(freeStruck),
        freePrintWasValue: money(this._freePrintWasValue()), freeStruckWas: money(freeStruckWas),
        framingTotal: money(framingTotal), freeFrames: money(freeFrames),
      });
      console.log('[BB] RESULT: compare=' + money(original), 'sale=' + money(sale), 'save=' + money(save),
        '| expected sale = estPaid*' + (1 - pct / 100).toFixed(2) + ' + framing + freeFrames =',
        money(Math.round(estPaid * (1 - pct / 100)) + framingTotal + freeFrames));
    } catch (e) { console.warn('[BB] diagnostics failed', e); }
    // ---- END TEMPORARY DIAGNOSTICS ----

    if (estPaid > 0) {
      if (save > 0) {
        this.badgeEl.innerHTML = 'SAVE ' + formatMoney(save)
          + '<span class="bundle-builder__save-info" tabindex="0" role="button" aria-label="How savings are calculated">'
          + ICON_INFO
          + '<span class="bundle-builder__save-tip" role="tooltip">' + SAVE_TOOLTIP + '</span></span>';
        const info = this.badgeEl.querySelector('.bundle-builder__save-info');
        if (info) info.addEventListener('click', (e) => { e.stopPropagation(); info.classList.toggle('is-open'); });
        this.badgeEl.style.display = '';
        this.compareEl.textContent = formatMoney(original);
      } else {
        this.badgeEl.innerHTML = '';
        this.badgeEl.style.display = 'none';
        this.compareEl.textContent = '';
      }
      if (this.fromEl) this.fromEl.hidden = !hasEmpty; // "From" only while estimating empty slots
      this.saleEl.textContent = formatMoney(sale);
      this.priceEl.hidden = false;
    } else {
      this.priceEl.hidden = true;
    }

    // ---- CTA (copy + price adapt to what's already in the cart) ----
    if (this.atcBtn) {
      const label = this.atcBtn.querySelector('.bundle-builder__atc-label');
      const allAssigned = paidSlots.concat(freeSlots);
      const notInCart = allAssigned.filter((s) => !this._inCart(s));
      const inCartCount = allAssigned.length - notInCart.length;
      // What the customer pays now = the paid prints being added (free prints are £0).
      const addOriginal = paidSlots.filter((s) => !this._inCart(s)).reduce((sum, s) => sum + this.slotPrice(s), 0);
      // Framing service embedded in the CTA total too (item 2) — flat fee for the eligible
      // framed prints being added now (already excludes in-cart), 0 when "removed".
      const framingAdd = (this.state.removeFraming ? 0 : (Number(this._framingSvc && this._framingSvc.price) || 0)) * this._framingEligibleSlots().length;
      const frameAddon = this._freeFrameAddon(); // frames on framed free prints being added (excludes in-cart)
      const addSale = Math.round(addOriginal * (1 - unlockedPct / 100)) + framingAdd + frameAddon;
      // The struck (was-) price reflects the value of the free print(s) being ADDED now.
      // A free print already in the cart is excluded — its saving was already realised, so
      // the CTA shouldn't double-count it (item 4).
      // Struck at PRE-SALE prices, matching the top block. This also means a single marked-down
      // print now shows a struck price on the CTA even before any bundle discount is earned —
      // previously the sale was invisible there because struck === addSale.
      const addWas = paidSlots.filter((s) => !this._inCart(s)).reduce((sum, s) => sum + this.slotWasPrice(s), 0);
      const freeWasAdding = freeSlots.filter((s) => !this._inCart(s)).reduce((sum, s) => sum + this.slotWasPrice(s), 0);
      const struck = addWas + freeWasAdding + framingAdd;
      const priceHtml = (struck > addSale)
        ? '<span class="bundle-builder__atc-compare">' + formatMoney(struck) + '</span> ' + formatMoney(addSale)
        : formatMoney(addSale);
      const packNum = this._packNum();
      const nextUp = this._nextTierUp();               // next tier's discount for the upsell copy

      let disabled = false;
      if (allAssigned.length === 0) {                 // (a) nothing chosen → unlock the entry discount
        disabled = true;
        const d = nextUp ? (Number(nextUp.discount) || 0) : pct;
        // Zero-discount offer (sale mode): sell the free prints, never "unlock 0% off".
        if (label) label.textContent = d === 0 ? this._zeroCopy('zeroCta', 'Add prints to unlock free prints') : 'Add prints to unlock ' + d + '% off';
      } else if (notInCart.length === 0) {            // everything already in the cart
        disabled = true;
        if (label) {
          const target = this._nextAddTarget(); // the next empty slot the shopper would fill
          if (target && target.kind === 'free') {
            label.textContent = 'Add your free prints';                 // (d) next slot is a free print
          } else if (nextUp) {
            const dUp = Number(nextUp.discount) || 0; // (e) next slot is paid
            label.textContent = dUp === 0 ? this._zeroCopy('zeroCta', 'Add prints to unlock free prints') : 'Add prints to unlock ' + dUp + '% off';
          } else {
            label.textContent = packNum + ' PACK IN CART'; // top tier — nothing more to add
          }
        }
      } else if (inCartCount === 0) {                 // (b) nothing in cart yet
        if (label) label.innerHTML = 'ADD TO CART — ' + priceHtml;
      } else {                                        // (c) some in cart, adding the rest
        const y = notInCart.length;
        // (ci) if every print still to add is a FREE print, say so ("ADD Y FREE PRINT(S)…").
        const addingPaid = paidSlots.filter((s) => !this._inCart(s)).length;
        const freeWord = addingPaid === 0 ? ' FREE' : '';
        // A free-only add is £0 — drop the price so the sticky CTA stays one line on mobile
        // (feedback) — unless it carries a paid frame (framed free print, service removed).
        if (label) {
          const base = 'ADD ' + y + freeWord + ' PRINT' + (y > 1 ? 'S' : '') + ' TO CART';
          label.innerHTML = (addingPaid === 0 && frameAddon === 0) ? base : base + ' · ' + priceHtml;
        }
      }
      // Pre-built bundle mode: keep ATC locked while the prefill is still resolving —
      // each mid-flight add re-runs recalculate, which would otherwise enable the CTA
      // on a partial pack (QA: a 1-print "bundle" could be ordered mid-load).
      this.atcBtn.disabled = disabled || !!this._prefillLoading;
    }

    // Keep the canvas upgrade price (item 7) and the framing-service block (item PDP-1) in sync.
    this._refreshCanvasPrice();
    this._renderFraming();
    this._updateCanvasAvailability(); // disable Canvas if a chosen print has no canvas (item 13)
  }

  // ---- Print Selector ----
  _ensureSelector() {
    if (this._selector.built) return;
    // Inject on all viewports: desktop = left-column panel, mobile = bottom drawer (CSS).
    const column = document.querySelector('.product-information__media')
      || document.querySelector('.product-information__grid')
      || this.closest('.shopify-section')
      || this.parentElement;
    if (!column) return;

    const panel = document.createElement('div');
    panel.className = 'bundle-print-selector';
    panel.dataset.cols = String(this._selector.layout);
    panel.innerHTML =
      '<div class="bundle-print-selector__top">' +
        '<p class="bundle-print-selector__title"><span class="bundle-builder__section-num">2</span>Choose Your Product</p>' +
        '<p class="bundle-print-selector__header"></p>' +
        '<button type="button" class="bundle-print-selector__close" aria-label="Done">Done</button>' +
      '</div>' +
      '<div class="bundle-print-selector__progress">' +
        '<button type="button" class="bundle-print-selector__progress-toggle" aria-expanded="false">' +
          '<span class="bundle-print-selector__caret" aria-hidden="true">' + ICON_CARET + '</span>' +
          '<span class="bundle-print-selector__progress-label"></span>' +
        '</button>' +
        '<div class="bundle-print-selector__progress-thumbs" hidden></div>' +
      '</div>' +
      '<div class="bundle-print-selector__controls">' +
        '<button type="button" class="bundle-print-selector__scroll-prev" aria-label="Back to start" hidden>' + ICON_CHEVRON_LEFT + '</button>' +
        '<div class="bundle-print-selector__scroller">' +
          '<div class="bundle-print-selector__row" role="group" aria-label="Filter prints"></div>' +
        '</div>' +
        '<button type="button" class="bundle-print-selector__scroll-next" aria-label="Show more filters" hidden>' + ICON_CHEVRON + '</button>' +
        '<button type="button" class="bundle-print-selector__search-toggle" aria-label="Search prints">' + ICON_SEARCH + '</button>' +
        '<button type="button" class="bundle-print-selector__recent" aria-pressed="false" hidden>' +
          '<span class="bundle-print-selector__recent-icon" aria-hidden="true">' + ICON_CLOCK + '</span>' +
          '<span class="bundle-print-selector__recent-label">Recently Viewed</span>' +
          '<span class="bundle-print-selector__recent-x" aria-hidden="true">' + ICON_CLOSE + '</span>' +
        '</button>' +
        '<div class="bundle-print-selector__layout" role="group" aria-label="Prints per row">' +
          '<button type="button" class="bundle-print-selector__layout-btn" data-cols="2" aria-label="Two prints per row" aria-pressed="false">' + ICON_GRID2 + '</button>' +
          '<button type="button" class="bundle-print-selector__layout-btn" data-cols="4" aria-label="Four prints per row" aria-pressed="false">' + ICON_GRID4 + '</button>' +
        '</div>' +
      '</div>' +
      '<div class="bundle-print-selector__search-wrap">' +
        '<input type="search" class="bundle-print-selector__search" placeholder="Search print name or artist name" aria-label="Search prints by name or artist" hidden />' +
      '</div>' +
      '<div class="bundle-print-selector__active" role="group" aria-label="Active filters" hidden></div>' +
      '<div class="bundle-print-selector__grid" role="list"></div>';
    column.appendChild(panel);

    this._selEls = {
      panel,
      header: panel.querySelector('.bundle-print-selector__header'),
      row: panel.querySelector('.bundle-print-selector__row'),
      scroller: panel.querySelector('.bundle-print-selector__scroller'),
      scrollNext: panel.querySelector('.bundle-print-selector__scroll-next'),
      scrollPrev: panel.querySelector('.bundle-print-selector__scroll-prev'),
      layout: panel.querySelector('.bundle-print-selector__layout'),
      search: panel.querySelector('.bundle-print-selector__search'),
      searchToggle: panel.querySelector('.bundle-print-selector__search-toggle'),
      recent: panel.querySelector('.bundle-print-selector__recent'),
      grid: panel.querySelector('.bundle-print-selector__grid'),
      active: panel.querySelector('.bundle-print-selector__active'),
      progressToggle: panel.querySelector('.bundle-print-selector__progress-toggle'),
      progressLabel: panel.querySelector('.bundle-print-selector__progress-label'),
      progressThumbs: panel.querySelector('.bundle-print-selector__progress-thumbs'),
    };
    panel.querySelector('.bundle-print-selector__close').addEventListener('click', () => this._closeSelectorDrawer());
    // Bundle-progress dropdown (mobile): expand to see current slots as thumbnails.
    this._selEls.progressToggle.addEventListener('click', () => {
      const open = this._selEls.progressThumbs.hidden;
      this._selEls.progressThumbs.hidden = !open;
      this._selEls.progressToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Layout toggle (2 / 4 per row), persisted.
    this._selEls.layout.querySelectorAll('.bundle-print-selector__layout-btn').forEach((b) => {
      b.addEventListener('click', () => this._setLayout(parseInt(b.dataset.cols, 10)));
    });
    this._setLayout(this._selector.layout, true);

    // "Recently Viewed" toggle chip (moved out of the pill row). Click toggles the view;
    // the × (shown when active) returns to All prints. Hidden when there's no history.
    if (this._selEls.recent) {
      this._selEls.recent.hidden = !getRecent().length;
      this._selEls.recent.addEventListener('click', (e) => {
        if (e.target.closest('.bundle-print-selector__recent-x')) { this._setView('all'); return; }
        this._setView(this._selector.view === 'recent' ? 'all' : 'recent');
      });
      this._updateRecentChip();
    }

    // Horizontal scroll arrows for the pill row: › (right) scrolls forward; once
    // scrolled, a ‹ (left) arrow appears on the LEFT to scroll back.
    this._selEls.scrollNext.addEventListener('click', () => this._scrollPills(1));
    this._selEls.scrollPrev.addEventListener('click', () => this._scrollPills(-1));
    this._selEls.scroller.addEventListener('scroll', () => this._updateScrollArrow());
    this._onResize = () => this._updateScrollArrow();
    window.addEventListener('resize', this._onResize);
    // The controls row changes width AFTER this one-shot check: the Recently Viewed chip is
    // inserted by the drool:recent-prints-updated handler (~138px) and the facet row is rebuilt
    // from Boost, so the pills can start fitting and end up clipped with the arrows still hidden.
    // Recalculate on any real size change, same as the swatch/tier arrows.
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => this._updateScrollArrow());
      ro.observe(this._selEls.scroller);
      ro.observe(this._selEls.row);
    }

    // Collapsible search (R1.5.9b): icon expands the input.
    // Collapsible search (item 24): the icon toggles the input open/closed. Prevent the icon's
    // mousedown from blurring the focused input first — otherwise the blur handler hides the input
    // and the click then toggles it back open, so clicking to close never worked.
    this._selEls.searchToggle.addEventListener('mousedown', (e) => e.preventDefault());
    this._selEls.searchToggle.addEventListener('click', () => {
      const s = this._selEls.search;
      s.hidden = !s.hidden;
      if (!s.hidden) s.focus();
      else s.blur();
    });
    let t;
    this._selEls.search.addEventListener('input', (e) => {
      clearTimeout(t);
      const q = e.target.value.trim();
      t = setTimeout(() => this._search(q), 250);
    });
    this._selEls.search.addEventListener('blur', () => {
      if (!this._selEls.search.value.trim()) this._selEls.search.hidden = true;
    });

    // Close an open facet dropdown on outside click (but keep it open for multi-select
    // while clicking options inside it).
    document.addEventListener('click', (e) => {
      if (this._selector.openDropdown
        && !e.target.closest('.bundle-print-selector__facet')
        && !e.target.closest('.bundle-print-selector__dropdown')) this._closeDropdown();
    });
    // The pill row scrolls horizontally (clips overflow), so close any open dropdown on scroll.
    this._selEls.scroller.addEventListener('scroll', () => this._closeDropdown());
    window.addEventListener('scroll', () => this._closeDropdown(), { passive: true });

    this._selector.built = true;
    this._buildFilterRow();
    this._loadSelector(false);
    this._buildFacetTree(); // refine facet values live from Boost (async, falls back silently)
  }

  // Build the facet-dropdown pill row. Quicklinks are gone: "All prints" is the default,
  // "Recently Viewed" is now a chip in the controls row (items 5-7).
  _buildFilterRow() {
    const row = this._selEls.row;
    row.innerHTML = '';
    this._pillOffset = 0; // reset the horizontal position on rebuild
    row.style.transform = '';
    // Facet dropdown pills (Medium / Style / Colour / Orientation / Theme).
    this._selector.facetDefs.forEach((f) => {
      // A facet with nothing to choose from renders as a dead pill — skip it. Only reachable for
      // Discount %, whose values arrive with Boost's filter tree (or not at all, if it fails).
      if (!f.values || !f.values.length) return;
      const wrap = document.createElement('div');
      wrap.className = 'bundle-print-selector__facet';
      const count = (this._selector.facets[f.key] && this._selector.facets[f.key].size) || 0;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-print-selector__pill bundle-print-selector__facet-btn' + (count ? ' is-active' : '');
      btn.dataset.facet = f.key;
      btn.setAttribute('aria-expanded', 'false');
      btn.innerHTML =
        '<span class="bundle-print-selector__pill-label">' + f.label + '</span>' +
        '<span class="bundle-print-selector__caret" aria-hidden="true">' + ICON_CARET + '</span>';
      btn.addEventListener('click', (e) => { e.stopPropagation(); this._toggleDropdown(f, wrap, btn); });
      wrap.appendChild(btn);
      row.appendChild(wrap);
    });
    this._applyViewDisabledState();
    this._renderActiveFilters();
    this._updateScrollArrow();
  }

  // Recently Viewed has no tag data → facets can't apply; disable them while it's active.
  _applyViewDisabledState() {
    const recent = this._selector.view === 'recent';
    this._selEls.row.querySelectorAll('.bundle-print-selector__facet-btn').forEach((b) => {
      b.disabled = recent;
      b.classList.toggle('is-disabled', recent);
    });
  }

  _setView(view) {
    this._selector.view = view;
    this._updateRecentChip();
    this._applyViewDisabledState();
    this._closeDropdown();
    this._loadSelector(false);
  }

  // Reflect the active state on the Recently Viewed chip (black pill + × when active).
  _updateRecentChip() {
    const chip = this._selEls && this._selEls.recent;
    if (!chip) return;
    chip.setAttribute('aria-pressed', this._selector.view === 'recent' ? 'true' : 'false');
  }

  _toggleDropdown(f, wrap, btn) {
    if (this._selector.openDropdown && this._selector.openDropdown.facet === f.key) { this._closeDropdown(); return; }
    this._closeDropdown();
    const dd = document.createElement('div');
    dd.className = 'bundle-print-selector__dropdown' + (f.swatch ? ' is-swatch' : '');
    const selected = this._selector.facets[f.key] || new Set();
    f.values.forEach((val) => {
      const full = f.prefix + val;
      const opt = document.createElement('label');
      opt.className = 'bundle-print-selector__opt';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = selected.has(full);
      cb.addEventListener('change', () => this._toggleFacetValue(f.key, full, cb.checked));
      opt.appendChild(cb);
      if (f.swatch) {
        const sw = document.createElement('span');
        sw.className = 'bundle-print-selector__swatch';
        sw.style.background = COLOUR_SWATCHES[val] || '#ccc';
        opt.appendChild(sw);
      }
      const lab = document.createElement('span');
      lab.textContent = this._facetLabel(f, full, val);
      opt.appendChild(lab);
      dd.appendChild(opt);
    });
    // Attach to the panel (not the clipping scroller) and position under the pill.
    const panel = this._selEls.panel;
    panel.appendChild(dd);
    const pr = panel.getBoundingClientRect();
    const br = btn.getBoundingClientRect();
    dd.style.top = (br.bottom - pr.top + 4) + 'px';
    dd.style.left = Math.max(0, br.left - pr.left) + 'px';
    btn.setAttribute('aria-expanded', 'true');
    this._selector.openDropdown = { facet: f.key, el: dd, btn };
  }

  // Display label for a facet value. Falls back to stripping the prefix; a facet may carry a
  // `labels` map (raw tag → friendly name) for mixed-family facets like Size (item 3).
  _facetLabel(f, full, val) {
    if (f.labels && f.labels[full]) return f.labels[full];
    return val != null ? val : (full.replace(f.prefix, '') || full);
  }

  _closeDropdown() {
    const od = this._selector.openDropdown;
    if (!od) return;
    od.el.remove();
    od.btn.setAttribute('aria-expanded', 'false');
    this._selector.openDropdown = null;
  }

  _toggleFacetValue(key, full, on) {
    let set = this._selector.facets[key];
    if (!set) { set = new Set(); this._selector.facets[key] = set; }
    if (on) set.add(full); else set.delete(full);
    if (!set.size) delete this._selector.facets[key];
    const btn = this._selEls.row.querySelector('.bundle-print-selector__facet-btn[data-facet="' + key + '"]');
    if (btn) btn.classList.toggle('is-active', !!(this._selector.facets[key] && this._selector.facets[key].size));
    this._renderActiveFilters();
    if (this._selector.view === 'recent') this._setView('best-sellers'); // facets need a tagged scope
    else this._loadSelector(false);
  }

  // Show the currently-applied facet values as removable chips (PLP-style), with a
  // "Clear all" affordance, in a row above the grid.
  _renderActiveFilters() {
    const box = this._selEls && this._selEls.active;
    if (!box) return;
    box.innerHTML = '';
    const defs = this._selector.facetDefs || [];
    let any = false;
    defs.forEach((f) => {
      const set = this._selector.facets[f.key];
      if (!set || !set.size) return;
      set.forEach((full) => {
        any = true;
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'bundle-print-selector__chip';
        const lbl = this._facetLabel(f, full);
        chip.innerHTML = '<span>' + lbl + '</span>' + ICON_CLOSE;
        chip.setAttribute('aria-label', 'Remove filter ' + lbl);
        chip.addEventListener('click', () => {
          this._toggleFacetValue(f.key, full, false);
          if (this._selector.openDropdown && this._selector.openDropdown.facet === f.key) this._closeDropdown();
        });
        box.appendChild(chip);
      });
    });
    if (any) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'bundle-print-selector__clear';
      clear.textContent = 'Clear all';
      clear.addEventListener('click', () => this._clearFilters());
      box.appendChild(clear);
    }
    box.hidden = !any;
  }

  _clearFilters() {
    this._selector.facets = {};
    this._closeDropdown();
    this._selEls.row.querySelectorAll('.bundle-print-selector__facet-btn').forEach((b) => b.classList.remove('is-active'));
    this._renderActiveFilters();
    this._loadSelector(false);
  }

  _setLayout(n, silent) {
    this._selector.layout = n;
    if (this._selEls && this._selEls.panel) this._selEls.panel.dataset.cols = String(n);
    if (this._selEls && this._selEls.layout) {
      this._selEls.layout.querySelectorAll('.bundle-print-selector__layout-btn').forEach((b) =>
        b.setAttribute('aria-pressed', parseInt(b.dataset.cols, 10) === n ? 'true' : 'false'));
    }
    if (!silent) setLayoutPref(n);
  }

  // Scroll the filter pills one WHOLE pill at a time (snap to pill boundaries) so each arrow
  // press cleanly reveals the next/previous filter instead of stopping mid-pill.
  _pillMax() {
    const sc = this._selEls && this._selEls.scroller;
    const row = sc && sc.firstElementChild;
    return row ? Math.max(0, row.scrollWidth - sc.clientWidth) : 0;
  }

  _scrollPills(dir) {
    const sc = this._selEls && this._selEls.scroller;
    const row = sc && sc.firstElementChild;
    if (!sc || !row) return;
    const max = this._pillMax();
    const cur = this._pillOffset || 0;
    const edges = [...row.children].map((el) => el.offsetLeft); // layout x of each pill (transform-independent)
    let next;
    if (dir > 0) {
      next = edges.find((e) => e > cur + 1);        // next pill boundary to the right
      if (next == null) next = max;
    } else {
      const before = edges.filter((e) => e < cur - 1);
      next = before.length ? before[before.length - 1] : 0; // previous pill boundary
    }
    this._pillOffset = Math.max(0, Math.min(max, next));
    row.style.transform = 'translateX(' + (-this._pillOffset) + 'px)';
    this._updateScrollArrow();
  }

  _updateScrollArrow() {
    const next = this._selEls && this._selEls.scrollNext;
    const prev = this._selEls && this._selEls.scrollPrev;
    if (!next || !prev) return;
    const max = this._pillMax();
    const cur = this._pillOffset || 0;
    if (max <= 4) { next.hidden = true; prev.hidden = true; return; }
    prev.hidden = cur <= 4;          // left arrow appears once moved
    next.hidden = cur >= max - 4;    // right arrow hides at the end
  }

  // Refine facet dropdown values from Boost's live filter tree (keeps in sync with the PLP,
  // respects Boost's excluded values + ordering). Falls back to FACET_DEFS on any failure.
  // The Boost collection the grid is currently reading, applied to `p`. Shared by the product
  // query and the facet tree so the two can never describe different collections: the filter row
  // must offer the values that exist in the collection the grid is actually showing. They were
  // separate, and the tree always used the PAPER scope — so in canvas mode every facet listed the
  // prints collection's values. Discount % made it obvious (2 values in canvas-prints, 0 in
  // prints, so the pill never rendered on a canvas PDP), but it applied to all of them.
  // Returns a key identifying the scope, for the per-scope cache in _buildFacetTree.
  _applyBoostScope(p) {
    if (this._gridMaterialIsCanvas()) {
      if (this._boost.canvasScope) { p.set('collection_scope', this._boost.canvasScope); return 'c:' + this._boost.canvasScope; }
      // No canvas collection configured: fall back to a product-type filter so the grid can
      // never mix materials. The facet tree accepts the same param, so both stay in step.
      p.append('pf_pt_product_type[]', 'Canvas Print');
      return 'c:type';
    }
    if (this._boost.scope) { p.set('collection_scope', this._boost.scope); return 'p:' + this._boost.scope; }
    return 'p:';
  }

  async _buildFacetTree() {
    try {
      const p = new URLSearchParams();
      p.set('shop', this._boost.shop);
      const scopeKey = this._applyBoostScope(p);
      // Switching material re-runs this; don't refetch a tree already built for that scope.
      if (!this._facetTreeCache) this._facetTreeCache = {};
      if (this._facetTreeCache[scopeKey]) {
        this._selector.facetDefs = this._facetTreeCache[scopeKey];
        this._buildFilterRow();
        return;
      }
      p.set('page', '1'); p.set('limit', '1');
      p.set('build_filter_tree', 'true'); p.set('product_available', 'true');
      // no-store because this tree CHANGES WITH THE SALE. The URL and params are identical on
      // every load, so a cached copy is indistinguishable from a live one — which is how the
      // Discount % facet stayed missing after a markdown was switched back on: the pill correctly
      // disappeared when the discount ended, then the browser kept serving that same "no discount"
      // tree once it returned. Matches the cart read at _removeInCartSlot.
      const d = await (await fetch(BOOST_FILTER_URL + '?' + p.toString(), { cache: 'no-store' })).json();
      const opts = (d.filter && d.filter.options) || [];
      const byId = {};
      opts.forEach((o) => { byId[o.filterOptionId] = o; });
      const defs = FACET_DEFS.map((f) => {
        const o = byId[f.key];
        if (!o) return f;
        const prefix = String(o.prefix || f.prefix).replace(/\\/g, '');
        // Size is a multi_level_tag facet mixing two tag families (Size-* buckets + Dimensions-*
        // dimensions). Use the FULL manualValues set so the selector offers everything the PLP
        // does (item 3), keyed by the raw tag with a clean display label from displayName.
        if (f.key === 'pf_mlt_size' && Array.isArray(o.manualValues) && o.manualValues.length) {
          const labels = {};
          const values = o.manualValues.map((v) => { labels[v.tag] = v.displayName || v.tag; return v.tag; });
          return Object.assign({}, f, { prefix: '', values, labels });
        }
        // Most facets carry values on `values[].key`; fall back to manualValues[].tag.
        let raw = Array.isArray(o.values) ? o.values.map((v) => v && v.key).filter(Boolean) : [];
        if (!raw.length && Array.isArray(o.manualValues)) raw = o.manualValues.map((v) => v && v.tag).filter(Boolean);
        // Keep Boost's own display text where it gives one, keyed by the FULL tag the way
        // _facetLabel looks it up. Discount % needs this — its keys are bare numbers ("-20")
        // that read as nothing on their own. A facet Boost gives no labels for is unchanged.
        const labels = {};
        if (Array.isArray(o.values)) {
          o.values.forEach((v) => {
            if (!v || !v.key) return;
            const text = v.label || v.displayName || v.name;
            if (text) labels[v.key] = text;
          });
        }
        // Prefix-strip the tag family (e.g. "Medium2026-Photography" -> "Photography"), but only
        // where the prefix actually matches. A facet whose keys are bare — Discount % reports
        // "-20", not "<prefix>-20" — would otherwise have every value filtered away and be dropped
        // from the row entirely, silently. Fall back to the raw keys in that case, with the prefix
        // cleared so _toggleDropdown rebuilds the same tag it was given.
        const stripped = prefix ? raw.filter((k) => String(k).indexOf(prefix) === 0) : raw;
        const usePrefix = stripped.length ? prefix : '';
        const values = (stripped.length ? stripped : raw).map((k) => String(k).slice(usePrefix.length)).filter(Boolean);
        return Object.assign({}, f, {
          prefix: usePrefix,
          values: values.length ? values : f.values,
          swatch: o.displayType === 'swatch' || f.swatch,
          labels: Object.keys(labels).length ? labels : f.labels,
        });
      });
      this._facetTreeCache[scopeKey] = defs;
      this._selector.facetDefs = defs;
      this._buildFilterRow();
    } catch (e) { /* keep fallback FACET_DEFS */ }
  }

  // Mobile: the selector is a bottom-sheet drawer (no-op styling impact on desktop,
  // where the panel is the always-visible left column).
  _openSelectorDrawer() {
    this._ensureSelector();
    const panel = this._selEls && this._selEls.panel;
    if (!panel) return;
    if (window.matchMedia('(max-width: 749px)').matches) {
      // The drawer lives inside .mrk-product-info-sec, which sets position:relative +
      // z-index:3 — a stacking context. That caps the drawer's effective z-index below the
      // sticky store header (z-index:8), so the header half-covers the drawer's "Done"
      // button no matter how high the drawer's own z-index is. Re-parent to <body> on
      // mobile so the drawer sits ABOVE the header; moved back on close for desktop's
      // sticky column layout.
      if (panel.parentElement !== document.body) {
        this._selectorHome = panel.parentElement;
        document.body.appendChild(panel);
      }
      document.body.style.overflow = 'hidden';
    }
    panel.classList.add('is-open');
    // Re-sync each card's Added / "+ ADD" label to the current bundle on every open, so a card can't
    // stay "Added" after its print was removed from the bundle/cart (feedback).
    if (this._selector.mode !== 'browse') this._markCards();
    // Populate the yellow progress bar (label + thumbs) on open. _ensureSelector builds these empty;
    // without this they stay blank until the next recalculate — the "no message in the yellow bar"
    // mobile bug on first open (review #3).
    this._updateSelectorCounter();
  }
  _closeSelectorDrawer() {
    const panel = this._selEls && this._selEls.panel;
    if (panel) panel.classList.remove('is-open');
    document.body.style.overflow = '';
    // Return the drawer to its original column so desktop's sticky positioning still works.
    if (panel && this._selectorHome && panel.parentElement === document.body) {
      this._selectorHome.appendChild(panel);
      this._selectorHome = null;
    }
  }

  // Is the shared material currently Canvas?
  _materialIsCanvas() {
    return !!(this._canvasMat && this.state.material === this._canvasMat.title);
  }

  // Is the NEXT add going into a FREE slot? (Paid slots full, an unlocked free slot
  // still empty — the same rule that flips the button label to "ADD FREE PRINT".)
  _nextAddIsFree() {
    return !this._firstEmptyPaid() && !!this._firstEmptyFree();
  }

  // The material the GRID should show. Free prints are art-paper only, so while the
  // next add is a free print the grid shows prints even when Canvas is selected; it
  // returns to the selected material once the free slots are filled.
  _gridMaterialIsCanvas() {
    return this._materialIsCanvas() && !this._nextAddIsFree();
  }

  // Does this product/card belong to the grid's material? Uses `type` when present
  // (search results), otherwise the store-wide `<handle>-canvas` sibling convention
  // (Boost cards and recents carry no type).
  _matchesMaterial(p) {
    const isCanvas = p && p.type ? /canvas/i.test(p.type) : /-canvas$/i.test((p && p.handle) || '');
    return this._gridMaterialIsCanvas() ? isCanvas : !isCanvas;
  }

  // Build the Boost filter-API query for the current view + active facets.
  _boostParams(page) {
    const p = new URLSearchParams();
    p.set('shop', this._boost.shop);
    // The grid follows the material: paper → the prints collection scope, canvas → the canvas
    // collection scope. Shared with the facet tree via _applyBoostScope so the filter row always
    // describes the collection the grid is reading.
    this._applyBoostScope(p);
    p.set('page', String(page));
    p.set('limit', String(BOOST_PAGE_SIZE));
    p.set('sort', this._selector.view === 'best-sellers' ? 'best-selling' : 'manual');
    p.set('build_filter_tree', 'false');
    p.set('product_available', 'true');
    Object.keys(this._selector.facets).forEach((key) => {
      this._selector.facets[key].forEach((val) => p.append(key + '[]', val));
    });
    return p;
  }

  // Load (or append) selector results. Recently Viewed renders from localStorage.
  // A lightweight selector-card object for the product whose PDP we're on.
  _currentAsCard() {
    // The landing print is pinned into the grid without going through Boost, so derive its
    // badge from the real variant data we already hold rather than leaving it un-badged.
    const cv = this._cheapestVariant();
    const salePct = cv ? markdownPct(cv.price, wasPriceOf(cv)) : 0;
    return { id: this.product.id, handle: this.product.handle, title: this.product.title, vendor: this.product.vendor, image: this.product.image, artImage: this.product.artImage, ratio: this.product.ratio, salePct };
  }

  // Pin the landing product to the front of the grid so, when you arrive on a PDP
  // (e.g. via a recommendation) with a bundle already in progress, the print you
  // just clicked is the first thing you see and can add. Deduped from the list.
  _pinCurrent(cards) {
    if (this.entryMode || !this.product || !this.product.handle) return cards;
    const cur = this._currentAsCard();
    if (!this._matchesMaterial(cur)) return cards; // the grid is material-scoped — don't pin the other material's product
    const rest = cards.filter((c) => String(c.id) !== String(cur.id) && c.handle !== cur.handle);
    // Capped at the page size so pinning never grows the grid past 48: when the landing
    // product isn't in the fetched page, the last fetched card is dropped instead.
    return [cur, ...rest].slice(0, BOOST_PAGE_SIZE);
  }

  // Recently-viewed cards for the selector grid. An entry written by an older build has no
  // vendor (so the card rendered without its "By <artist>" line) and may hold whatever image was
  // current when it was stored rather than the product's FIRST image. Both are backfilled once
  // per handle from /products/<handle>.js and persisted, so it costs one request the first time
  // and nothing afterwards — and it works however the product was reached (home, PLP or PDP).
  _recentCards() {
    return getRecent().map((p) => {
      const stale = Number(p.v) !== RECENT_V || !p.vendor || !p.image;
      const meta = stale ? this._recentMeta(p.handle) : null;
      return {
        id: p.id,
        handle: p.handle,
        title: p.title,
        vendor: p.vendor || (meta && meta.vendor) || null,
        image: (meta && meta.image) || p.image || null,
        ratio: p.imageW && p.imageH ? p.imageW + ' / ' + p.imageH : (p.ratio || null),
      };
    });
  }

  // One /products/<handle>.js lookup per handle, cached in memory AND written back to storage.
  // Returns null while in flight; the repaint below re-renders the grid once the data lands.
  _recentMeta(handle) {
    if (!handle) return null;
    if (!this._recentMetaCache) this._recentMetaCache = {};
    if (handle in this._recentMetaCache) return this._recentMetaCache[handle];
    this._recentMetaCache[handle] = null; // in flight
    fetch('/products/' + handle + '.js')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const meta = d
          ? { vendor: d.vendor || null, image: (d.images && d.images[0]) || d.featured_image || null }
          : { vendor: null, image: null };
        this._recentMetaCache[handle] = meta;
        upgradeRecent(handle, meta);
        this._recentRepaint();
      })
      .catch(() => { this._recentMetaCache[handle] = { vendor: null, image: null }; });
    return null;
  }

  // Collapse the repaints from a burst of backfills into one, so a grid of 24 recents doesn't
  // re-render 24 times as the lookups land.
  _recentRepaint() {
    if (this._recentRepaintQueued) return;
    this._recentRepaintQueued = true;
    requestAnimationFrame(() => {
      this._recentRepaintQueued = false;
      if (this._selector && this._selector.view === 'recent') this._loadSelector(false);
    });
  }

  async _loadSelector(append) {
    if (this._selector.view === 'recent') {
      this._renderCards(this._pinCurrent(this._recentCards().filter((c) => this._matchesMaterial(c))), { append: false });
      this._renderLoadMore(false);
      this._updateSelectorCounter();
      return;
    }
    if (this._selEls.search) this._selEls.search.value = '';
    if (!append) {
      this._selector.page = 1;
      this._selector.loadedCount = 0;
      this._selEls.grid.innerHTML = '<div class="bundle-print-selector__loading">Loading…</div>';
    }
    const page = this._selector.page;
    const reqId = ++this._selector.reqId;
    // Instant path: serve the first page from the per-session cache (perf feedback). Page 1 only,
    // keyed by view + facets. Pinning of the current PDP's print is applied at render, not cached.
    if (!append && page === 1) {
      const cached = this._selCacheGet();
      if (cached) {
        this._selector.total = cached.total || 0;
        this._selector.loadedCount = cached.products.length;
        this._renderCards(this._pinCurrent(cached.products), { append: false });
        this._renderLoadMore(cached.products.length > 0 && cached.products.length < this._selector.total);
        return;
      }
    }
    try {
      const d = await (await fetch(BOOST_FILTER_URL + '?' + this._boostParams(page).toString())).json();
      if (reqId !== this._selector.reqId) return; // a newer request superseded this one
      const products = (d.products || []).map(mapBoostProduct);
      this._selector.total = d.total_product || 0;
      this._selector.loadedCount = (append ? this._selector.loadedCount : 0) + products.length;
      if (!append && page === 1) this._selCacheSet(products, this._selector.total); // cache the first page for instant re-opens
      // Pin the landing product first on page 1; drop it from later pages so it
      // never appears twice as you "Load more".
      let list = products;
      if (!append) list = this._pinCurrent(products);
      else if (!this.entryMode && this.product && this.product.handle) {
        list = products.filter((c) => String(c.id) !== String(this.product.id) && c.handle !== this.product.handle);
      }
      this._renderCards(list, { append });
      this._renderLoadMore(products.length > 0 && this._selector.loadedCount < this._selector.total);
    } catch (e) {
      if (!append) this._selEls.grid.innerHTML = '<div class="bundle-print-selector__empty">Could not load prints.</div>';
    }
  }

  _loadMore() {
    this._selector.page = (this._selector.page || 1) + 1;
    this._loadSelector(true);
  }

  // ---- First-page selector cache (sessionStorage; perf — Boost's product call is ~1.5-1.7s) ----
  // Keyed by shop-scope + view + active facets so a filtered query never serves the wrong list.
  // Short TTL so a print that sells out / changes can't linger. sessionStorage (localStorage is
  // maxed on this store) — cleared when the tab closes.
  _selCacheKey() {
    const facets = {};
    Object.keys(this._selector.facets || {}).sort().forEach((k) => {
      facets[k] = [...this._selector.facets[k]].sort();
    });
    // Scope is material-resolved so a cached prints page can never serve the canvas grid.
    const scope = this._gridMaterialIsCanvas() ? 'canvas:' + (this._boost.canvasScope || 'type') : this._boost.scope;
    return SEL_CACHE_KEY + ':' + JSON.stringify({ scope, view: this._selector.view, facets });
  }
  _selCacheGet() {
    try {
      const raw = sessionStorage.getItem(this._selCacheKey());
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || !Array.isArray(obj.products)) return null;
      if (Date.now() - (obj.t || 0) > SEL_CACHE_TTL) { sessionStorage.removeItem(this._selCacheKey()); return null; }
      return obj;
    } catch (e) { return null; }
  }
  _selCacheSet(products, total) {
    try { sessionStorage.setItem(this._selCacheKey(), JSON.stringify({ products, total, t: Date.now() })); } catch (e) { /* quota / private mode — skip */ }
  }

  _renderLoadMore(hasMore) {
    const grid = this._selEls.grid;
    const existing = grid.querySelector('.bundle-print-selector__more');
    if (existing) existing.remove();
    if (!hasMore) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bundle-print-selector__more';
    btn.textContent = 'Load more';
    btn.addEventListener('click', () => this._loadMore());
    grid.appendChild(btn);
  }

  async _search(q) {
    if (!q) { this._loadSelector(false); return; }
    const grid = this._selEls.grid;
    const reqId = ++this._selector.reqId;
    grid.innerHTML = '<div class="bundle-print-selector__loading">Searching…</div>';
    try {
      const url = '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=20&resources[options][unavailable_products]=last';
      const r = await fetch(url);
      const d = await r.json();
      if (reqId !== this._selector.reqId) return;
      const items = ((d.resources && d.resources.results && d.resources.results.products) || [])
        // The grid is material-scoped — keep only the matching product type.
        .filter((p) => this._matchesMaterial(p));
      const products = items.map((p) => {
        const fi = p.featured_image;
        let ratio = null;
        if (fi && fi.aspect_ratio) ratio = String(fi.aspect_ratio);
        else if (fi && fi.width && fi.height) ratio = fi.width + ' / ' + fi.height;
        // suggest.json returns price_min / compare_at_price_min as market-correct strings
        // ("22.40"), and "0.00" when there's no markdown — markdownPct's `was > price` guard
        // handles both that and a compare_at stored equal to the price.
        const salePct = markdownPct(parseFloat(p.price_min) || 0, parseFloat(p.compare_at_price_min) || 0);
        return { id: p.id, handle: p.handle, title: p.title, vendor: p.vendor, image: (fi && fi.url) || p.image, ratio, salePct };
      });
      this._renderCards(products);
      this._renderLoadMore(false); // search results aren't paginated
    } catch (e) {
      grid.innerHTML = '<div class="bundle-print-selector__empty">Search failed.</div>';
    }
  }

  _renderCards(products, { append = false } = {}) {
    const grid = this._selEls.grid;
    const moreBtn = grid.querySelector('.bundle-print-selector__more');
    if (moreBtn) moreBtn.remove();
    if (!append) grid.innerHTML = '';
    if (!products.length && !append) { grid.innerHTML = '<div class="bundle-print-selector__empty">No prints found.</div>'; return; }
    products.forEach((p) => {
      const card = document.createElement('div');
      card.className = 'bundle-print-selector__card';
      card.dataset.productId = p.id;
      card.dataset.handle = p.handle || '';
      card.setAttribute('role', 'listitem');

      // Media (image + eye/expand + added overlay).
      const media = document.createElement('div');
      media.className = 'bundle-print-selector__card-media';
      const img = document.createElement('img');
      img.className = 'bundle-print-selector__card-img';
      img.loading = 'lazy';
      if (p.image) img.src = cdnThumb(p.image, 440); // 206x275 box -> 440w covers 2x DPR
      img.alt = p.title || ''; // uniform fixed-height box; image is centered + letterboxed
      media.appendChild(img);
      // Flash-sale badge. The percentage is DERIVED per product, never hardcoded: the sale
      // rotates between products, and on this store the same sale is a different percentage on
      // unframed vs framed variants (the markdown applies to the print, not the frame).
      if (p.salePct > 0) {
        const sb = document.createElement('span');
        sb.className = 'bundle-print-selector__sale-badge';
        sb.textContent = 'Extra ' + p.salePct + '% off';
        media.appendChild(sb);
      }
      // Expand → full-view modal (Figma: eye icon, bottom-right).
      const expand = document.createElement('button');
      expand.type = 'button';
      expand.className = 'bundle-print-selector__expand';
      expand.setAttribute('aria-label', 'View ' + (p.title || 'print') + ' larger');
      expand.innerHTML = ICON_EYE;
      expand.addEventListener('click', (e) => { e.stopPropagation(); this._openQuickView(p); });
      media.appendChild(expand);
      card.appendChild(media);

      const title = document.createElement('span');
      title.className = 'bundle-print-selector__card-title';
      title.textContent = p.title || '';
      card.appendChild(title);
      if (p.vendor) { const a = document.createElement('span'); a.className = 'bundle-print-selector__card-artist'; a.textContent = 'By ' + p.vendor; card.appendChild(a); }

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'bundle-print-selector__add';
      if (this._selector.mode === 'browse') {
        // Browse mode (single-view "View more"): pick a print → go to its product page.
        add.textContent = 'View print';
        const go = (e) => { if (e) e.stopPropagation(); if (!p.handle) return; this._stampViewMode(); window.location.href = '/products/' + p.handle; };
        add.addEventListener('click', go);
        media.style.cursor = 'pointer';
        media.addEventListener('click', (e) => { if (e.target.closest('.bundle-print-selector__expand')) return; go(e); });
      } else {
        add.textContent = '+ ADD';
        add.addEventListener('click', () => {
          if (add.dataset.added === 'true') { this._removeProductFromSlots(p); return; } // click an added card → remove it
          // Mobile: "+ ADD" opens the Quick ATC modal so the shopper picks size/frame before
          // the print lands in the pack. Desktop keeps the one-tap add straight to the slot.
          if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) { this._openQuickView(p); return; }
          this._addProduct(p, add);
        });
      }
      card.appendChild(add);
      grid.appendChild(card);
    });
    if (this._selector.mode !== 'browse') this._markCards(); // browse cards navigate, no slot state
  }

  // ---- Expand modal: full view of one print + Add to bundle (R1.5.7) ----
  _ensureExpand() {
    if (this._expandBuilt) return;
    this._expandBuilt = true;
    this.expandEl = this._expandNode || this.querySelector('.bundle-expand');
    if (!this.expandEl) return;
    this.expandEl.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this._closeExpand()));
    // Click anywhere outside the panel closes the modal (item 2).
    this.expandEl.addEventListener('click', (e) => { if (!e.target.closest('.bundle-expand__panel')) this._closeExpand(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.expandEl && !this.expandEl.hidden) this._closeExpand(); });
    this.expandEl.querySelector('.bundle-expand__nav--prev').addEventListener('click', () => this._carouselTo(this._carousel.index - 1));
    this.expandEl.querySelector('.bundle-expand__nav--next').addEventListener('click', () => this._carouselTo(this._carousel.index + 1));
  }
  _closeExpand() { if (this.expandEl) this.expandEl.hidden = true; }

  _carouselTo(i) {
    const c = this._carousel;
    if (!c || !c.count) return;
    c.index = Math.max(0, Math.min(c.count - 1, i));
    this.expandEl.querySelector('.bundle-expand__track').style.transform = 'translateX(' + (-c.index * 100) + '%)';
    [...this.expandEl.querySelectorAll('.bundle-expand__dot')].forEach((d, di) => d.setAttribute('aria-current', di === c.index ? 'true' : 'false'));
    this.expandEl.querySelector('.bundle-expand__nav--prev').hidden = c.index === 0;
    this.expandEl.querySelector('.bundle-expand__nav--next').hidden = c.index === c.count - 1;
  }

  _fillCarousel(images, alt) {
    const track = this.expandEl.querySelector('.bundle-expand__track');
    const dots = this.expandEl.querySelector('.bundle-expand__dots');
    track.innerHTML = ''; dots.innerHTML = '';
    const imgs = (images && images.length ? images : ['']).slice(0, 8);
    imgs.forEach((src, i) => {
      const slide = document.createElement('div');
      slide.className = 'bundle-expand__slide';
      const img = document.createElement('img');
      img.src = src; img.alt = alt || ''; img.loading = 'lazy';
      slide.appendChild(img);
      track.appendChild(slide);
      const dot = document.createElement('button');
      dot.type = 'button'; dot.className = 'bundle-expand__dot';
      dot.setAttribute('aria-label', 'Image ' + (i + 1));
      dot.addEventListener('click', () => this._carouselTo(i));
      dots.appendChild(dot);
    });
    dots.hidden = imgs.length < 2;
    this._carousel = { index: 0, count: imgs.length };
    this._carouselTo(0);
  }

  async _openExpand(p) {
    return;
    this._ensureExpand();
    const el = this.expandEl;
    if (!el) return;
    el.querySelector('.bundle-expand__title').textContent = p.title || '';
    el.querySelector('.bundle-expand__artist').textContent = p.vendor ? 'By ' + p.vendor : '';
    el.querySelector('.bundle-expand__bio-text').textContent = '';
    el.querySelector('.bundle-expand__readmore').hidden = true;
    this._fillCarousel([p.image].filter(Boolean), p.title);
    const addBtn = el.querySelector('.bundle-expand__add');
    addBtn.disabled = false;
    addBtn.textContent = 'Add to pack';
    addBtn.onclick = async () => { await this._addProduct(p, addBtn); this._closeExpand(); };
    el.hidden = false;
    try {
      const prod = await (await fetch('/products/' + p.handle + '.js')).json();
      el.querySelector('.bundle-expand__artist').textContent = prod.vendor ? 'By ' + prod.vendor : '';
      this._fillCarousel((prod.images || []).length ? prod.images : [prod.featured_image].filter(Boolean), prod.title);
      // Artist bio (falls back to the product description) with a "Read more" toggle.
      const bio = (prod.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      const textEl = el.querySelector('.bundle-expand__bio-text');
      const moreBtn = el.querySelector('.bundle-expand__readmore');
      const LIMIT = 180;
      if (bio.length > LIMIT) {
        const short = bio.slice(0, LIMIT).trim() + '…';
        textEl.textContent = short;
        moreBtn.hidden = false;
        moreBtn.textContent = 'Read more';
        moreBtn.onclick = () => {
          const expanded = moreBtn.textContent === 'Read less';
          textEl.textContent = expanded ? short : bio;
          moreBtn.textContent = expanded ? 'Read more' : 'Read less';
        };
      } else {
        textEl.textContent = bio;
        moreBtn.hidden = true;
      }
    } catch (e) { /* keep card image */ }
  }

  _firstEmptyPaid() {
    const pc = this._paidCount();
    for (let i = 0; i < pc; i++) { const s = this.state.paidSlots[i]; if (s && !s.assigned) return { kind: 'paid', index: i }; }
    return null;
  }
  _firstEmptyFree() {
    const tier = this._tier();
    for (let i = 0; i < tier.free; i++) if (!this.state.freeSlots[i].assigned) return { kind: 'free', index: i };
    return null;
  }

  // Which slot the next added print should fill. A free print is "earned" once enough paid prints are
  // filled (≥2 entry threshold, then one per extra paid, capped at the tier's free count). Once an
  // earned free slot is unfilled, the next add goes THERE before any remaining paid slot — so on a
  // 3-pack, adding a 3rd print after 2 paid becomes a FREE print, not a 3rd paid (feedback).
  _nextAddTarget() {
    // Use the canonical combined counts so an earned free slot is offered before an extra paid slot
    // (feedback: on a 3-pack, the 3rd print after 2 paid becomes FREE, not a 3rd paid).
    if (this._freeFilledCount() < this._freeEarnedCount()) { const f = this._firstEmptyFree(); if (f) return f; }
    return this._firstEmptyPaid() || this._firstEmptyFree();
  }

  async _addProduct(card, btn, preselect) {
    if (btn && btn.dataset.bbAdding === '1') return; // guard double-clicks (item F3)
    // Pre-built bundle mode: while the prefill is resolving, the print selector panel and
    // the quick-view / expand modals stay live — they're mounted OUTSIDE <bundle-builder>
    // (selector under .product-information__media, modals re-parented to <body>), so the
    // inert lock can't reach them (QA round 3: a mid-load selector click bumped the tier
    // and scrambled the link's bundle). Ignore every add that isn't the prefill's own —
    // identified by the wdPrefill marker on preselect, checked synchronously at entry, so
    // a click landing WHILE a prefill add is awaiting its fetch is still rejected (a
    // shared "adding" flag would be true for that whole window and let it through).
    // No-op outside prefill: _prefillLoading is never set on normal loads.
    if (this._prefillLoading && !(preselect && preselect.wdPrefill)) return;
    if (btn) { btn.dataset.bbAdding = '1'; btn.disabled = true; btn.textContent = 'Adding…'; }
    let added = false;
    try {
      // Resolve full variants (cents) from the product endpoint, with a timeout so a slow/hung
      // request can't leave the card stuck on "Adding…" forever (item F3).
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), 12000);
      let p;
      try {
        const r = await fetch('/products/' + card.handle + '.js', { signal: ctrl.signal });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        p = await r.json();
      } finally { clearTimeout(to); }

      const imgs = p.images || [];
      // Raw (unframed) art = the SECOND product image — the theme's positional convention
      // (cart-products.liquid composites images[1]; the cart frame addon uses media[1]).
      // The old /mock-up/ filename guess picked the FIRST (framed) image whenever a
      // product's art file wasn't named "mock-up-…". Filename match + featured are
      // fallbacks only, for products with fewer than 2 images.
      const rawArt = imgs[1] || imgs.find((u) => /mock-up/i.test(u)) || null;
      const artMedia = (p.media || [])[1]
        || (p.media || []).find((m) => /mock-up/i.test(m.src || (m.preview_image && m.preview_image.src) || ''))
        || (p.media || [])[0];
      let ratio = 0;
      if (artMedia) {
        const w = artMedia.width || (artMedia.preview_image && artMedia.preview_image.width);
        const h = artMedia.height || (artMedia.preview_image && artMedia.preview_image.height);
        if (w && h) ratio = w / h;
      }
      const product = {
        id: p.id, handle: p.handle, title: p.title, vendor: p.vendor, type: p.type || '',
        image: card.image || (p.featured_image || (p.images && p.images[0])) || null,
        artImage: rawArt || (p.featured_image || (p.images && p.images[0])) || null,
        ratio: ratio || 0.707,
        framingService: (p.tags || []).indexOf('framing-service') !== -1,
        variants: (p.variants || []).map((v) => ({ id: v.id, option1: v.option1, option2: v.option2, price: v.price, compare_at_price: v.compare_at_price })),
      };

      // Honour the size/frame chosen in the quick-view buy-box. Prefer the exact
      // selected variant id (it encodes size + frame together); else fall back to the
      // size/frame strings, validated against the product's real options; else default.
      let chosen = null;
      if (preselect && preselect.variantId) {
        chosen = product.variants.find((v) => String(v.id) === String(preselect.variantId)) || null;
      }
      const wantSize = chosen
        ? chosen.option1
        : (preselect && preselect.size && product.variants.some((v) => v.option1 === preselect.size) ? preselect.size : null);
      // Frame resolution, in priority: (1) the print's own frame option2 chosen in the
      // variant-picker swatch; (2) a colour chosen in a separate frame-variant-picker,
      // mapped to this product's option2; (3) the selected variant's option2.
      let wantFrame = null;
      if (preselect && preselect.frame && product.variants.some((v) => v.option2 === preselect.frame)) {
        wantFrame = preselect.frame;
      }
      if (!wantFrame && preselect && preselect.frameColor) {
        // Standard products: map the colour to the print's own frame option2. Child-frame
        // products have no such option2 (the frame is a linked product) — fall back to the
        // bare colour word, which _childFrameVariant resolves to the linked frame at add-to-cart.
        wantFrame = this._matchFrameByColour(product, preselect.frameColor) || this._frameColourWord(preselect.frameColor);
      }
      if (!wantFrame && chosen && chosen.option2) {
        wantFrame = chosen.option2;
      }
      // A framed choice with no variant at the chosen size (e.g. A0 + Framed black from a
      // stale quick-view picker) must not land in the slot — variantFor would silently
      // price/add the unframed variant while the slot shows a frame. Only applies when the
      // frame is a real option2 of this product; child-frame colour words ("Black") don't
      // use the main combo and are left alone.
      if (wantFrame && wantSize && !/unframed/i.test(wantFrame)
        && product.variants.some((v) => v.option2 === wantFrame)
        && !this._comboExists(product, wantSize, wantFrame)) {
        wantFrame = this._unframedOption(product);
      }

      // Find a slot: paid first, then free; else bump tier; else (top tier full) append an
      // extra paid slot so the shopper can keep adding beyond the pack (item 4: unlimited).
      // Pre-built bundle mode ONLY: the prefill loop declares which slot KIND each handle
      // fills (?paid= vs ?free=), bypassing the paid-first heuristic so a 3rd paid handle
      // can't land in an earned free slot. Opt-in via preselect — never set on normal adds.
      let target = null;
      if (preselect && preselect.wdPrefillKind) {
        target = preselect.wdPrefillKind === 'free' ? this._firstEmptyFree() : this._firstEmptyPaid();
      }
      if (!target) target = this._nextAddTarget();
      if (!target && this._bumpTier()) target = this._nextAddTarget();
      if (!target) {
        const idx = this.state.paidSlots.length;
        this.state.paidSlots.push({ assigned: false, product: null, size: null, frame: this._defaultFrame });
        target = { kind: 'paid', index: idx };
      }

      // A quick-view choice that isn't free-eligible (a size other than the ratio's locked
      // free size, or a framed option) can't fill a free slot: route the print to a PAID
      // slot instead, bumping the tier when the paid slots are full — the same rule
      // _moveFreeToPaidIfIneligible applies when a free slot is edited in the pack.
      // A FRAMED choice is the exception: the print is still the free item and its frame ships as
      // a separate PAID child line, so it stays free as long as the print offers the framing
      // service. Only a framed print with no child frame has to go paid.
      const wantFramed = !!(wantFrame && !/unframed/i.test(wantFrame));
      const framedBlocksFree = wantFramed && !(this.state.removeFraming && product.framingService);
      // A product that can never BE a free print (canvas type, or no size matching the locked
      // free-size keywords) is routed to paid too — the free slot stays open for a print that
      // actually qualifies, instead of receiving a paid-only size via _freeSizeFor's fallback.
      if (target.kind === 'free'
        && (!this._productFreeEligible(product) || (wantSize && !this._isFreeSize(product, wantSize)) || framedBlocksFree)) {
        let paidTarget = this._firstEmptyPaid();
        if (!paidTarget && this._bumpTier()) paidTarget = this._firstEmptyPaid();
        if (!paidTarget) {
          const idx = this.state.paidSlots.length;
          this.state.paidSlots.push({ assigned: false, product: null, size: null, frame: this._defaultFrame });
          paidTarget = { kind: 'paid', index: idx };
        }
        target = paidTarget;
      }

      if (target.kind === 'paid') {
        const slot = this.state.paidSlots[target.index];
        slot.assigned = true;
        slot.product = product;
        slot.size = wantSize || this._smallestSizeFor(product); // paid prints default to smallest size
        slot.frame = wantFrame || slot.frame || this._defaultFrame; // per-slot framing, default Unframed
      } else {
        const slot = this.state.freeSlots[target.index];
        slot.assigned = true;
        slot.product = product;
        // Keep a free-eligible size chosen in the quick-view (e.g. 4:5's second eligible
        // size); otherwise the ratio's locked free size.
        slot.size = (wantSize && this._isFreeSize(product, wantSize)) ? wantSize : this._freeSizeFor(product);
        // Unframed by default; a framed choice only reaches here when it's allowed to stay
        // free (service removed + the print offers it), so keep the chosen frame.
        slot.frame = wantFramed ? wantFrame : this._defaultFrame;
      }
      added = true;
      // The analytics event reports a SHOPPER's choice. A URL-prefilled add is the marketing
      // link filling the pack, not an action anyone took — reporting it fires one phantom
      // "Add to bundle" per handle (GTM + Klaviyo) on page load, before a single click. Suppress
      // the event for prefill adds ONLY: wdPrefill is set solely by _prefillFromParams, so every
      // real add (selector, quick-view, expand modal) still reports, including later edits to a
      // prefilled pack. Slotting above is untouched — the link fills the same slots as before.
      if (!(preselect && preselect.wdPrefill)) this._pushBundleDataLayer(product, target); // custom "Add to bundle" analytics event (item 5)
      // If Canvas is active, resolve this print's canvas sibling now so price + cart are correct.
      // A failure here must NOT block the add — the print is already slotted.
      if (this._canvasMat && this.state.material === this._canvasMat.title) {
        try { await this._ensureCanvasFor(product.handle); this._reconcileSizes(); } catch (e) {}
      }
      this._afterSlotChange();
    } catch (e) {
      console.error('[bundle-builder] add to slot failed', card && card.handle, e);
    } finally {
      // ALWAYS clear the "Adding…" state and re-sync every card's label to the real slot state,
      // so a failure or a slow render can never leave a card stuck (item F3).
      if (btn) { btn.dataset.bbAdding = ''; btn.disabled = false; if (!added) btn.textContent = '+ ADD'; }
      try { this._markCards(); } catch (e) {}
    }
  }

  // Custom "Add to bundle" data-layer event (item 5). Fires each time a print is added to the
  // bundle via the print selector. Purely additive — a fresh window.dataLayer.push that does not
  // read or modify any existing event. Safe if GTM/dataLayer isn't present.
  _pushBundleDataLayer(product, target) {
    try {
      const paidCount = this.state.paidSlots.filter((s) => s && s.assigned).length;
      const freeCount = this.state.freeSlots.filter((s) => s && s.assigned).length;
      // Variant-level details to match an ATC payload (feedback item 2): variant id, sku, size,
      // frame, price — pulled from the slot this print just landed in.
      const store = target && target.kind === 'free' ? this.state.freeSlots : this.state.paidSlots;
      const slot = (target && store && store[target.index]) || null;
      const variant = slot ? this.variantFor(this._effective(slot) || slot.product, slot.size, slot.frame) : null;
      // Absolute product + image URLs (client request). Image is what the shopper saw on the
      // card; URL built from the locale-aware route root so market subfolders keep working.
      let imageUrl = (product && (product.image || product.artImage)) || null;
      if (imageUrl && imageUrl.indexOf('//') === 0) imageUrl = 'https:' + imageUrl;
      const routeRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const productUrl = product && product.handle ? window.location.origin + routeRoot + 'products/' + product.handle : null;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'Add to bundle',
        bundle: {
          tier: this.state.tier,
          slotType: (target && target.kind) || 'paid',      // 'paid' | 'free'
          productId: product && product.id,
          productHandle: product && product.handle,
          productTitle: product && product.title,
          productUrl,
          imageUrl,
          vendor: product && product.vendor,
          variantId: variant && variant.id,
          sku: variant && variant.sku,
          size: slot && slot.size,
          frame: slot && slot.frame,
          price: variant && variant.price,                  // in cents
          paidCount, freeCount,
          totalPrints: paidCount + freeCount,
        },
      });
      // Klaviyo custom trigger event ("add to bundle"). Same payload, Klaviyo-style property
      // names, price converted from cents to currency units ($value is Klaviyo's monetary
      // property). window.klaviyo is the async queue from the app-embed klaviyo.js — pushing
      // is safe even before the script loads, and identity is handled by Klaviyo's cookie.
      const klPrice = variant && typeof variant.price === 'number' ? variant.price / 100 : null;
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(['track', 'add to bundle', {
        BundleTier: this.state.tier,
        SlotType: (target && target.kind) || 'paid',
        ProductID: product && product.id,
        ProductHandle: product && product.handle,
        ProductName: product && product.title,
        ProductURL: productUrl,
        ImageURL: imageUrl,
        Brand: product && product.vendor,
        VariantID: variant && variant.id,
        SKU: variant && variant.sku,
        Size: slot && slot.size,
        Frame: slot && slot.frame,
        Price: klPrice,
        PaidCount: paidCount,
        FreeCount: freeCount,
        TotalPrints: paidCount + freeCount,
        $value: klPrice,
      }]);
    } catch (e) { /* analytics must never break the add */ }
  }

  _assignedProductIds() {
    const ids = new Set();
    this.state.paidSlots.forEach((s) => { if (s.assigned && s.product) ids.add(String(s.product.id)); });
    this.state.freeSlots.forEach((s) => { if (s.assigned && s.product) ids.add(String(s.product.id)); });
    // Prints added via the cart (single PDP / PLP Quick-Add / cart recommendations) live only in the
    // cart, not in state slots — include them so the selector grid marks them "Added" too.
    (this._cartItems || []).forEach((l) => {
      if ((l.type || '').indexOf('print') > -1 && !/framing service/i.test(l.title || '')) ids.add(String(l.product_id));
    });
    return ids;
  }

  // Base handles (canvas suffix stripped) of assigned slot products. The grid is
  // material-scoped, so a print in the pack must also mark its SIBLING's card as
  // "Added" — the slot stores one material but _effective() renders the other.
  _assignedBaseHandles() {
    const bases = new Set();
    const push = (s) => {
      if (s && s.assigned && s.product && s.product.handle) bases.add(String(s.product.handle).replace(/-canvas$/i, '').toLowerCase());
    };
    this.state.paidSlots.forEach(push);
    this.state.freeSlots.forEach(push);
    return bases;
  }

  // Un-add a product (clicked an "Added" selector card) — remove it from its slot.
  // Matches by id OR base handle so a sibling card (other material) removes the same slot.
  _removeProductFromSlots(card) {
    const id = String(card && card.id);
    const base = String((card && card.handle) || '').replace(/-canvas$/i, '').toLowerCase();
    const match = (s) => s.assigned && s.product && (String(s.product.id) === id
      || (!!base && String(s.product.handle || '').replace(/-canvas$/i, '').toLowerCase() === base));
    let idx = this.state.paidSlots.findIndex(match);
    if (idx > -1) { this._removeSlot(idx); return; }
    idx = this.state.freeSlots.findIndex(match);
    if (idx > -1) { this._removeFreeSlot(idx); return; }
    // Cart-only print (added via single PDP / PLP Quick-Add / cart recs): not in a state slot, so
    // remove it straight from the cart — mirrors the "In cart" slot's X button in the pack.
    const cb = this._cartBundle();
    const line = cb.paidLines.concat(cb.freeLines).find((o) => String(o.id) === id);
    if (line) this._removeCartGroup(this._cartLineSlot(line));
  }

  _markCards() {
    if (!this._selEls) return;
    // Free-pick awareness: when the grid's material flips (Canvas selected and the paid
    // slots just filled → free picks are paper; free slots just completed → back to the
    // selected material), reload the grid. The state key is set BEFORE reloading, so the
    // reload's own _renderCards → _markCards pass sees it unchanged and cannot loop.
    const winFree = this._nextAddIsFree();
    const gridCanvas = this._gridMaterialIsCanvas();
    const gridFlipped = this._bbGridCanvas !== undefined && this._bbGridCanvas !== gridCanvas;
    this._bbGridCanvas = gridCanvas;
    if (gridFlipped && this._selector.built && this._selector.mode !== 'browse') this._loadSelector(false);
    const ids = this._assignedProductIds();
    const bases = this._assignedBaseHandles();
    // If the paid slots are full and a FREE slot is next, the next add is a free print.
    const nextIsFree = winFree;
    const addLabel = nextIsFree ? 'ADD FREE PRINT' : '+ ADD';
    [...this._selEls.grid.querySelectorAll('.bundle-print-selector__card')].forEach((card) => {
      const btn = card.querySelector('.bundle-print-selector__add');
      const cardBase = String(card.dataset.handle || '').replace(/-canvas$/i, '').toLowerCase();
      const added = ids.has(String(card.dataset.productId)) || (!!cardBase && bases.has(cardBase));
      btn.dataset.added = added ? 'true' : 'false';
      btn.disabled = false; // added cards are now clickable to REMOVE
      if (added) {
        // Default "Added"; on hover the CSS swaps to a black "Remove" button.
        btn.innerHTML = '<span class="bundle-print-selector__add-added">Added</span><span class="bundle-print-selector__add-remove">Remove</span>';
      } else {
        btn.textContent = addLabel;
      }
      card.dataset.added = added ? 'true' : 'false'; // dims the tile (Figma 50% white overlay)
    });
  }

  _updateSelectorCounter() {
    if (!this._selEls) return;
    const tier = this._tier();
    const pc = this._paidCount();
    const total = pc + tier.free;
    // Count cart-added prints too — single PDP / PLP Quick-Add / cart-rec prints live only in the
    // cart, so a state-slot-only count under-reports the pack (mobile widget bug).
    const assigned = Math.min(this._paidFilledCount(), pc) + Math.min(this._freeFilledCount(), tier.free);
    this._selEls.header.textContent = 'Select your prints — ' + assigned + ' of ' + total + ' selected';
    this._renderProgress();
  }

  // Bundle-progress dropdown (mobile drawer): label + current slots as thumbnails.
  // Mobile-selector progress copy — mirrors the PLP widget's "Start your pack…/You're N away…/
  // You've unlocked…" messaging (feedback: replace "X of Y Products Selected"). n = paid prints in
  // the bundle + cart.
  _progressMessage() {
    const steps = this._packTierKeys()
      .map((k) => this.tiers[k])
      .filter(Boolean)
      .map((t) => ({ count: Number(t.paid) || 0, discount: Number(t.discount) || 0, free: Number(t.free) || 0 }));
    if (!steps.length) return '';
    // Zero-discount offer (sale mode, tier discount settings all 0): every state below
    // references only the free prints — never "0% off". Mirrors bundle-widget.js message().
    const zero = (s) => (Number(s && s.discount) || 0) === 0;
    const n = this.state.paidSlots.filter((s) => s && s.assigned).length + this._cartBundle().paidCount;
    const top = steps[steps.length - 1];
    if (n === 0) {
      if (zero(top)) return 'Start your pack — up to ' + top.free + ' free prints';
      return 'Start your pack — Save up to ' + top.discount + '% and ' + top.free + ' free prints';
    }
    const next = steps.find((s) => n < s.count);
    if (!next) {
      if (zero(top)) return 'Nice! You’ve unlocked ' + top.free + ' free prints';
      return 'Nice! You’ve unlocked ' + top.discount + '% off + ' + top.free + ' free prints';
    }
    const current = steps.slice().reverse().find((s) => n >= s.count);
    if (current) {
      if (zero(current)) return 'You’ve unlocked ' + current.free + ' free print' + (current.free > 1 ? 's' : '');
      return 'You’ve unlocked ' + current.discount + '% off and ' + current.free + ' free print' + (current.free > 1 ? 's' : '');
    }
    const away = next.count - n;
    if (zero(next) && next.free > 0) return 'You’re ' + away + ' ' + (away === 1 ? 'print' : 'prints') + ' away from ' + next.free + ' free print' + (next.free > 1 ? 's' : '');
    let msg = 'You’re ' + away + ' ' + (away === 1 ? 'print' : 'prints') + ' away from ' + next.discount + '% off';
    if (next.free > 0) msg += ' and ' + next.free + ' free print' + (next.free > 1 ? 's' : '');
    return msg;
  }

  // Ordered pack contents for the selector's progress thumbnails — MUST mirror _renderSlots so
  // prints added via the CART (single PDP, PLP Quick-Add, cart recommendations), which live only in
  // _cartItems and not in state slots, still show in the selector widget (mobile bug). Returns
  // { paid:[…], free:[…] } padded with null up to the tier capacity.
  _packThumbSlots() {
    const cb = this._cartBundle();
    const column = (stateSlots, lockedLines, cap) => {
      const remaining = lockedLines.slice();
      const takeMatch = (slot) => {
        // Same dual-id match as _renderSlots: cart lines hold the EFFECTIVE (paper/canvas)
        // product id, so stored-id-only matching doubled the progress thumbs on canvas.
        const eff = this._effective(slot);
        const ids = [slot.product && String(slot.product.id), eff && String(eff.id)].filter(Boolean);
        if (!ids.length) return null;
        let idx = remaining.findIndex((o) => ids.indexOf(String(o.id)) > -1 && o.size === slot.size && o.frame === slot.frame);
        if (idx < 0) idx = remaining.findIndex((o) => ids.indexOf(String(o.id)) > -1);
        return idx < 0 ? null : remaining.splice(idx, 1)[0];
      };
      const ordered = [];
      (stateSlots || []).forEach((s) => {
        if (s && s.assigned && this._inCart(s)) { const line = takeMatch(s); ordered.push(line ? this._cartLineSlot(line) : s); }
        else if (s && s.assigned) { ordered.push(s); }
      });
      // External cart adds (no bundle slot) first, then the shopper's in-progress slots — same order
      // as the pack. Render EVERY filled print (never truncate the bundle's own in-progress prints
      // when the cart already holds `cap` of them — that dropped bundle prints from the widget), then
      // pad with empty placeholders up to the tier capacity.
      const filled = remaining.map((o) => this._cartLineSlot(o)).concat(ordered);
      for (let i = filled.length; i < cap; i++) filled.push(null);
      return filled;
    };
    return {
      paid: column(this.state.paidSlots, cb.paidLines, this._paidCount()),
      free: column(this.state.freeSlots, cb.freeLines, this._tier().free),
    };
  }

  _renderProgress() {
    if (!this._selEls || !this._selEls.progressThumbs) return;
    this._selEls.progressLabel.textContent = this._progressMessage();
    const wrap = this._selEls.progressThumbs;
    wrap.innerHTML = '';
    const pack = this._packThumbSlots();
    pack.paid.concat(pack.free).forEach((s) => wrap.appendChild(s ? this._framedThumb(s) : this._thumb(null)));
  }

  // The frame COLOUR options on a print (option2 "Framed black/white/natural") map to the
  // shared physical frame product's colour values (Black / White / Oak).
  _frameColourName(slotFrame) {
    const word = String(slotFrame || '').replace(/framed/i, '').trim().toLowerCase();
    const MAP = { black: 'Black', white: 'White', natural: 'Oak', oak: 'Oak' };
    return MAP[word] || (word ? word.charAt(0).toUpperCase() + word.slice(1) : '');
  }

  // The `Framing` line-item property VALUE — the native cart uses the frame COLOUR ("Black"),
  // not the option label ("Framed black"). Unframed prints stay "Unframed".
  _framingProp(frame) {
    if (!frame || /unframed/i.test(frame)) return 'Unframed';
    return this._frameColourName(frame);
  }

  // The "Unframed" option value for a product (usually literally "Unframed").
  _unframedOption(product) {
    const opts = [...new Set(((product && product.variants) || []).map((v) => v.option2).filter(Boolean))];
    return opts.find((o) => /unframed/i.test(o)) || 'Unframed';
  }

  // The handle whose `custom.child_frames` metafield applies to a slot — the EFFECTIVE
  // (paper/canvas) product, because that's the variant that actually goes into the cart.
  _frameHandleFor(slot) {
    const eff = (slot && this._effective(slot)) || (slot && slot.product) || null;
    return (eff && eff.handle) || '';
  }

  // Each PRINT names its own physical frame product via the `custom.child_frames` metafield —
  // blocks/frame-variant-picker.liquid takes the first entry's product and falls back to the
  // block's `frame_product` setting (`classic-frame`). Hardcoding a handle here is what made the
  // pack add `classic-frame` for prints assigned the discounted `frame-1`, so the shopper was
  // shown one price and charged another.
  //
  // Metafields aren't exposed by /products/<handle>.js, so this fetches
  // templates/product.child-frame.liquid via `?view=child-frame`, which reproduces that exact
  // resolution. Cached per PRINT handle. Returns null while the request is in flight — callers
  // treat that as "no frame price yet" and are re-run when it lands.
  _childFramesFor(handle) {
    if (!handle) return null;
    if (!this._childFrameCache) this._childFrameCache = {};
    if (handle in this._childFrameCache) return this._childFrameCache[handle];
    this._childFrameCache[handle] = null; // in flight
    if (!this._childFramePending) this._childFramePending = {};
    this._childFramePending[handle] = fetch('/products/' + handle + '?view=child-frame')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const map = {};
        ((d && d.variants) || []).forEach((v) => { map[v.size + '|' + v.colour] = { id: v.id, price: v.price }; });
        this._childFrameCache[handle] = { product: (d && d.frameProduct) || '', map };
        // Prices/ids that read 0/null while this was loading need repainting.
        if (this.state && this.state.tier !== 'single') { this._renderSlots(); this.recalculate(); }
        const dialog = document.getElementById('quick-add-dialog');
        if (dialog && dialog.hasAttribute('data-bb-free')) this._qvApplyFreePricing(dialog);
        return this._childFrameCache[handle];
      })
      .catch(() => {
        this._childFrameCache[handle] = { product: '', map: {} };
        return this._childFrameCache[handle];
      });
    return null;
  }

  // Await the lookup. Used before add-to-cart, where the real variant id is mandatory.
  _ensureChildFrames(handle) {
    const cached = this._childFramesFor(handle);
    if (cached) return Promise.resolve(cached);
    return (this._childFramePending && this._childFramePending[handle]) || Promise.resolve(null);
  }

  // The frame variant for THIS print at a given size + frame colour.
  _childFrameEntry(handle, size, slotFrame) {
    const cf = this._childFramesFor(handle); // also kicks off the fetch on first use
    if (!cf) return null;
    const at = (colour) => (colour ? cf.map[size + '|' + colour] || null : null);
    // Direct map first ("Framed black" -> "Black"). A child-frame picker hands back handle-style
    // values ("black-wooden-frame", "natural-oak") that don't map directly, so retry via the
    // extracted colour word ("Black", "Natural" -> "Oak").
    return at(this._frameColourName(slotFrame))
      || at(this._frameColourName(this._frameColourWord(slotFrame)))
      || null;
  }
  _childFrameVariant(handle, size, slotFrame) {
    const e = this._childFrameEntry(handle, size, slotFrame);
    return e ? e.id : null;
  }

  _childFramePrice(handle, size, slotFrame) {
    const e = this._childFrameEntry(handle, size, slotFrame);
    return (e && e.price) || 0;
  }

  // Total price of the separate physical frames on framed FREE prints being added now (only
  // possible with the framing service removed): the print is free, its frame is paid. Flat —
  // the pack discount applies to prints, not frames. Excludes in-cart lines.
  _freeFrameAddon() {
    if (!this.state.removeFraming) return 0; // a framed free print only exists while removed
    let sum = 0;
    for (let i = 0; i < this._tier().free; i++) {
      const s = this.state.freeSlots[i];
      if (!s || !s.assigned || this._inCart(s)) continue;
      if (s.frame && !/unframed/i.test(s.frame) && this._framingSvcPrint(s)) {
        sum += this._childFramePrice(this._frameHandleFor(s), s.size, s.frame);
      }
    }
    return sum;
  }

  // ---- Add to cart (assigned paid + free slots) ----
  async _addToCart() {
    const tier = this._tier();
    // Resolve each framed print's OWN child frame (custom.child_frames) before building the cart
    // lines — the variant id is mandatory for the unframed-print + separate-frame split.
    await Promise.all(this._framedSlots().map((s) => this._ensureChildFrames(this._frameHandleFor(s))));
    // Safety net: make sure every assigned slot's effective (paper/canvas) sibling is
    // loaded so we never submit the wrong-material variant by mistake.
    {
      const wantCanvas = this._canvasMat && this.state.material === this._canvasMat.title;
      // Every assigned slot at any index — see the note on the item loops below.
      const handles = [];
      [...this.state.paidSlots, ...this.state.freeSlots].forEach((s) => { if (s && s.assigned && s.product) handles.push(s.product.handle); });
      if (wantCanvas) {
        await Promise.all(handles.filter((h) => !/-canvas$/i.test(h)).map((h) => this._ensureCanvasFor(h)));
      } else {
        await Promise.all(handles.filter((h) => /-canvas$/i.test(h)).map((h) => this._ensurePaperFor(h)));
      }
    }
    const bundleId = 'bb-' + this.product.id + '-' + this.state.tier;
    const items = [];
    // NOTE: no `Material` property — the native cart doesn't add one (paper vs canvas is already
    // encoded by the product/variant), so adding it created a stray "Material: Art Paper" line
    // at checkout that the live site doesn't have.
    const baseProps = { _bundle_id: bundleId, _bundle_type: this.state.tier };
    // Walk the WHOLE slot array, not the first `tier.paid` entries. _renderSlots draws every
    // assigned slot and recalculate now prices every one, so windowing here silently refused to add
    // prints the shopper could see and the CTA had already quoted: with two in-cart prints holding
    // indices 0-1, two freshly added prints sat at 2-3, every index in range was skipped as
    // "already in cart", `items` came out empty, and the `if (!items.length) return` below made the
    // button do nothing at all on click.
    for (let i = 0; i < this.state.paidSlots.length; i++) {
      const slot = this.state.paidSlots[i];
      if (!slot || !slot.assigned) continue;
      if (this._inCart(slot)) continue; // already in cart ("In cart" badge) — don't re-add
      const eff = this._effective(slot);
      const v = this.variantFor(eff, slot.size, slot.frame); // the framed (or unframed) variant
      if (!v) continue;
      const framed = slot.frame && !/unframed/i.test(slot.frame);
      const fid = this._frameIdFor(slot); // lets the live cart composite the frame image

      if (framed && this.state.removeFraming) {
        // "Remove framing service" = ship the UNFRAMED print + a SEPARATE physical frame product
        // (parent_id-linked child) + `_main_variant`, EXACTLY like the native PDP — not the
        // assembled framed variant (which is the wrong fulfilment SKU). See _childFrameVariant.
        const unframedV = this.variantFor(eff, slot.size, this._unframedOption(eff));
        const childFrameId = this._childFrameVariant(this._frameHandleFor(slot), slot.size, slot.frame);
        if (unframedV && childFrameId) {
          const props = { ...baseProps, Framing: this._framingProp(slot.frame), _main_variant: String(v.id) };
          if (fid) props._frame_id = fid;
          items.push({ id: unframedV.id, quantity: 1, properties: props });
          items.push({ id: childFrameId, quantity: 1, parent_id: unframedV.id, properties: { _bundle_id: bundleId, _bundle_type: this.state.tier } });
          continue;
        }
        // Fallback: couldn't resolve the frame product — add the framed variant so the cart isn't broken.
        const fprops = { ...baseProps, Framing: this._framingProp(slot.frame) };
        if (fid) fprops._frame_id = fid;
        items.push({ id: v.id, quantity: 1, properties: fprops });
        continue;
      }

      // KEEP framing (or an unframed print): the framed/unframed variant itself, plus — when
      // framed + eligible — a framing-service child whose parent_id is THIS print's variant, so
      // the cart nests it (the native "keep" structure), not a stray un-removable line.
      const props = { ...baseProps, Framing: this._framingProp(slot.frame) };
      if (fid) props._frame_id = fid;
      items.push({ id: v.id, quantity: 1, properties: props });
      if (framed && slot.product.framingService && !this.state.removeFraming && this._framingSvc && this._framingSvc.id) {
        items.push({ id: this._framingSvc.id, quantity: 1, parent_id: v.id, properties: { _bundle_id: bundleId, _bundle_type: this.state.tier } });
      }
    }
    for (let i = 0; i < this.state.freeSlots.length; i++) {
      const slot = this.state.freeSlots[i];
      if (!slot || !slot.assigned) continue;
      if (this._inCart(slot)) continue; // already in cart — don't re-add
      const eff = this._effective(slot);
      const v = this.variantFor(eff, slot.size || this._freeSizeFor(eff), slot.frame || this._defaultFrame);
      if (!v) continue;
      const framed = slot.frame && !/unframed/i.test(slot.frame);
      const fid = this._frameIdFor(slot);
      if (framed && this.state.removeFraming && slot.product.framingService) {
        // A framed FREE print: the PRINT is the free item — its UNFRAMED variant carries
        // _bundle_free so the discount app zeros just the print — plus the separate physical
        // frame as a PAID child line, same as the paid path.
        const unframedV = this.variantFor(eff, slot.size, this._unframedOption(eff));
        const childFrameId = this._childFrameVariant(this._frameHandleFor(slot), slot.size, slot.frame);
        if (unframedV && childFrameId) {
          const props = { ...baseProps, Framing: this._framingProp(slot.frame), _main_variant: String(v.id), _bundle_free: 'true' };
          if (fid) props._frame_id = fid;
          items.push({ id: unframedV.id, quantity: 1, properties: props });
          items.push({ id: childFrameId, quantity: 1, parent_id: unframedV.id, properties: { _bundle_id: bundleId, _bundle_type: this.state.tier } });
          continue;
        }
        // Fallback: no child frame to carry the frame as a paid line — CHARGE the framed variant
        // (no _bundle_free) rather than give a framed print away. The _framingSvcPrint gate on the
        // free/paid moves should make this unreachable; this is the belt-and-braces.
        const fbProps = { ...baseProps, Framing: this._framingProp(slot.frame) };
        if (fid) fbProps._frame_id = fid;
        items.push({ id: v.id, quantity: 1, properties: fbProps });
        continue;
      }
      const props = { ...baseProps, Framing: this._framingProp(slot.frame || this._defaultFrame), _bundle_free: 'true' };
      if (fid) props._frame_id = fid;
      items.push({ id: v.id, quantity: 1, properties: props });
    }
    if (!items.length) return;

    // Was this a COMPLETE pack? If so, nudge the shopper toward the next pack up (item 10).
    const packComplete = this.state.paidSlots.filter((s) => s && s.assigned).length >= tier.paid;
    const nextTierKey = this.tierOrder[this.tierOrder.indexOf(this.state.tier) + 1];
    // (Framing-service lines are added inline per print above, linked via parent_id.)

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component')).map((el) => el.dataset.sectionId).filter(Boolean);
    this.atcBtn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items, sections: sectionIds }) });
      const data = await res.json();
      // Keep the bundle persisted (item 11, issue A): the prints are in the cart now, so on the
      // next PDP they should reappear as locked "In cart" slots rather than vanish. _loadCart()
      // marks them in-cart; they clear from persist only when the shopper empties the bundle.
      await this._loadCart();
      this._persistSave();
      if (typeof window.manualCartRefresh === 'function') window.manualCartRefresh(data);
      document.querySelector('cart-drawer-component .header-actions__action')?.click();
      // Wiggle the next pack up ~5s after adding a complete pack, to nudge the upgrade (item 10).
      if (packComplete && nextTierKey) {
        const btn = this.tierButtons.find((b) => b.dataset.tier === nextTierKey);
        if (btn) setTimeout(() => {
          btn.classList.add('bundle-builder__tier--wiggle');
          setTimeout(() => btn.classList.remove('bundle-builder__tier--wiggle'), 1500);
        }, 5000);
      }
    } catch (e) {
      console.error('[bundle-builder] add to cart failed', e);
    } finally {
      this.atcBtn.disabled = false;
    }
  }

  // ---- Product swatches (Single mode, R1.6) ----

  // Sale A/B variant arm (html.bb-off-pdp): the ONLY builder feature that survives is the
  // product swatch row, relocated into the native single view above the pickers — the same
  // position _ensureSingleCard gives it, minus the white card, so the variant keeps the
  // plain Material/Size/Frame view. Initialises just enough state for the swatch code
  // paths (_renderSwatchRow reads state.tier via _stampViewMode) and nothing else; the
  // host element stays hidden (inline style + the block stylesheet's bb-off-pdp rule).
  _initVariantSwatches() {
    this.entryMode = false;
    this.product = this._readJSON('.bundle-builder__product') || {};
    pushRecent(this.product);
    const boostCfg = this._readJSON('.bundle-builder__boost') || {};
    this._boost = {
      shop: boostCfg.shop || (window.Shopify && window.Shopify.shop) || 'drool-art.myshopify.com',
      scope: boostCfg.collectionScope ? String(boostCfg.collectionScope) : '',
      canvasScope: boostCfg.collectionScopeCanvas ? String(boostCfg.collectionScopeCanvas) : '',
    };
    this.state = { tier: 'single', paidSlots: [], freeSlots: [] };
    this.swatchesEl = this.querySelector('.bundle-builder__swatches');
    this.swatchTabsEl = this.querySelector('.bundle-builder__swatch-tabs');
    this.swatchRowEl = this.querySelector('.bundle-builder__swatch-row');
    const urlMedium = new URLSearchParams(window.location.search).get('medium');
    this._swatch = {
      built: false,
      activeMedium:
        urlMedium && SWATCH_MEDIUMS.some((m) => m.key === urlMedium)
          ? urlMedium
          : (this.product && this.product.vendor ? '__artist' : '__fbt'),
      loaded: {},
    };
    if (this.swatchesEl) this.swatchesEl.hidden = false;
    // Wire the swatch subsystem BEFORE relocating it: _wireSwatchArrows finds the arrow
    // buttons via this.querySelector, so they must still sit inside the host at wire time
    // (the control path has the same order — _ensureSwatches before _ensureSingleCard's
    // move). Element references and listeners survive the move.
    this._ensureSwatches();
    const pickers = [...document.querySelectorAll('.wd-native-picker, frame-variant-picker')];
    if (this.swatchesEl && pickers.length) pickers[0].parentElement.insertBefore(this.swatchesEl, pickers[0]);
    this.style.display = 'none'; // everything still inside the host stays hidden
    // Re-measure the scroll arrows once the relocation has settled — the wire-time
    // measurement ran while the host was display:none, so it read zero widths. Same
    // reflow + resize nudge the control path uses on single-view reveal.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      void document.documentElement.offsetHeight;
      window.dispatchEvent(new Event('resize'));
    }));
  }

  _ensureSwatches() {
    if (this._swatch.built) return;
    this._swatch.built = true;
    this._renderSwatchTabs();
    this._loadSwatchTab(this._swatch.activeMedium);
    this._wireSwatchArrows();
  }

  // Desktop scroll arrows for the swatch TABS and the swatch ROW, so no-trackpad users can page
  // through them (item 13). Buttons live in the Liquid; wire clicks + overflow-based show/hide.
  _wireSwatchArrows() {
    const tabs = this.swatchTabsEl;
    if (!tabs) return;
    this._swatchPrev = this.querySelector('.bundle-builder__swatch-tabs-arrow--prev');
    this._swatchNext = this.querySelector('.bundle-builder__swatch-tabs-arrow--next');
    if (this._swatchPrev) this._swatchPrev.innerHTML = ICON_CHEVRON_LEFT;
    if (this._swatchNext) this._swatchNext.innerHTML = ICON_CHEVRON;
    const scrollBy = (dir) => tabs.scrollBy({ left: dir * Math.max(140, tabs.clientWidth * 0.6), behavior: 'smooth' });
    if (this._swatchPrev) this._swatchPrev.addEventListener('click', () => scrollBy(-1));
    if (this._swatchNext) this._swatchNext.addEventListener('click', () => scrollBy(1));
    tabs.addEventListener('scroll', () => this._updateSwatchArrows());
    window.addEventListener('resize', () => this._updateSwatchArrows());

    const row = this.swatchRowEl;
    this._rowPrev = this.querySelector('.bundle-builder__swatch-row-arrow--prev');
    this._rowNext = this.querySelector('.bundle-builder__swatch-row-arrow--next');
    if (this._rowPrev) this._rowPrev.innerHTML = ICON_CHEVRON_LEFT;
    if (this._rowNext) this._rowNext.innerHTML = ICON_CHEVRON;
    if (row) {
      const rowScroll = (dir) => row.scrollBy({ left: dir * Math.max(200, row.clientWidth * 0.8), behavior: 'smooth' });
      if (this._rowPrev) this._rowPrev.addEventListener('click', () => rowScroll(-1));
      if (this._rowNext) this._rowNext.addEventListener('click', () => rowScroll(1));
      row.addEventListener('scroll', () => this._updateSwatchArrows());
    }
    // The row first renders while the single-view container is still hidden (width 0), so the
    // initial overflow check under-reports. A ResizeObserver recalculates once it gets real width.
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => this._updateSwatchArrows());
      if (row) ro.observe(row);
      if (tabs) ro.observe(tabs);
    }
  }
  _updateSwatchArrows() {
    const tabs = this.swatchTabsEl;
    if (tabs && this._swatchPrev && this._swatchNext) {
      const overflow = tabs.scrollWidth - tabs.clientWidth;
      if (overflow <= 2) { this._swatchPrev.hidden = true; this._swatchNext.hidden = true; }
      else { this._swatchPrev.hidden = tabs.scrollLeft <= 2; this._swatchNext.hidden = tabs.scrollLeft >= overflow - 2; }
    }
    const row = this.swatchRowEl;
    if (row && this._rowPrev && this._rowNext) {
      const overflow = row.scrollWidth - row.clientWidth;
      if (overflow <= 2) { this._rowPrev.hidden = true; this._rowNext.hidden = true; }
      else { this._rowPrev.hidden = row.scrollLeft <= 2; this._rowNext.hidden = row.scrollLeft >= overflow - 2; }
    }
  }

  // Same treatment for the pack-size row, a horizontal scroller since the ladder outgrew the row
  // (ten buttons, Single → 10+ Pack). Mirrors _wireSwatchArrows(): chevrons injected here, shown
  // only while the row actually overflows in that direction.
  _wireTierArrows() {
    const row = this.querySelector('.bundle-builder__tiers');
    if (!row) return;
    this._tiersRow = row;
    this._tierPrev = this.querySelector('.wd-bundle-builder__tiers-arrow--prev');
    this._tierNext = this.querySelector('.wd-bundle-builder__tiers-arrow--next');
    if (this._tierPrev) this._tierPrev.innerHTML = ICON_CHEVRON_LEFT;
    if (this._tierNext) this._tierNext.innerHTML = ICON_CHEVRON;
    const by = (dir) => row.scrollBy({ left: dir * Math.max(160, row.clientWidth * 0.6), behavior: 'smooth' });
    if (this._tierPrev) this._tierPrev.addEventListener('click', () => by(-1));
    if (this._tierNext) this._tierNext.addEventListener('click', () => by(1));
    row.addEventListener('scroll', () => this._updateTierArrows());
    window.addEventListener('resize', () => this._updateTierArrows());
    // The block can be laid out while hidden (entry mode / quick view), where the overflow check
    // under-reports; recheck once it gets real width.
    if (window.ResizeObserver) new ResizeObserver(() => this._updateTierArrows()).observe(row);
    this._updateTierArrows();
  }
  _updateTierArrows() {
    const row = this._tiersRow;
    if (!row || !this._tierPrev || !this._tierNext) return;
    const overflow = row.scrollWidth - row.clientWidth;
    if (overflow <= 2) { this._tierPrev.hidden = true; this._tierNext.hidden = true; return; }
    this._tierPrev.hidden = row.scrollLeft <= 2;
    this._tierNext.hidden = row.scrollLeft >= overflow - 2;
  }
  // Bring a tier button into view after a programmatic change (_bumpTier, carried view mode,
  // the post-add wiggle) so the newly selected pack isn't parked off the edge of the scroller.
  _revealTier(tier) {
    const row = this._tiersRow;
    if (!row || row.scrollWidth - row.clientWidth <= 2) return;
    const btn = (this.tierButtons || []).find((b) => b.dataset.tier === tier);
    if (!btn) return;
    const left = btn.offsetLeft - (row.clientWidth - btn.offsetWidth) / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }

  _renderSwatchTabs() {
    if (!this.swatchTabsEl) return;
    this.swatchTabsEl.innerHTML = '';
    const tabs = [...SWATCH_MEDIUMS];
    // Order (feedback #10): More from this artist → Frequently Bought Together → Recently Viewed →
    // medium tabs (already in `tabs`). unshift in reverse so the LAST unshift is the first tab.
    // "More from this artist" + FBT are powered by Boost's recommend API (related-items / FBT).
    this._hasRecentTab = getRecent().length > 0;
    if (this._hasRecentTab) tabs.unshift({ key: '__recent', label: 'Recently Viewed' });
    if (!this.entryMode && this.product && this.product.id) tabs.unshift({ key: '__fbt', label: 'Frequently Bought Together' });
    if (!this.entryMode && this.product && this.product.vendor) tabs.unshift({ key: '__artist', label: 'More from this artist' });
    tabs.forEach((m) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-builder__swatch-tab';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', m.key === this._swatch.activeMedium ? 'true' : 'false');
      btn.textContent = m.label;
      btn.addEventListener('click', () => {
        this._swatch.activeMedium = m.key;
        [...this.swatchTabsEl.children].forEach((c) => c.setAttribute('aria-selected', c === btn ? 'true' : 'false'));
        this._loadSwatchTab(m.key, true);
      });
      this.swatchTabsEl.appendChild(btn);
    });
    this._updateSwatchArrows();
  }

  // "More from this artist" — Boost's recommend API. recommendationType must be "related-items" (the type
  // configured on the "The Artist's Collection" widget, wid productpage-314666, whose sameVendor+sameProductType
  // rules scope results to this artist; "more-from-brand" is not a valid type on this shop and 400s).
  // Even with the widget LIVE, its rules are NOT applied server-side (verified 2026-08-11: identical
  // generic results for every seed product), so Boost survivors are merged with — never trusted
  // over — the same-vendor search.
  async _loadArtistRecs(userClick) {
    const row = this.swatchRowEl;
    if (!row) return;
    if (this._swatch.loaded.__artist) { this._renderSwatchRow(this._swatch.loaded.__artist, '__artist'); return; }
    // Known-empty from an earlier load: show the message, no refetch — and never redirect a click.
    if (this._swatch.artistEmpty) {
      row.innerHTML = '<div class="bundle-print-selector__empty">No other prints from this artist.</div>';
      return;
    }
    row.innerHTML = '<div class="bundle-print-selector__loading">Loading…</div>';
    // Empty artist row → on the initial auto-load, default to the FBT tab instead (mirrors
    // _loadFBT's medium-tab fallback). On a user click, KEEP this tab selected and show the
    // empty message — silently bouncing the user to FBT reads as a broken tab.
    const fbtFallback = () => {
      this._swatch.artistEmpty = true;
      if (userClick || this.entryMode || !this.product || !this.product.id) {
        row.innerHTML = '<div class="bundle-print-selector__empty">No other prints from this artist.</div>';
        return;
      }
      this._swatch.activeMedium = '__fbt';
      this._renderSwatchTabs();
      this._loadFBT();
    };
    const finish = (products) => {
      // Dedup by ARTWORK, not product id: paper + canvas versions share a base handle
      // (`foo` / `foo-canvas`, the store-wide sibling convention), so keep one per base
      // and drop the current artwork's own sibling — the id check alone can't catch
      // "same print, other material".
      const curBase = String(this.product.handle || '').replace(/-canvas$/i, '').toLowerCase();
      const seenBases = new Set();
      products = (products || []).filter((p) => {
        if (!p.handle || String(p.id) === String(this.dataset.productId)) return false;
        const base = String(p.handle).replace(/-canvas$/i, '').toLowerCase();
        if (base === curBase || seenBases.has(base)) return false;
        seenBases.add(base);
        return true;
      });
      if (!products.length) { fbtFallback(); return; }
      this._swatch.loaded.__artist = products;
      this._renderSwatchRow(products, '__artist');
    };
    // Boost BACKFILLS with generic recommendations when the artist has nothing else (its
    // widget rules rank — they don't strictly filter), so never trust the response as-is:
    // keep only true matches — same vendor AND same material as the current product.
    // STRICT type equality (a blank type only matches blank): product type is the material
    // source of truth on this store, so a blank current type must not skip the check — that
    // skip is what let paper + canvas versions of the same artwork through together.
    const matches = (vendor, type) => vendor === this.product.vendor
      && String(type || '') === String(this.product.type || '');
    const limit = SWATCH_MAX + 1;
    // Never short-circuit on a non-empty Boost result: when the current artist happens to be in
    // Boost's generic list, only 1-3 products survive the filter — showing just those (and skipping
    // the vendor search) is what made the artist row look near-empty for some artists. Fetch both
    // sources concurrently and merge, Boost survivors first (relevance-ranked); finish() dedups by
    // base handle. Self-heals if Boost ever starts honoring the sameVendor rule.
    const boostRecs = (async () => {
      const shop = this._boost.shop;
      const url = 'https://services.mybcapps.com/discovery/recommend?recommendationType=related-items&limit=' + limit
        + '&shop=' + shop + '&wid=productpage-314666&event_type=init&pg=product_page&locale=en&layoutDisplay=carousel&return_all_currency_fields=false';
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shop, recommendationType: 'related-items', limit, wid: 'productpage-314666', productIds: [this.product.id], locale: 'en' }) });
      const d = await res.json();
      return ((d && d.products) || []).filter((p) => matches(p.vendor, p.product_type)).map((p) => ({
        id: p.id, handle: p.handle, title: p.title,
        image: (p.images_info && p.images_info[0] && p.images_info[0].src) || (p.images && p.images[0]) || p.image || null,
      }));
    })().catch(() => []);
    const vendorSearch = (async () => {
      // Boost's filter API (same service the print-selector grid queries) instead of
      // /search/suggest.json: predictive search hard-caps at 10 results, which kept this row
      // below the 16 the other tabs can show. The vendor + product-type facets filter
      // server-side and only in-stock products come back.
      const p = new URLSearchParams();
      p.set('shop', this._boost.shop);
      p.append('pf_v_vendor[]', this.product.vendor);
      if (this.product.type) p.append('pf_pt_product_type[]', this.product.type);
      p.set('page', '1');
      p.set('limit', String(limit));
      p.set('sort', 'best-selling');
      p.set('build_filter_tree', 'false');
      p.set('product_available', 'true');
      const d = await (await fetch(BOOST_FILTER_URL + '?' + p.toString())).json();
      return ((d && d.products) || []).filter((pr) => matches(pr.vendor, pr.product_type)).map(mapBoostProduct);
    })().catch(() => null);
    const [fromBoost, fromSearch] = await Promise.all([boostRecs, vendorSearch]);
    // Both sources failed outright (fromSearch null = fetch/parse error, not just no matches).
    if (fromSearch === null && !fromBoost.length) { fbtFallback(); return; }
    finish(fromBoost.concat(fromSearch || []));
  }

  async _loadSwatchTab(medium, userClick) {
    const row = this.swatchRowEl;
    if (!row) return;
    if (medium === '__fbt') { this._loadFBT(); return; }
    if (medium === '__artist') { this._loadArtistRecs(userClick); return; }
    if (medium === '__recent') {
      this._renderSwatchRow(getRecent().map((p) => ({ id: p.id, handle: p.handle, title: p.title, image: p.image })), '__recent');
      return;
    }
    if (this._swatch.loaded[medium]) { this._renderSwatchRow(this._swatch.loaded[medium], medium); return; }
    row.innerHTML = '<div class="bundle-print-selector__loading">Loading…</div>';
    try {
      const r = await fetch('/collections/' + medium + '/products.json?limit=' + (SWATCH_MAX + 1));
      const d = await r.json();
      const products = (d.products || []).map((p) => ({
        id: p.id, handle: p.handle, title: p.title,
        image: (p.images && p.images[0] && p.images[0].src) || null,
      }));
      this._swatch.loaded[medium] = products;
      this._renderSwatchRow(products, medium);
    } catch (e) { row.innerHTML = ''; }
  }

  // "Frequently Bought Together" swatches via Boost's recommend API (item 12). Falls back to
  // the product's medium tab if the API returns nothing or errors.
  async _loadFBT() {
    const row = this.swatchRowEl;
    if (!row) return;
    if (this._swatch.loaded.__fbt) { this._renderSwatchRow(this._swatch.loaded.__fbt, '__fbt'); return; }
    row.innerHTML = '<div class="bundle-print-selector__loading">Loading…</div>';
    const fallback = () => {
      const key = this.dataset.productMedium || SWATCH_MEDIUMS[0].key;
      this._swatch.activeMedium = key;
      this._renderSwatchTabs();
      [...this.swatchTabsEl.children].forEach((c) => c.setAttribute('aria-selected', c.textContent === (SWATCH_MEDIUMS.find((m) => m.key === key) || {}).label ? 'true' : 'false'));
      this._loadSwatchTab(key);
    };
    try {
      const shop = this._boost.shop;
      const limit = SWATCH_MAX + 1;
      const url = 'https://services.mybcapps.com/discovery/recommend?recommendationType=frequently-bought-together&limit=' + limit
        + '&shop=' + shop + '&wid=productpage-221575&event_type=init&pg=product_page&locale=en&layoutDisplay=carousel&return_all_currency_fields=false';
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shop, recommendationType: 'frequently-bought-together', limit, wid: 'productpage-221575', productIds: [this.product.id], locale: 'en' }) });
      const d = await res.json();
      const products = ((d && d.products) || []).map((p) => ({
        id: p.id, handle: p.handle, title: p.title,
        image: (p.images_info && p.images_info[0] && p.images_info[0].src) || (p.images && p.images[0]) || p.image || null,
      })).filter((p) => p.handle);
      if (!products.length) { fallback(); return; }
      this._swatch.loaded.__fbt = products;
      this._renderSwatchRow(products, '__fbt');
    } catch (e) { fallback(); }
  }

  _renderSwatchRow(products, medium) {
    const row = this.swatchRowEl;
    row.innerHTML = '';
    // In the current product's own medium tab, pin it first + highlighted so the
    // "selected" print is always visible (R1.7.2), even if not in the top results.
    let list = products.slice();
    const curId = String(this.dataset.productId);
    if (medium === this.dataset.productMedium && !list.some((p) => String(p.id) === curId)) {
      list.unshift({ id: this.product.id, handle: this.product.handle, title: this.product.title, image: this.product.image });
    }
    list.slice(0, SWATCH_MAX).forEach((p) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-builder__swatch';
      btn.title = p.title || '';
      if (String(p.id) === String(this.dataset.productId)) btn.setAttribute('aria-current', 'true');
      const img = document.createElement('img');
      img.className = 'bundle-builder__swatch-img';
      img.loading = 'lazy';
      if (p.image) img.src = cdnThumb(p.image, 120); // 64x80 box -> 120w covers 2x DPR
      img.alt = p.title || '';
      btn.appendChild(img);
      btn.addEventListener('click', () => { this._stampViewMode(); window.location.href = '/products/' + p.handle + '?medium=' + medium; });
      row.appendChild(btn);
    });
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'bundle-builder__swatch-more';
    more.setAttribute('aria-label', 'View more prints');
    more.innerHTML = '<span class="bundle-builder__swatch-more-plus" aria-hidden="true">+</span>'; // dashed + box (feedback 2)
    more.addEventListener('click', () => this._openModal());
    row.appendChild(more);
    this._updateSwatchArrows();
  }

  // ---- View More modal ----
  // Single-view "View more" hosts the SAME print-selector UI (facets, search, recently-viewed,
  // aligned cards) in a "browse" mode, so it's identical to the bundle mini-PLP (feedback 4).
  _openModal() {
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    // MOBILE: reuse the native full-screen selector DRAWER (the .bundle-modal shell doesn't hold
    // the fixed-position drawer reliably — feedback 3). Browse mode → "View print" cards.
    if (isMobile) {
      this._selector.mode = 'browse';
      this._openSelectorDrawer(); // builds the selector + slides the drawer up over everything
      this._setView('all');       // (re)render the cards in browse mode
      return;
    }
    // DESKTOP: the print-selector experience in a modal — "+ ADD" to the pack (feedback 4).
    // NOTE: do NOT switch to a bundle tier here — that triggered a full bundle re-render + a second
    // Boost fetch, which made the modal slow to open (feedback 5). The switch happens lazily on the
    // first "+ ADD" instead (see _addProduct).
    this._ensureModal();     // wires close/esc on the modal shell
    this._selector.mode = 'bundle';
    const alreadyBuilt = this._selector.built;
    this._ensureSelector();  // builds the selector panel (facets + cards) in the media column
    const panel = this._selEls && this._selEls.panel;
    if (!panel) { this.modalEl.hidden = false; document.body.style.overflow = 'hidden'; return; }
    if (!this._selectorHome) this._selectorHome = panel.parentElement; // remember its bundle-mode home
    this.modalEl.classList.add('bundle-modal--browse');
    this.modalEl.querySelector('.bundle-modal__panel').appendChild(panel);
    panel.classList.add('bundle-print-selector--browse');
    this.modalEl.hidden = false;
    document.body.style.overflow = 'hidden';
    // On reopen, reset to the 'all' view ONLY if the shopper had filtered to something else —
    // re-rendering ~50 cards when we're already on 'all' just made reopen sluggish (feedback: slow).
    if (alreadyBuilt && this._selector.view !== 'all') this._setView('all');
  }
  _closeModal() {
    if (this.modalEl) { this.modalEl.hidden = true; this.modalEl.classList.remove('bundle-modal--browse'); }
    document.body.style.overflow = '';
    // Return the selector panel to its bundle-mode home so pack mode still works.
    const panel = this._selEls && this._selEls.panel;
    if (panel && this._selectorHome) { panel.classList.remove('bundle-print-selector--browse'); this._selectorHome.appendChild(panel); }
    this._selector.mode = 'bundle';
  }
  _ensureModal() {
    if (this._modal.built) return;
    this._modal.built = true;
    const filtersEl = this.modalEl.querySelector('.bundle-modal__filters');
    const searchEl = this.modalEl.querySelector('.bundle-modal__search');
    this.modalEl.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this._closeModal()));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.modalEl && !this.modalEl.hidden) this._closeModal(); });
    FILTERS.forEach((f, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bundle-modal__filter';
      b.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      b.textContent = f.label;
      b.addEventListener('click', () => {
        this._modal.filter = f.handle;
        searchEl.value = '';
        [...filtersEl.children].forEach((c) => c.setAttribute('aria-pressed', c === b ? 'true' : 'false'));
        this._loadModalCollection(f.handle);
      });
      filtersEl.appendChild(b);
    });
    let t;
    searchEl.addEventListener('input', (e) => { clearTimeout(t); const q = e.target.value.trim(); t = setTimeout(() => this._modalSearch(q), 250); });
    this._loadModalCollection(this._modal.filter);
  }
  async _loadModalCollection(handle) {
    const grid = this.modalEl.querySelector('.bundle-modal__grid');
    grid.innerHTML = '<div class="bundle-modal__loading">Loading…</div>';
    try {
      const r = await fetch('/collections/' + handle + '/products.json?limit=40');
      const d = await r.json();
      const products = (d.products || []).map((p) => {
        const img = p.images && p.images[0];
        return { handle: p.handle, title: p.title, image: (img && img.src) || null, ratio: img && img.width && img.height ? img.width + ' / ' + img.height : null, medium: mediumFromTags(p.tags) };
      });
      this._renderModalCards(products);
    } catch (e) { grid.innerHTML = '<div class="bundle-modal__empty">Could not load prints.</div>'; }
  }
  async _modalSearch(q) {
    if (!q) { this._loadModalCollection(this._modal.filter); return; }
    const grid = this.modalEl.querySelector('.bundle-modal__grid');
    grid.innerHTML = '<div class="bundle-modal__loading">Searching…</div>';
    try {
      const r = await fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=20');
      const d = await r.json();
      const items = (d.resources && d.resources.results && d.resources.results.products) || [];
      const products = items.map((p) => {
        const fi = p.featured_image;
        let ratio = null;
        if (fi && fi.aspect_ratio) ratio = String(fi.aspect_ratio);
        return { handle: p.handle, title: p.title, image: (fi && fi.url) || p.image, ratio, medium: null };
      });
      this._renderModalCards(products);
    } catch (e) { grid.innerHTML = '<div class="bundle-modal__empty">Search failed.</div>'; }
  }
  _renderModalCards(products) {
    const grid = this.modalEl.querySelector('.bundle-modal__grid');
    grid.innerHTML = '';
    if (!products.length) { grid.innerHTML = '<div class="bundle-modal__empty">No prints found.</div>'; return; }
    products.forEach((p) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'bundle-modal__card';
      const img = document.createElement('img');
      img.className = 'bundle-modal__card-img';
      img.loading = 'lazy';
      if (p.image) img.src = cdnThumb(p.image, 440); // 4-col grid, ~200px card -> 440w covers 2x DPR
      if (p.ratio) img.style.aspectRatio = p.ratio;
      img.alt = p.title || '';
      const title = document.createElement('span');
      title.className = 'bundle-modal__card-title';
      title.textContent = p.title || '';
      card.appendChild(img);
      card.appendChild(title);
      card.addEventListener('click', () => {
        window.location.href = '/products/' + p.handle + (p.medium ? '?medium=' + p.medium : '');
      });
      grid.appendChild(card);
    });
    this._markCards(); // reflect Added / "ADD FREE PRINT" state on freshly-rendered cards
  }

  // ---- Material (Art Paper / Canvas, shared across slots) ----
  // Hide the native "Complete the look" upsell in pack mode. Walks up from each
  // `.mrk-bundle-header` to the LARGEST ancestor that still EXCLUDES this bundle block,
  // so it can never hide a container that also holds the bundle panel (that bug collapsed
  // the whole right column when done via a CSS :has() rule).
  _toggleNativeUpsell(hide) {
    document.querySelectorAll('.mrk-bundle-header').forEach((hdr) => {
      let target = hdr;
      while (target.parentElement
        && target.parentElement !== document.body
        && !target.parentElement.contains(this)) {
        target = target.parentElement;
      }
      target.style.display = hide ? 'none' : '';
    });
  }

  _toggleNativeMaterial(hide) {
    // The product form can be rendered multiple times (A/B app, mobile/desktop) — hide ALL.
    document.querySelectorAll('.mrk-custom-linked').forEach((native) => {
      const wrap = native.closest('.variant-option--buttons') || native.closest('.product-form__item') || native.parentElement;
      if (wrap) wrap.style.display = hide ? 'none' : '';
    });
  }

  _renderMaterials() {
    if (!this.materialOptionsEl) return;
    if (this.materials.length < 2) { if (this.materialEl) this.materialEl.hidden = true; return; }
    if (this.materialEl) this.materialEl.hidden = false;
    this.materialOptionsEl.innerHTML = '';
    this.materials.forEach((m) => {
      // Mirror the site's native material swatch UI (image tile + label below).
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-builder__material-option';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', m.title === this.state.material ? 'true' : 'false');
      const tile = document.createElement('span');
      tile.className = 'bundle-builder__material-swatch';
      if (m.image) tile.style.backgroundImage = 'url(' + m.image + ')';
      btn.appendChild(tile);
      if (m.badge) {
        const b = document.createElement('span');
        b.className = 'bundle-builder__material-badge';
        b.textContent = m.badge;
        btn.appendChild(b);
      }
      const label = document.createElement('span');
      label.className = 'bundle-builder__material-name';
      label.textContent = m.title;
      btn.appendChild(label);
      // Canvas unavailable (item 13): a chosen print has no canvas equivalent → disable the
      // Canvas tile + show a "?" tooltip next to the label (opens on hover / click).
      // NOTE: the free-pick window does NOT lock this tile — the material stays freely
      // switchable; only the GRID falls back to prints while a free print is next.
      const canvasBlocked = m.isCanvas && this._canvasUnavailable;
      if (m.isCanvas) {
        if (canvasBlocked) {
          btn.classList.add('bundle-builder__material-option--disabled');
          btn.setAttribute('aria-disabled', 'true');
          // Reuse the STANDARD tooltip (item G10) so the design matches every other tooltip.
          const info = document.createElement('span');
          info.className = 'bundle-builder__tip bundle-builder__material-tip-icon';
          info.setAttribute('tabindex', '0');
          info.setAttribute('role', 'button');
          info.setAttribute('aria-label', 'Why is canvas unavailable?');
          info.innerHTML = ICON_INFO + '<span class="bundle-builder__tip-bubble" role="tooltip">One or more of your prints cannot be purchased in canvas</span>';
          label.appendChild(document.createTextNode(' '));
          label.appendChild(info);
          info.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this._toggleTip(info); });
        } else {
          const price = document.createElement('span');
          price.className = 'bundle-builder__material-price';
          btn.appendChild(price);
          this._canvasPriceEl = price;
          this._refreshCanvasPrice();
        }
      }
      btn.addEventListener('click', () => {
        if (canvasBlocked) { const inf = btn.querySelector('.bundle-builder__tip'); if (inf) this._toggleTip(inf); return; }
        this._setMaterial(m);
      });
      this.materialOptionsEl.appendChild(btn);
    });
  }

  // Item 13: is the global Canvas option unavailable? True if any assigned paid print has no
  // canvas sibling (its `<handle>-canvas` product 404s). Fetches + caches siblings, then
  // re-renders the material tiles if the availability changed.
  async _updateCanvasAvailability() {
    if (!this._canvasMat) return;
    const slots = [];
    for (let i = 0; i < this._paidCount(); i++) { const s = this.state.paidSlots[i]; if (s && s.assigned && s.product) slots.push(s); }
    if (!slots.length) { if (this._canvasUnavailable) { this._canvasUnavailable = false; this._renderMaterials(); } return; }
    try { await Promise.all(slots.map((s) => this._ensureCanvasFor(s.product.handle))); } catch (e) {}
    const unavailable = slots.some((s) => {
      const h = s.product.handle || '';
      if (/-canvas$/i.test(h)) return false; // already a canvas product
      return this._canvasCache[h.replace(/-canvas$/i, '')] === null;
    });
    if (unavailable !== this._canvasUnavailable) {
      this._canvasUnavailable = unavailable;
      // If canvas just became unavailable while selected, fall back to paper.
      if (unavailable && this._canvasMat && this.state.material === this._canvasMat.title && this._paperMat) {
        this.state.material = this._paperMat.title;
        this._reconcileSizes(); this._renderSlots();
      }
      this._renderMaterials();
    }
  }

  // Under the Canvas tile: "+£{discounted} £{struck}" — the exact upgrade cost to switch
  // every APPLICABLE print to canvas (assigned paid prints not yet in the cart; free prints
  // and in-cart prints excluded, item 7). Async: fetches canvas siblings (cached) then fills.
  async _refreshCanvasPrice() {
    const el = this._canvasPriceEl;
    if (!el) return;
    const pct = Number(this._tier().discount) || 0;
    const slots = [];
    for (let i = 0; i < this._paidCount(); i++) {
      const s = this.state.paidSlots[i];
      if (s && s.assigned && s.product && !this._inCart(s)) slots.push(s);
    }
    if (!slots.length) { el.textContent = ''; return; }
    try {
      await Promise.all(slots.map((s) => this._ensureCanvasFor(s.product.handle)));
    } catch (e) { /* fall through — partial cache */ }
    let delta = 0;
    for (const s of slots) {
      const base = (s.product.handle || '').replace(/-canvas$/i, '');
      const canvas = this._canvasCache[base];
      const paperProd = /-canvas$/i.test(s.product.handle || '') ? (this._paperCache[base] || s.product) : s.product;
      const paperV = this.variantFor(paperProd, s.size, s.frame);
      const canvasV = canvas ? this.variantFor(canvas, s.size, s.frame) : null;
      if (paperV && canvasV) delta += Math.max(0, canvasV.price - paperV.price);
    }
    if (delta <= 0) { el.textContent = ''; return; }
    const disc = Math.round(delta * (1 - pct / 100));
    el.innerHTML = '+' + formatMoney(disc)
      + (disc < delta ? ' <span class="bundle-builder__material-price-was">' + formatMoney(delta) + '</span>' : '');
  }

  async _setMaterial(m) {
    if (!m || this.state.material === m.title) return;
    if (m.isCanvas && this._canvasUnavailable) return; // a chosen print has no canvas (item 13)
    // Pre-load the sibling product each assigned slot needs for the target material,
    // so pricing/sizes are ready before we re-render (both paper→canvas and canvas→paper).
    const tier = this._tier();
    const slots = [];
    for (let i = 0; i < this._paidCount(); i++) { const s = this.state.paidSlots[i]; if (s && s.assigned) slots.push(s); }
    for (let i = 0; i < tier.free; i++) { const s = this.state.freeSlots[i]; if (s.assigned) slots.push(s); }
    if (m.isCanvas) {
      await Promise.all(slots.filter((s) => !/-canvas$/i.test(s.product.handle || '')).map((s) => this._ensureCanvasFor(s.product.handle)));
    } else {
      await Promise.all(slots.filter((s) => /-canvas$/i.test(s.product.handle || '')).map((s) => this._ensurePaperFor(s.product.handle)));
    }
    this.state.material = m.title;
    this._reconcileSizes();
    this._renderMaterials();
    this._renderSlots();
    this.recalculate();
    this._persistSave();
    // The selector grid is material-scoped (prints vs canvas collection) — reload it, and rebuild
    // the filter row from the SAME collection. Without this the row keeps the previous material's
    // facet values (cached per scope, so flipping back and forth costs one request each way).
    if (this._selector.built) { this._buildFacetTree(); this._loadSelector(false); }
  }

  // Fetch the PAPER (base) sibling of a canvas product so its variants/prices are available.
  async _ensurePaperFor(handle) {
    const base = handle.replace(/-canvas$/i, '');
    if (base in this._paperCache) return this._paperCache[base];
    try {
      const r = await fetch('/products/' + base + '.js');
      if (!r.ok) { this._paperCache[base] = null; return null; }
      const p = await r.json();
      this._paperCache[base] = {
        id: p.id, handle: p.handle, title: p.title, vendor: p.vendor, type: p.type || '',
        image: (p.featured_image || (p.images && p.images[0])) || null,
        variants: (p.variants || []).map((v) => ({ id: v.id, option1: v.option1, option2: v.option2, price: v.price, compare_at_price: v.compare_at_price })),
      };
      return this._paperCache[base];
    } catch (e) { this._paperCache[base] = null; return null; }
  }

  // Ensure the effective sibling (canvas here) is cached for every assigned slot.
  // Returns true if anything was fetched (so the caller can re-render).
  async _preloadEffectiveForAssigned() {
    const tier = this._tier();
    const slots = [];
    for (let i = 0; i < this._paidCount(); i++) { const s = this.state.paidSlots[i]; if (s && s.assigned) slots.push(s); }
    for (let i = 0; i < tier.free; i++) { const s = this.state.freeSlots[i]; if (s.assigned) slots.push(s); }
    const pending = slots.filter((s) => !/-canvas$/i.test(s.product.handle || ''));
    if (!pending.length) return false;
    await Promise.all(pending.map((s) => this._ensureCanvasFor(s.product.handle)));
    return true;
  }

  async _ensureCanvasFor(handle) {
    const base = handle.replace(/-canvas$/, '');
    if (base in this._canvasCache) return this._canvasCache[base];
    try {
      const r = await fetch('/products/' + base + '-canvas.js');
      if (!r.ok) { this._canvasCache[base] = null; return null; }
      const p = await r.json();
      this._canvasCache[base] = {
        id: p.id, handle: p.handle, title: p.title, vendor: p.vendor, type: p.type || '',
        image: (p.featured_image || (p.images && p.images[0])) || null,
        variants: (p.variants || []).map((v) => ({ id: v.id, option1: v.option1, option2: v.option2, price: v.price, compare_at_price: v.compare_at_price })),
      };
      return this._canvasCache[base];
    } catch (e) { this._canvasCache[base] = null; return null; }
  }

  // After a material change, re-resolve each slot's size to one the effective product offers.
  _reconcileSizes() {
    this.state.paidSlots.forEach((slot) => {
      if (!slot.assigned) return;
      const eff = this._effective(slot);
      if (!eff) return;
      const sizes = [...new Set((eff.variants || []).map((v) => v.option1).filter(Boolean))];
      if (slot.size == null || !sizes.includes(slot.size)) {
        slot.size = this._smallestSizeFor(eff) || sizes[0] || slot.size;
      }
      this._dropFrameIfComboMissing(slot); // the sibling may not offer this frame at this size
    });
    this.state.freeSlots.forEach((slot) => {
      if (!slot.assigned) return;
      const eff = this._effective(slot);
      if (!eff) return;
      const sizes = [...new Set((eff.variants || []).map((v) => v.option1).filter(Boolean))];
      if (slot.size == null || !sizes.includes(slot.size)) {
        slot.size = this._freeSizeFor(eff) || sizes[0] || slot.size; // free size fixed by ratio
      }
      this._dropFrameIfComboMissing(slot); // the sibling may not offer this frame at this size
    });
  }

  // ---- Bundle persistence across PDPs ----
  // An in-progress bundle (added but not yet checked out) is saved so the print
  // slots stay filled as the shopper navigates between products, then cleared on
  // add-to-cart. Uses sessionStorage: this store's localStorage is routinely maxed
  // out by third-party apps (Boost search widgets etc.), so writes there fail;
  // sessionStorage has its own quota and survives same-tab PDP navigation.
  _persistLoad() {
    try {
      const raw = sessionStorage.getItem(this._persistKey);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.ts) return null;
      return data;
    } catch (e) { return null; }
  }

  _persistSave() {
    try {
      if (this.state.tier === 'single') return; // single mode isn't a bundle — leave any saved bundle intact
      const anyAssigned = this.state.paidSlots.some((s) => s.assigned && s.product) || this.state.freeSlots.some((s) => s.assigned && s.product);
      // If the bundle has been emptied, CLEAR the saved bundle — don't leave a stale one behind
      // that would re-appear on the next PDP (item 11, issue B).
      if (!anyAssigned) { this._persistClear(); return; }
      // Trim each stored product to only what restore needs (id/handle/title/vendor/
      // images/ratio + the variant fields used for pricing + cart) to keep it light.
      const packProduct = (p) => p ? {
        id: p.id, handle: p.handle, title: p.title, vendor: p.vendor,
        image: p.image, artImage: p.artImage, ratio: p.ratio,
        framingService: !!p.framingService,
        variants: (p.variants || []).map((v) => ({ id: v.id, option1: v.option1, option2: v.option2, price: v.price, compare_at_price: v.compare_at_price })),
      } : null;
      const data = {
        ts: Date.now(),
        tier: this.state.tier,
        material: this.state.material,
        removeFraming: !!this.state.removeFraming,
        paidSlots: this.state.paidSlots.map((s) => ({ assigned: !!s.assigned, size: s.size, frame: s.frame, product: s.assigned ? packProduct(s.product) : null })),
        freeSlots: this.state.freeSlots.map((s) => ({ assigned: !!s.assigned, size: s.size, frame: s.frame, product: s.assigned ? packProduct(s.product) : null })),
        overflow: this._overflow.map((o) => ({ size: o.size, frame: o.frame, product: packProduct(o.product) })),
      };
      sessionStorage.setItem(this._persistKey, JSON.stringify(data));
    } catch (e) { /* quota or serialisation issue — non-fatal */ }
  }

  _persistClear() { try { sessionStorage.removeItem(this._persistKey); } catch (e) {} }

  // Rehydrate a saved in-progress bundle over the freshly-initialised state.
  // Takes precedence over the landing print: the restored slots win.
  _restoreFromPersist() {
    const data = this._persistLoad();
    if (!data || !data.tier || data.tier === 'single' || !this.tiers[data.tier]) return false;
    const anyAssigned = (data.paidSlots || []).some((s) => s && s.assigned && s.product) || (data.freeSlots || []).some((s) => s && s.assigned && s.product);
    if (!anyAssigned) return false;
    // The shopper's carried view wins over the saved bundle's tier: if they switched back to
    // Single before navigating here, keep Single and only restore the CONTENTS. _trimToTier
    // stashes the restored prints in _overflow, and _expandFromOverflow refills the slots the
    // moment a pack tier is reopened — the in-progress bundle is preserved, not re-imposed.
    if (this.state.tier !== 'single') this.state.tier = data.tier;
    if (data.material) this.state.material = data.material;
    if (typeof data.removeFraming === 'boolean') this.state.removeFraming = data.removeFraming;
    this._overflow = (data.overflow || []).filter((o) => o && o.product).map((o) => ({ product: o.product, size: o.size, frame: o.frame }));
    // The bundle can hold extra paid slots beyond MAX_PAID (item 4), so restore the full length.
    const paidLen = Math.max(MAX_PAID, (data.paidSlots || []).length);
    this.state.paidSlots = [];
    for (let i = 0; i < paidLen; i++) {
      const src = (data.paidSlots || [])[i];
      if (src && src.assigned && src.product) {
        this.state.paidSlots[i] = { assigned: true, product: src.product, size: src.size, frame: src.frame || this._defaultFrame };
      } else {
        this.state.paidSlots[i] = { assigned: false, product: this._currentProductRef(), size: this._defaultSize, frame: this._defaultFrame };
      }
    }
    const freeLen = Math.max(this._maxFree, (data.freeSlots || []).length);
    for (let i = 0; i < freeLen; i++) {
      const src = (data.freeSlots || [])[i];
      if (src && src.assigned && src.product) {
        this.state.freeSlots[i] = { assigned: true, product: src.product, size: src.size, frame: src.frame || this._defaultFrame };
      } else {
        this.state.freeSlots[i] = { assigned: false, product: null, frame: this._defaultFrame };
      }
    }
    return true;
  }

  // ---- Pre-built bundle mode (URL-prefilled bundles) ----
  // A marketing link can land the shopper on a bundle PDP with the slots already filled:
  //   ?paid=<h1,h2>&free=<h3,h4>  — explicit paid/free split (primary form)
  //   ?prints=<h1,h2,h3>          — single list, auto-distributed paid-first-then-free
  //                                 via the normal _nextAddTarget routing (fallback form)
  //   &BBframe=<colour>           — frames the PAID prints (explicit form only). A plain
  //                                 value, not BBframe[colour] — brackets get mangled by
  //                                 email/ad platforms. Applies to paid= prints wherever
  //                                 the param sits: query params are semantically
  //                                 unordered, so position must never carry meaning.
  //                                 Matched case-insensitively (bbframe works), and named
  //                                 BBframe because plain `frame=` is ALREADY a PDP
  //                                 deep-link (mrk.js checks that frame swatch on the
  //                                 host buy-box), which on a bundle URL surfaced the
  //                                 single-print framing-service checkbox by mistake.
  // COMPLETELY ISOLATED: everything below is gated on the params being present — when
  // absent, _prefill is null and no existing behaviour changes anywhere.

  _parsePrefillParams() {
    // jQuery-based (theme ships jQuery 3.7.1 via scripts.liquid, deferred in <head> —
    // guaranteed to run before this module executes). If it's ever missing, bail to
    // exact no-param behaviour rather than take the builder down.
    const $ = window.jQuery;
    if (!$) { console.warn('[bundle-builder] prefill: jQuery unavailable — prefill disabled'); return null; }
    let qs;
    // URLSearchParams stays: jQuery has no query-string PARSER ($.param only serialises).
    try { qs = new URLSearchParams(window.location.search); } catch (e) { return null; }
    const handles = (v) => $.grep($.map(String(v || '').split(','), (s) => $.trim(s)), (s) => !!s);
    const paid = handles(qs.get('paid'));
    const free = handles(qs.get('free'));
    // &BBframe=<colour>: framing for the PAID prints. Carried only on the explicit
    // paid=/free= form — on ?prints= the paid/free split is decided inside _addProduct,
    // so a frame preference would distort the routing. Validated per PRODUCT at apply
    // time (the catalogue decides what "black" means for each print), not here.
    // Name matched case-insensitively so a lowercased campaign link still frames.
    let frame = null;
    qs.forEach((v, k) => { if (frame === null && k.toLowerCase() === 'bbframe') frame = $.trim(v) || null; });
    // Cap at the top tier's capacity (10+10 by default) — extras are dropped with a warning.
    const top = this.tiers[this.tierOrder[this.tierOrder.length - 1]] || {};
    const maxPaid = Number(top.paid) || MAX_PAID;
    const maxFree = Number(top.free) || this._maxFree;
    if (paid.length || free.length) {
      if (paid.length > maxPaid) console.warn('[bundle-builder] prefill: dropping paid handles beyond the top tier:', paid.slice(maxPaid).join(', '));
      if (free.length > maxFree) console.warn('[bundle-builder] prefill: dropping free handles beyond the top tier:', free.slice(maxFree).join(', '));
      if (frame && !paid.length) console.warn('[bundle-builder] prefill: BBframe= applies to paid prints only — no paid= handles given, ignored');
      return { paid: paid.slice(0, maxPaid), free: free.slice(0, maxFree), frame: paid.length ? frame : null };
    }
    const prints = handles(qs.get('prints'));
    if (prints.length) {
      const cap = maxPaid + maxFree;
      if (prints.length > cap) console.warn('[bundle-builder] prefill: dropping prints beyond the top tier:', prints.slice(cap).join(', '));
      if (frame) console.warn('[bundle-builder] prefill: BBframe= is only supported with the explicit paid=/free= form — ignored on prints=');
      return { prints: prints.slice(0, cap) };
    }
    return null;
  }

  // Smallest tier that fits the supplied prefill — config-driven (this.tiers), no
  // hardcoded tier shapes. Single is skipped (a prefilled link is a bundle, and single
  // mode has no slot UI to show the prints in). ?prints= fits by the tier's paid-first
  // capacity: its paid slots plus the free prints those paid prints can earn.
  _prefillFitTier() {
    const $ = window.jQuery;
    const p = this._prefill || {};
    const wantPaid = (p.paid || []).length;
    const wantFree = (p.free || []).length;
    const total = (p.prints || []).length;
    let fit = null;
    // $.each: returning false breaks, any other return continues (jQuery's continue/break idiom).
    $.each(this.tierOrder, (i, k) => {
      if (k === 'single') return;
      const t = this.tiers[k];
      if (!t) return;
      const tp = Number(t.paid) || 0;
      const tf = Number(t.free) || 0;
      const ok = p.prints ? total <= tp + Math.min(tp, tf) : (tp >= wantPaid && tf >= wantFree);
      if (ok) { fit = k; return false; }
    });
    return fit || this.tierOrder[this.tierOrder.length - 1];
  }

  // Resolve + slot every prefill handle via the normal _addProduct flow. Sequential
  // awaits keep the slot routing and free-earned invariant deterministic; a handle that
  // 404s is already caught inside _addProduct (console.error) and simply skipped. Counts
  // that violate the tier invariant clamp through the existing _enforceFreeCap logic.
  // Prefilled PAID prints are forced to the MEDIUM size free prints use (_freeSizeFor)
  // so paid and free match — on this prefill path ONLY, never for normal adds.
  async _prefillFromParams() {
    const $ = window.jQuery;
    const p = this._prefill;
    if (!p || !$ || this._prefillRan) return;
    this._prefillRan = true;
    this._setPrefillLoading(true);
    try {
      const forceMedium = (before) => {
        // Canvas landing (material = Canvas): SKIP the forcing and keep the normal
        // smallest-size default. Free prints are always paper (item 6), so "make paid
        // match free" is unachievable on a canvas pack — and on a keyword-less canvas
        // ladder _freeSizeFor falls through to mediumSizeFor's middle-of-ladder pick,
        // silently pricing the pack above the hand-built equivalent (QA round 6b).
        if (this._canvasMat && this.state.material === this._canvasMat.title) return;
        // The paid slot this add just filled = the newly-assigned one (diff vs snapshot).
        let idx = -1;
        $.each(this.state.paidSlots, (i, s) => { if (s && s.assigned && !before[i]) { idx = i; return false; } });
        if (idx < 0) return; // landed in a free slot (already medium) or the fetch failed
        const slot = this.state.paidSlots[idx];
        // Force only to a GENUINE free-eligible size (_freeSizes, keyword-matched) — never
        // to _freeSizeFor's mediumSizeFor/sizes[0] fallback. On a ladder matching no free
        // keyword the fallback is the middle entry — the LARGEST on a two-size ladder —
        // inflating the paid slot (QA 6b: +73% on a real print) without achieving the
        // paid-matches-free intent. No eligible size → keep the normal smallest default.
        const size = this._freeSizes(this._effective(slot) || slot.product)[0];
        if (size) slot.size = size;
      };
      // &BBframe=<colour>: frame the newly-added PAID print. Free prints stay unframed —
      // merchandising intent (the framing rules in _addProduct would even allow a framed
      // free print while the service is removed, so this must never "frame everything and
      // let the reroute sort it out"). Runs AFTER forceMedium so the frame is validated
      // against the slot's FINAL size. Resolution per product, strictest first, and every
      // rung is checked with _comboExists so the slot only ever receives a value its own
      // Framing dropdown could have selected — anything else would price as unframed via
      // variantFor's size-only fallback while displaying (and charging a service for) a
      // frame:
      //   1. the exact option2 value, case-insensitive ("Framed black");
      //   2. the colour word via _matchFrameByColour ("black" → "Black"/"Framed black",
      //      oak ↔ natural ↔ wood synonyms);
      //   3. BLACK — the merchant default for an unrecognised/unavailable colour (every
      //      framed print carries a black option, catalogue-checked 2026-08-25), warned;
      //   4. unframed, warned — a marketing link must never break on catalogue drift.
      const forceFrame = (before) => {
        const want = p.frame;
        if (!want || /unframed|no ?frame|^none$/i.test(want)) return; // absent or explicitly unframed = default
        let idx = -1;
        $.each(this.state.paidSlots, (i, s) => { if (s && s.assigned && !before[i]) { idx = i; return false; } });
        if (idx < 0) return; // the fetch failed — nothing was slotted
        const slot = this.state.paidSlots[idx];
        const product = this._effective(slot) || slot.product;
        const usable = (o) => (o && !/unframed/i.test(o) && this._comboExists(product, slot.size, o)) ? o : null;
        const opt2s = $.map((product && product.variants) || [], (v) => v.option2 || null);
        let hit = usable($.grep(opt2s, (o) => o.toLowerCase() === want.toLowerCase())[0]);
        if (!hit) hit = usable(this._matchFrameByColour(product, want));
        if (!hit) {
          hit = usable(this._matchFrameByColour(product, 'black'));
          if (hit) console.warn('[bundle-builder] prefill: frame "' + want + '" unavailable for ' + slot.product.handle + ' — defaulting to black');
        }
        if (hit) slot.frame = hit;
        else console.warn('[bundle-builder] prefill: no framed option for ' + slot.product.handle + ' at ' + slot.size + ' — staying unframed');
      };
      // NB $.map's callback is (value, index) — the reverse of $.each's (index, value).
      const list = p.prints
        ? $.map(p.prints, (h) => ({ handle: h, kind: null }))
        : $.merge($.map(p.paid, (h) => ({ handle: h, kind: 'paid' })), $.map(p.free, (h) => ({ handle: h, kind: 'free' })));
      for (const item of list) {
        // ($.map keeps false — it only drops null/undefined — so the snapshot is intact.)
        const before = $.map(this.state.paidSlots, (s) => !!(s && s.assigned));
        // wdPrefill marks this add as the prefill's own, so _addProduct's guard (which
        // rejects selector/quick-view adds while the lock is up) lets it through. The
        // ?prints= form carries the marker WITHOUT wdPrefillKind, keeping its normal
        // paid-first _nextAddTarget routing. Sequenced with await — _addProduct returns a
        // native Promise, which jQuery 3.x Deferreds interop with anyway.
        await this._addProduct({ handle: item.handle }, null, item.kind ? { wdPrefill: true, wdPrefillKind: item.kind } : { wdPrefill: true });
        forceMedium(before);
        if (item.kind === 'paid') forceFrame(before);
      }
      this._afterSlotChange(); // re-render with the medium-forced sizes + persist the built bundle
      // Consume the params once anything was actually slotted, so a reload / back-nav /
      // tab restore follows the normal _restoreFromPersist path and keeps the shopper's
      // EDITS instead of re-imposing the link's bundle (QA: refresh discarded edits).
      // On a totally failed prefill the params are kept, so a reload retries the link.
      // (native URL + history.replaceState — jQuery has no history API.)
      const anyFilled = $.grep(this.state.paidSlots, (s) => !!(s && s.assigned)).length > 0
        || $.grep(this.state.freeSlots, (s) => !!(s && s.assigned)).length > 0;
      if (anyFilled) {
        try {
          const url = new URL(window.location.href);
          $.each(['paid', 'free', 'prints'], (i, k) => { url.searchParams.delete(k); });
          // BBframe is matched case-insensitively at parse time — strip it the same way.
          $.each($.grep([...url.searchParams.keys()], (k) => k.toLowerCase() === 'bbframe'), (i, k) => { url.searchParams.delete(k); });
          history.replaceState(history.state, '', url.toString());
        } catch (e) { /* non-fatal — worst case the params linger as before */ }
      }
    } finally {
      this._setPrefillLoading(false);
    }
  }

  // Brief, lightweight loading state while the prefill fetches resolve: pulse-dim the
  // whole pack UI (slots + price + tiers + CTA — price/tiers sit OUTSIDE __bundle in the
  // block markup, so each is listed) and block interaction. pointer-events doesn't stop
  // keyboard/AT activation, so the actionable buttons are also hard-disabled; recalculate
  // keeps the ATC locked via _prefillLoading (QA: a partial pack could be ordered, or a
  // mid-flight tier click could scramble the link's bundle). Style injected here (no
  // Liquid change), wd- scoped.
  _setPrefillLoading(on) {
    const $ = window.jQuery;
    if (!$) return; // prefill never starts without jQuery (_parsePrefillParams bails)
    if (on && !$('#wd-bb-prefill-style').length) {
      $('<style>', { id: 'wd-bb-prefill-style' })
        .text('bundle-builder.wd-bb-prefilling .bundle-builder__bundle,bundle-builder.wd-bb-prefilling .bundle-builder__tiers,bundle-builder.wd-bb-prefilling .bundle-builder__price{opacity:.45;pointer-events:none;animation:wd-bb-pulse 1.2s ease-in-out infinite}@keyframes wd-bb-pulse{50%{opacity:.7}}')
        .appendTo(document.head);
    }
    this._prefillLoading = !!on;
    const $root = $(this);
    $root.toggleClass('wd-bb-prefilling', !!on);
    if (on) $root.attr('aria-busy', 'true');
    else $root.removeAttr('aria-busy');
    // `inert` takes the whole subtree out of the tab order and blocks activation —
    // crucially it also covers the slot controls (remove / size / material / browse)
    // that _renderSlots RE-CREATES on every add, which per-node disabling can't hold
    // onto (QA round 2: a mid-prefill keyboard Enter on a slot-remove lost a print).
    // .prop() writes the IDL property, same as el.inert = on. The pointer-events CSS
    // + explicit disabled below stay as the fallback for browsers without inert.
    $($.grep([this.bundleEl, this.priceEl], (el) => !!el))
      .add($root.find('.bundle-builder__tiers'))
      .prop('inert', !!on);
    $(this.tierButtons || []).prop('disabled', !!on);
    if (this.atcBtn) $(this.atcBtn).prop('disabled', !!on || this.atcBtn.disabled);
    if (on) { if (this.addSlotBtn) $(this.addSlotBtn).prop('disabled', true); }
    else {
      this._updateAddSlotBtn();  // restore the normal rule (disabled only on gallery)
      this.recalculate();        // restore the real ATC state now the lock is lifted
    }
  }

  get currentTier() { return this._tier(); }
}

if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);

export { BundleBuilder, TIER_ORDER };